import Icon from './Icon';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { sendMessage, getConversationHistory, createNote, defineTerm, getWalkthrough, getSessionRecap, listChats, newChat, deleteChat, getPaperInstructions, savePaperInstructions, summarizePaperInstructions, savePlan, setActivePlan, deactivatePlans, getModeSettings, saveModeSettings, getActiveTest, getActiveCycle, saveTestResults, saveCycleProgress, addGlossaryTerm, suggestSections, suggestSectionSettings, suggestPresets, suggestTopics, suggestTopicWeights, retakeTest, resolveHighlights, saveSessionProgress, modifySections, authorCycle, summarizeThread, getTestRunThreads, exitTest } from '../services/api';
import LessonPlanPreview, { CycleCard, exportPdf, exportWord, exportJson, fixPageRanges } from './LessonPlanPreview';
import InstructorAnalytics from './InstructorAnalytics';
import StudentOverview from './StudentOverview';
import AnalyticsAssistant from './AnalyticsAssistant';
import PlanHistoryPanel from './PlanHistoryPanel';
import ModeHomeScreen from './ModeHomeScreen';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from './MermaidDiagram';
import '../styles/ChatSection.css';
import TestCompleteModal from './TestCompleteModal';

function CopyableCodeBlock({ className, children }) {
  const [copied, setCopied] = useState(false);
  const code = String(children).trimEnd();

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="code-block-wrapper">
      <button
        type="button"
        className={`code-copy-btn${copied ? ' copied' : ''}`}
        onClick={handleCopy}
        aria-label="Copy code"
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <pre className={className}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

// react-markdown v9 removed the `inline` prop from the `code` component, so block
// code is told apart by its wrapping <pre> instead: only a fenced block gets one.
// Handling blocks here keeps `code` inline-only -- branching on `inline` made it
// undefined for every span, so each `backticked term` rendered as a full
// copy-button block and broke the sentence around it into pieces.
const markdownComponents = {
  pre({ children }) {
    const codeEl = React.Children.toArray(children).find(React.isValidElement);
    const className = codeEl?.props?.className || '';
    const code = codeEl ? codeEl.props.children : children;
    if (/language-mermaid/.test(className)) {
      return <MermaidDiagram code={String(code).trim()} />;
    }
    return <CopyableCodeBlock className={className}>{code}</CopyableCodeBlock>;
  },
  code({ node, className, children, ...props }) {
    return <code className={className} {...props}>{children}</code>;
  },
};


// How long the "Thinking…" bubble may sit with no reply before we tell the student
// something is wrong. Generation itself can legitimately take a while (Sonnet at
// 4096 max_tokens), so this is a WARNING, not a timeout: the request keeps running
// and a late reply still renders normally.
const STALL_WARNING_MS = 50000;

// Returns true if the test plan has the confidence meter enabled (defaults to true when absent).
const getShowConfidence = (testQuestionsJson) => {
  if (!testQuestionsJson) return true;
  try { return JSON.parse(testQuestionsJson)?.show_confidence !== false; } catch { return true; }
};

// Format a section's page span, guarding against an inverted range (start > end)
// so we never render "pp. 7–6". Returns '' when no pages are known.
const fmtPages = (start, end) => {
  if (start == null && end == null) return '';
  const a = start ?? end, b = end ?? start;
  const lo = Math.min(a, b), hi = Math.max(a, b);
  return lo === hi ? `p. ${lo}` : `pp. ${lo}–${hi}`;
};

const getHintStyle = (testQuestionsJson) => {
  if (!testQuestionsJson) return null;
  try { return JSON.parse(testQuestionsJson)?.hint_style ?? null; } catch { return null; }
};

// Cycle plans carry the instructor's hint preset under config.hints (the
// authored value, e.g. 'direct' | '3-hints' | 'infinite' | 'minimal' |
// 'moderate' | 'heavily-involved'). Used to decide whether to offer hints.
const getCycleHintStyle = (cycleQuestionsJson) => {
  if (!cycleQuestionsJson) return null;
  try {
    const plan = JSON.parse(cycleQuestionsJson);
    return plan?.config?.hints ?? plan?.hint_style ?? null;
  } catch { return null; }
};
const hintsEnabled = (style) => !!style && !['direct', 'none', 'off'].includes(style);

// Look up the question text directly from the plan JSON by index — more reliable than
// parsing it from an AI message which may contain feedback or other text.
const getQuestionFromPlan = (testQuestionsJson, questionIndex) => {
  if (!testQuestionsJson) return null;
  try {
    const plan = JSON.parse(testQuestionsJson);
    const questions = plan?.questions || plan?.testing_mode?.questions || [];
    return questions[questionIndex] || null;
  } catch { return null; }
};

// Total number of questions in a test plan, used to bound question prefetch so
// we never request a presentation past the last question.
const getTotalQuestions = (testQuestionsJson) => {
  if (!testQuestionsJson) return 0;
  try {
    const plan = JSON.parse(testQuestionsJson);
    const questions = plan?.questions || plan?.testing_mode?.questions || [];
    return Array.isArray(questions) ? questions.length : 0;
  } catch { return 0; }
};

// Instructor-pinned highlights for a plan item (accepts a legacy single `highlight`).
const getItemHighlights = (item) => {
  if (Array.isArray(item?.highlights)) return item.highlights.filter(h => h && h.text);
  if (item?.highlight?.text) return [item.highlight];
  return [];
};

// AI-suggested highlights for a plan item (emitted by the cycle author LLM).
const getItemAiHighlights = (item) =>
  Array.isArray(item?.ai_highlights) ? item.ai_highlights.filter(h => h && h.text) : [];

// Effective highlights — what the student's PDF actually shows for an item,
// each tagged with `kind` ('instructor' | 'ai') so the viewer can shade them
// differently. Rules:
//   - no instructor highlights        → show the AI's suggestions
//   - instructor highlights, no opt-in → show only the instructor's (override)
//   - instructor highlights + opt-in   → show BOTH (item.show_ai_highlights)
// AI quotes resolve with exact:false so a partial quote widens to sentence ends.
const getEffectiveItemHighlights = (item) => {
  const manual = getItemHighlights(item).map(h => ({ ...h, kind: 'instructor' }));
  const ai = getItemAiHighlights(item).map(h => ({ ...h, exact: false, kind: 'ai' }));
  if (!manual.length) return ai;
  if (item?.show_ai_highlights) return [...manual, ...ai];
  return manual;
};

// Resolve a kind-tagged highlight list to drawable, kind-tagged rect groups.
// Instructor and AI highlights are resolved in separate calls so a highlight
// that fails to locate can't scramble the kind→group mapping (the backend
// silently drops unresolvable highlights, shifting indices otherwise).
const resolveDrawGroups = async (paperId, tagged) => {
  const manual = tagged.filter(h => h.kind !== 'ai');
  const ai = tagged.filter(h => h.kind === 'ai');
  const [mRes, aRes] = await Promise.all([
    manual.length ? resolveHighlights(paperId, manual) : Promise.resolve({ groups: [] }),
    ai.length ? resolveHighlights(paperId, ai) : Promise.resolve({ groups: [] }),
  ]);
  return [
    ...((mRes?.groups) || []).filter(g => g?.rects?.length).map(g => ({ ...g, kind: 'instructor' })),
    ...((aRes?.groups) || []).filter(g => g?.rects?.length).map(g => ({ ...g, kind: 'ai' })),
  ];
};

// Merge a regenerated/refined cycle over the previous draft: the LLM never sees
// instructor-pinned highlights (added client-side), so carry them over; restore
// ai_highlights if the model dropped the field on a follow-up.
const mergeDraftCycle = (prev, next) => {
  if (!next) return prev;
  const merged = { ...next };
  const manual = getItemHighlights(prev);
  if (manual.length && !getItemHighlights(next).length) merged.highlights = manual;
  if (!getItemAiHighlights(next).length && getItemAiHighlights(prev).length) {
    merged.ai_highlights = getItemAiHighlights(prev);
  }
  // show_ai_highlights is a UI-only opt-in the LLM never emits — carry it over.
  if (next.show_ai_highlights === undefined && prev?.show_ai_highlights !== undefined) {
    merged.show_ai_highlights = prev.show_ai_highlights;
  }
  return merged;
};

// When the instructor regenerates or edits a plan, the instructor LLM returns the
// WHOLE plan but drops instructor-pinned `highlights` (never in its schema) and,
// for cycles, `ai_highlights` (the bulk instructor cycle schema has no such field —
// only the cycle-author pipeline emits them). Left alone, a single edit silently
// wipes every highlight the student would otherwise see. Carry them back over by
// index so highlights survive any edit. `regenIndex` is an item being fully
// replaced — its own highlights are NOT preserved (fresh content); pass -1 (the
// default) to preserve every item, e.g. for a targeted chat edit or a sub-question
// regen where the item's own highlights still apply.
const preserveHighlightsOnRegen = (prevPlan, nextPlan, regenIndex = -1) => {
  if (!prevPlan || !nextPlan) return nextPlan;
  const key = nextPlan.plan_type === 'testing' ? 'questions' : 'cycles';
  const prevItems = prevPlan[key];
  const nextItems = nextPlan[key];
  if (!Array.isArray(prevItems) || !Array.isArray(nextItems)) return nextPlan;
  const merged = nextItems.map((item, i) => {
    if (i === regenIndex) return item;
    const prev = prevItems[i];
    if (!prev) return item;
    const out = { ...item };
    const prevManual = getItemHighlights(prev);
    if (prevManual.length && !getItemHighlights(item).length) out.highlights = prevManual;
    const prevAi = getItemAiHighlights(prev);
    if (prevAi.length && !getItemAiHighlights(item).length) out.ai_highlights = prevAi;
    if (prev.show_ai_highlights !== undefined && item.show_ai_highlights === undefined) {
      out.show_ai_highlights = prev.show_ai_highlights;
    }
    return out;
  });
  return { ...nextPlan, [key]: merged };
};

// Extract the cycles/questions array from a (stringified) plan, tolerating nesting.
const getPlanItems = (planStr) => {
  if (!planStr) return [];
  let plan;
  try { plan = typeof planStr === 'string' ? JSON.parse(planStr) : planStr; } catch { return []; }
  return plan?.cycles || plan?.questions
    || plan?.plan?.cycles || plan?.plan?.questions
    || plan?.cycling_mode?.cycles || plan?.testing_mode?.questions
    || [];
};

// Per-mode last-active timestamps ({ qa, test, cycle }) used to resume the mode a
// student left off in, and to fall back to the next-most-recent when one is gone.
const readModeTimestamps = (userId, paperId, session) => {
  try { return JSON.parse(localStorage.getItem(`pb_mode_ts_${userId}_${paperId}_${session}`) || '{}'); }
  catch { return {}; }
};

// Modes ordered most-recent first. Modes with no timestamp keep the legacy
// test > cycle > qa order (stable sort), so old sessions behave as before.
const modesByRecency = (ts) => {
  return ['test', 'cycle', 'qa'].slice().sort((a, b) => (ts[b] || 0) - (ts[a] || 0));
};

// Parse MCQ options directly from the AI's last message text.
// Matches lines like "A. Text", "B) Text", "**C.** Text", "- D. Text" etc.
// Returns { options: [{letter, text}], isMultiAnswer: bool } or null.
const parseMcqOptionsFromMessage = (text) => {
  if (!text) return null;
  const optionLines = text.split('\n').filter(
    line => /^\s*[-*]?\s*\*{0,2}[A-E][.)]\*{0,2}\s+\S/.test(line.trimEnd())
  );
  if (optionLines.length < 2) return null;
  const options = optionLines.map(line => {
    const cleaned = line.replace(/\*{1,2}/g, '').replace(/^\s*[-*]?\s*/, '').trim();
    const m = cleaned.match(/^([A-E])[.)]\s*(.+)/);
    return m ? { letter: m[1], text: m[2].trim() } : null;
  }).filter(Boolean);
  if (options.length < 2) return null;
  const isMultiAnswer = /select all( that apply| correct| that are correct)/i.test(text);
  // Extract the question text: last non-empty paragraph that appears before the first option line.
  const lines = text.split('\n');
  const firstOptionIdx = lines.findIndex(
    line => /^\s*[-*]?\s*\*{0,2}[A-E][.)]\*{0,2}\s+\S/.test(line.trimEnd())
  );
  const beforeOptions = firstOptionIdx > 0 ? lines.slice(0, firstOptionIdx).join('\n') : '';
  const questionParagraphs = beforeOptions.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const questionText = questionParagraphs[questionParagraphs.length - 1] || '';
  return { options, isMultiAnswer, questionText };
};

// Strip A. / B. / C. / D. option lines from an AI message so they don't appear
// as plain text alongside the interactive MCQ buttons.
const stripMcqOptionLines = (text) => {
  return text
    .split('\n')
    .filter(line => !/^\s*[-*]?\s*\*{0,2}[A-E][.)]\*{0,2}\s+\S/.test(line.trimEnd()))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Non-linear slider values for the num_questions picker: 1-15 continuous, then jump steps
const NUM_Q_SLIDER_VALUES = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,20,25,30,50];
const numQValueToIndex = v => { const i = NUM_Q_SLIDER_VALUES.indexOf(Number(v)); return i === -1 ? 4 : i; };
const numQIndexToValue = i => NUM_Q_SLIDER_VALUES[i] ?? 5;

// Specific defaults for the instructor test-mode options panel
const INSTRUCTOR_OPTION_DEFAULTS = {
  format: 'All',
  difficulty: 'Medium',
  scope: 'Both',
  cf_focus: 'All',
  num_questions: '5',
};

function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="info-tooltip-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tooltip-btn"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        aria-label="More info"
      >i</button>
      {open && <div className="info-tooltip-popup" role="tooltip">{text}</div>}
    </span>
  );
}

function ResultsRow({ result }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr className="results-row">
        <td>{result.student_name || '—'}</td>
        <td>
          <span className={`results-grade-badge ${result.average_grade >= 80 ? 'grade-good' : result.average_grade >= 60 ? 'grade-mid' : 'grade-low'}`}>
            {result.average_grade != null ? `${Math.round(result.average_grade)}%` : '—'}
          </span>
        </td>
        <td>{result.ai_flags ?? 0}</td>
        <td>{result.total_hints_used ?? 0}</td>
        <td>{result.completed_at ? new Date(result.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}</td>
        <td>
          {result.question_results?.length > 0 && (
            <button type="button" className="results-expand-btn" onClick={() => setExpanded(v => !v)}>
              {expanded ? '▲' : '▼'}
            </button>
          )}
        </td>
      </tr>
      {expanded && result.question_results?.map(q => (
        <tr key={q.question_number} className="results-question-row">
          <td colSpan={2} className="results-q-label">Q{q.question_number}</td>
          <td colSpan={2}><span className={`results-grade-badge ${q.numerical_grade >= 80 ? 'grade-good' : q.numerical_grade >= 60 ? 'grade-mid' : 'grade-low'}`}>{q.categorical_grade || q.numerical_grade + '%'}</span></td>
          <td colSpan={2} />
        </tr>
      ))}
    </>
  );
}

const SUGGESTED_QUESTIONS = [
  "What's the main contribution of this paper?",
  "What problem is this paper trying to solve?",
  "What are the most important concepts to understand here?",
  "Can you summarize what I've read so far?",
];

// A 503 from /send-message/ with llm_unavailable means the backend reached the
// Anthropic call and it failed outright — a dead/expired ANTHROPIC_API_KEY, or an
// upstream outage. It comes back in about a second, so the stall watchdog never
// fires for it; it needs its own, persistent notice in the chat rather than a
// transient alert() the student can dismiss and forget.
function isLlmUnavailable(error) {
  return !!error?.response?.data?.llm_unavailable;
}

// Prefer the server's explanation (spend limit, test lock) over a generic failure.
function sendErrorMessage(error) {
  const data = error?.response?.data;
  if (data?.budget_exceeded) {
    return `${data.error}

Ask your instructor if you need more.`;
  }
  return data?.error || 'Failed to send message. Please try again.';
}

function ChatSection({ user, paper, currentSession, currentPage, openThreadRequest, onHighlightRequest, onHighlightResolved, onZoomRequest, selectedPdfText, selectedPdfPage, onClearSelection, onNoteCreated, mode: modeProp = 'student', proactiveSectionName, onProactiveDismiss, showAnalytics: showAnalyticsProp, setShowAnalytics: setShowAnalyticsProp, showStudents: showStudentsProp, setShowStudents: setShowStudentsProp, onRecapAvailableChange, onRecapInvalidate, onTestLockChange, chatFontSize = 14, tocSections = [] }) {
  // Allow toggling between student/test locally; instructor is always prop-driven
  const [localMode, setLocalMode] = useState(modeProp);
  const mode = modeProp === 'instructor' ? 'instructor' : localMode;

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // True once the "Thinking…" bubble has sat there for STALL_WARNING_MS with no
  // reply — see the effect below.
  const [aiStalled, setAiStalled] = useState(false);
  // Set to the server's explanation when the AI backend is confirmed unavailable
  // (fast 503). Cleared on the next send attempt.
  const [llmDown, setLlmDown] = useState(null);

  // Watchdog for a wedged AI call. If `loading` stays true for STALL_WARNING_MS the
  // reply is almost certainly never coming — a dead ANTHROPIC_API_KEY (401), the
  // backend being down, or an upstream outage all look identical from here: the
  // bubble just spins forever with no feedback. Flip a flag so the UI can say so.
  // Deliberately does NOT cancel the request; if a slow reply lands it renders and
  // this resets. Re-arms on every new send because `loading` goes false in between.
  useEffect(() => {
    if (!loading) {
      setAiStalled(false);
      return undefined;
    }
    setAiStalled(false);
    const timer = setTimeout(() => setAiStalled(true), STALL_WARNING_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // Sub-mode toggle: only applies when mode === 'student'
  const [chatMode, setChatMode] = useState('qa'); // 'qa' | 'test' | 'cycle' | 'annotation'
  // Live value for async work: loadHistory closes over chatMode, and a load that
  // resolves after the student switches mode must not restore the old one.
  const chatModeRef = useRef(chatMode);
  useEffect(() => { chatModeRef.current = chatMode; }, [chatMode]);
  const effectiveMode = mode === 'instructor' ? 'instructor' : (chatMode === 'test' ? 'test' : chatMode === 'cycle' ? 'cycle' : 'student');

  // Test mode state
  const [testQuestions, setTestQuestions] = useState(null);
  const [testComplete, setTestComplete] = useState({ open: false, score: null });
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [confidence, setConfidence] = useState(3);
  const [grades, setGrades] = useState([]);
  const [categoricalGrades, setCategoricalGrades] = useState([]);
  const [aiFlagCount, setAiFlagCount] = useState(0);
  const [hintsUsedCount, setHintsUsedCount] = useState(0);
  const [testCompleteActionReceived, setTestCompleteActionReceived] = useState(false);
  const [selectedMcqOption, setSelectedMcqOption] = useState(null);
  const [selectedMcqOptions, setSelectedMcqOptions] = useState(new Set());
  const [persistedMcqData, setPersistedMcqData] = useState(null);
  // Tracks how many questions have been completed (incremented unconditionally on question_complete,
  // regardless of whether the AI returned a valid numerical grade).
  const questionIndexRef = useRef(0);
  // State mirror of questionIndexRef — drives the instructor-highlight effect.
  // Refs don't trigger re-renders, and progress.current (the old trigger) is
  // polluted by the AI's progress_bar action, so highlights showed inconsistently.
  // testActiveQ is the canonical 0-based "which question are we on" for highlighting.
  const [testActiveQ, setTestActiveQ] = useState(0);
  // Question navigator: which past question the student is reviewing (null = live/current).
  const [viewingQuestion, setViewingQuestion] = useState(null);
  // Cycle navigator (mirrors test question navigator)
  const cycleIndexRef = useRef(0);        // 0-based current cycle index
  const [cycleActiveC, setCycleActiveC] = useState(0); // state mirror so renders update
  const [viewingCycle, setViewingCycle] = useState(null); // null = live, int = reviewing past
  // Stash of the live continuous-flow messages while reviewing a past question/cycle.
  const continuousMessagesRef = useRef(null);
  // Total comes from the plan (reliable), not the AI's progress_bar action.
  const planQuestionCount = getPlanItems(testQuestions).length;
  const testTotalQuestions = planQuestionCount || progress.total;
  // Questions the student has actually completed. grades.length is the primary
  // signal; testActiveQ advances on every question_complete even when a grade was
  // missing, so take the larger to avoid under-counting.
  const questionsCompleted = Math.max(grades.length, testActiveQ);
  const answeredAll = testTotalQuestions > 0 && questionsCompleted >= testTotalQuestions;
  // The test is finished once every question is answered. An explicit test_complete
  // from the AI is trusted to END the test ONLY when we have no reliable plan count
  // (custom/legacy tests) — otherwise a premature test_complete (the AI miscounting
  // on an isolated per-question thread) would skip the remaining questions and fire
  // the completion confetti early.
  const testFinished = answeredAll || (planQuestionCount === 0 && testCompleteActionReceived);
  // Test integrity lock: while actively taking a test (questions loaded, not finished),
  // the student is hard-locked into test mode — can't switch to Q&A/cycle to fish for
  // answers. The only way out is "Exit test", which ends the attempt.
  const activeTestLock = effectiveMode === 'test' && !!testQuestions && !testFinished;
  // An old transcript opened from the sidebar: nothing here is answerable.
  const viewingPastAttempt = effectiveMode === 'test' && !testQuestions && messages.length > 0;

  // Marker chats are another route to the AI, so they close during a test.
  useEffect(() => {
    onTestLockChange?.(activeTestLock);
  }, [activeTestLock, onTestLockChange]);
  const skipNextHistoryRef = useRef(false);
  // When a page-refresh restore sets this, the next loadHistory call uses it instead of null.
  const restoreChatIndexRef = useRef(null);
  // One-shot flags: on a refresh-restore, load the conversation messages for the
  // restored mode (the generic history effect is suppressed via skipNextHistoryRef).
  const restoreCycleLoadRef = useRef(false);
  const restoreTestLoadRef = useRef(false);
  // Set when an instructor authoring snapshot is restored on refresh, so the
  // "show home screen on entry" init effect doesn't override the restored screen.
  const instructorRestoredRef = useRef(false);
  // Per-mode state snapshots — preserved when switching tabs
  const modeStateRef = useRef({ qa: null, test: null, cycle: null, annotation: null });
  // Student mode availability (pre-fetched to conditionally show mode buttons)
  const [hasActiveTest, setHasActiveTest] = useState(null);
  const [hasActiveCycle, setHasActiveCycle] = useState(null);
  // Sidebar metadata for instructor-assigned items
  const [instructorTestMeta, setInstructorTestMeta] = useState(null); // { due_date, num_questions }
  const [instructorCycleMeta, setInstructorCycleMeta] = useState(null); // { due_date }
  const qaHasMessagesRef = useRef(false);
  const qaChatIndexRef = useRef(null);
  // Q&A context-health nudge: backend flags when this thread is long enough that
  // compression is degrading answers → suggest starting a fresh conversation.
  const [contextDegrading, setContextDegrading] = useState(false);
  // Threads already asked for a title, so a second message does not ask again.
  // Once the student dismisses the nudge, don't re-show it for this thread.
  const contextNudgeDismissedRef = useRef(false);
  // Instructor-assigned test
  const [activeInstructorTest, setActiveInstructorTest] = useState(null); // {plan_id, plan, due_date, name}
  const [activeTests, setActiveTests] = useState([]); // all active instructor tests (may be >1)
  const [loadingActiveTest, setLoadingActiveTest] = useState(false);
  const resultsSavedRef = useRef(false);
  const activePlanIdRef = useRef(null); // plan_id captured at test start; stays set even if activeInstructorTest clears
  const savedTestStateRef = useRef(null); // preserves in-progress test when user switches to Q&A
  const retakePlanRef = useRef(null); // {plan_id, plan} stored at test start for retake flow
  const pendingStudentResponseRef = useRef(''); // stores raw student text for the next question_complete action
  const pendingConfidenceRef = useRef(3); // confidence at the moment student submits each test answer
  const pendingFeedbackRef = useRef(null); // last feedback action ({strengths, weaknesses, improvements}) before question_complete
  // Test-mode question prefetch. Each test question lives in its own isolated
  // thread (chatIndex+1+index) and the questions are predefined in the plan, so
  // PaperBuddy can present upcoming questions in the background while the student
  // works on the current one — no per-advance round-trip wait. Maps qIndex →
  // Promise of the presentation response. Mirrors the cycle-author pregen pattern.
  const testQPrefetchRef = useRef(new Map());
  const TEST_Q_PREFETCH_LOOKAHEAD = 2; // how many upcoming questions to keep warming
  // Cycle mode state
  const [cycleQuestions, setCycleQuestions] = useState(null); // the cycle plan JSON string
  const [activeCyclePlan, setActiveCyclePlan] = useState(null); // {plan_id, plan, due_date, name} — selected cycle
  const [activeCycles, setActiveCycles] = useState([]); // all active instructor cycle plans
  const [loadingActiveCycle, setLoadingActiveCycle] = useState(false);
  // Cycle progress: derived from cycle_complete actions; total parsed from plan JSON
  const [cycleProgress, setCycleProgress] = useState({ current: 0, total: 0 });
  const cycleConfettiKey = user && paper ? `cycleConfettiSeen_${user.id}_${paper.id}` : null;
  const [cycleComplete, setCycleComplete] = useState({ open: false });
  // Kept so the thread can show the passage and question it came from.
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  // 'reading' → intro presented, waiting for student; 'questioning' → answering; 'done' → cycle_complete received
  const [cyclePhase, setCyclePhase] = useState(null); // null | 'reading' | 'questioning' | 'done'
  // Tracks messages exchanged within the current cycle (resets on cycle_complete)
  // so per-cycle engagement can be reported to the analytics endpoint.
  const cycleMessageCountRef = useRef(0);
  // The plan_id used to start the current cycle run (null for custom-pasted plans).
  const cyclePlanIdRef = useRef(null);
  // Instructor analytics panel — lifted to ReadingPage when in instructor mode
  const [showAnalyticsInternal, setShowAnalyticsInternal] = useState(false);
  const showAnalytics = showAnalyticsProp !== undefined ? showAnalyticsProp : showAnalyticsInternal;
  const setShowAnalytics = setShowAnalyticsProp !== undefined ? setShowAnalyticsProp : setShowAnalyticsInternal;
  // Consolidated per-student overview panel — also lifted to ReadingPage.
  const [showStudentsInternal, setShowStudentsInternal] = useState(false);
  const showStudents = showStudentsProp !== undefined ? showStudentsProp : showStudentsInternal;
  const setShowStudents = setShowStudentsProp !== undefined ? setShowStudentsProp : setShowStudentsInternal;
  // Analytics Assistant — standalone AI chat page (instructor only)
  const [showAnalyticsAssistant, setShowAnalyticsAssistant] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);

  // Instructor options panel (clickable multiple-choice for Steps 1 & 2)
  const [optionsPanel, setOptionsPanel] = useState(null); // null | { questions, selections }
  // Instructor awaiting-review state (one question at a time in test generation)
  const [awaitingReview, setAwaitingReview] = useState(false);
  // Instructor sub-mode: which type of plan the instructor is building
  const [instructorSubMode, setInstructorSubMode] = useState(null); // null | 'cycle' | 'test' | 'guided'
  // For guided sub-mode the API needs 'guided'; everything else uses effectiveMode
  const apiMode = (mode === 'instructor' && instructorSubMode === 'guided') ? 'guided' : effectiveMode;
  const [cycleEnabled, setCycleEnabled] = useState(true);
  const [testEnabled, setTestEnabled] = useState(true);
  // Instructor home screen
  const [showInstructorHome, setShowInstructorHome] = useState(false);
  const [showModeHome, setShowModeHome] = useState(null); // null | 'cycle' | 'test'
  // Student home screen — a "how do you want to study?" picker shown on entry.
  // Dismissed (per paper) once the student picks a mode, so returning students
  // land straight in their chosen view.
  const [showStudentHome, setShowStudentHome] = useState(() => {
    try { return localStorage.getItem(`pb_student_home_dismissed_${paper?.id}`) !== '1'; }
    catch { return true; }
  });
  const dismissStudentHome = () => {
    setShowStudentHome(false);
    try { localStorage.setItem(`pb_student_home_dismissed_${paper?.id}`, '1'); } catch { /* ignore */ }
  };
  const cycleAutoRestoredRef = useRef(false);
  const modeSettingsLoadedRef = useRef(false);
  const [triggerAutoGenerate, setTriggerAutoGenerate] = useState(0);
  const handleInstructorSetupSubmitRef = useRef(null);
  // Instructor custom instructions tab
  const [instructorTab, setInstructorTab] = useState('chat'); // 'chat' | 'prompt'
  const [customInstructions, setCustomInstructions] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [instructorPromptSaved, setInstructorPromptSaved] = useState(false);
  const [promptSummary, setPromptSummary] = useState('');
  const [summarizingPrompt, setSummarizingPrompt] = useState(false);

  // AI-suggested paper sections (shown in test & cycle setup forms)
  const [suggestedPaperSections, setSuggestedPaperSections] = useState(null); // null = not yet fetched
  const [aiGeneratedSections, setAiGeneratedSections] = useState([]); // clean AI output, never overwritten by edits
  const [loadingPaperSections, setLoadingPaperSections] = useState(false);
  const [showSectionsPopup, setShowSectionsPopup] = useState(false);
  const [showAdvancedSetup, setShowAdvancedSetup] = useState(false);
  // Per-section customization: { [sectionNumber]: { difficulty: 0-10, importance: 0-10 } }
  const [sectionSettings, setSectionSettings] = useState({});
  // Pending recommendation from guided setup — applied once instructor switches to the recommended tab
  const [guidedRecommendation, setGuidedRecommendation] = useState(null);
  // Which mode the current guided session is targeting ('cycle' | 'test')
  const [guidedTargetMode, setGuidedTargetMode] = useState(null);
  // Guided setup multi-step flow: null | 'structure' (paper structure review, cycle-only)
  const [guidedStep, setGuidedStep] = useState(null);
  const [sectionDropdownOpen, setSectionDropdownOpen] = useState({});
  // Local editable copy of sections shown on the structure review page
  const [guidedStructureSections, setGuidedStructureSections] = useState([]);
  // AI chat within the structure review page
  const [structureAiInput, setStructureAiInput] = useState('');
  const [structureAiLoading, setStructureAiLoading] = useState(false);
  const [structureAiMessage, setStructureAiMessage] = useState('');
  // Input value for the guided setup large initial textarea
  const [guidedInput, setGuidedInput] = useState('');
  // Goals text saved when the instructor clicks "Paper Structure Setup →"
  const [guidedGoals, setGuidedGoals] = useState('');
  const [loadingAiSectionSettings, setLoadingAiSectionSettings] = useState(false);
  const [regeneratingSections, setRegeneratingSections] = useState(false);
  const [guidedPresetsLoading, setGuidedPresetsLoading] = useState(false);
  const suggestedSectionsPaperIdRef = useRef(null); // tracks which paper was last fetched

  // Notes / definition / walkthrough state
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [walkthrough, setWalkthrough] = useState(null);
  const [loadingWalkthrough, setLoadingWalkthrough] = useState(false);
  // Inject dynamic font size into chat section
  useEffect(() => {
    const styleId = 'chat-font-size-override';
    let el = document.getElementById(styleId);
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = `.chat-section, .chat-section * { font-size: ${chatFontSize}px !important; }`;
    return () => {}; // keep style alive across re-renders
  }, [chatFontSize]);

  // Chat history state
  const [chatIndex, setChatIndex] = useState(null);
  const [chats, setChats] = useState([]);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [chatCtxMenu, setChatCtxMenu] = useState(null); // {x, y, chat_index}

  // Thread sidebar state
  const [showThreadSidebar, setShowThreadSidebar] = useState(false);
  const [allModeChats, setAllModeChats] = useState({ qa: [], test: [], cycle: [], annotation: [] });
  const [threadTitles, setThreadTitles] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pb_thread_titles') || '{}'); } catch { return {}; }
  });
  const [editingThread, setEditingThread] = useState(null); // { key: 'qa_3', value: 'title' }
  const [sidebarSectionOpen, setSidebarSectionOpen] = useState({ qa: true, test: true, cycle: true, annotation: true });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Instructor plan state
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planApprovalNotes, setPlanApprovalNotes] = useState('');
  const [planDueDate, setPlanDueDate] = useState('');
  const [planName, setPlanName] = useState('');
  const [planApproved, setPlanApproved] = useState(false);
  const [planSaved, setPlanSaved] = useState(false);
  const [regenIndex, setRegenIndex] = useState(null);
  const [regenQuestionKey, setRegenQuestionKey] = useState(null); // {cycleIndex, qIndex} of the review-plan question regenerating
  const [showPlanHistory, setShowPlanHistory] = useState(false);
  const [planAiReply, setPlanAiReply] = useState(null); // AI response to change requests
  const [planPreviewScreen, setPlanPreviewScreen] = useState('edit'); // 'edit' | 'final'

  // Incremental cycle authoring (instructor cycle mode): generate a lightweight
  // overview first, then detail + finalize one cycle at a time in its own thread.
  const [authorOverview, setAuthorOverview] = useState(null);      // {config, cycles:[{number,title,read_sections,focus}]}
  const [authorCycleIndex, setAuthorCycleIndex] = useState(0);     // 0-based index of the cycle being detailed
  const [authorFinalized, setAuthorFinalized] = useState([]);      // finalized detailed cycle objects
  const [authorDraftCycle, setAuthorDraftCycle] = useState(null);  // current cycle's detailed JSON (shown/edited)
  const [authorPhase, setAuthorPhase] = useState(null);            // null | 'detailing' | 'review'
  const [railOpen, setRailOpen] = useState(true);
  const [authorRegenLoading, setAuthorRegenLoading] = useState(false);
  const [authorRegenQuestionIndex, setAuthorRegenQuestionIndex] = useState(null); // index of the cycle question currently regenerating
  const [authorReviewingIndex, setAuthorReviewingIndex] = useState(null); // null = current cycle; number = past cycle being revisited

  // Background pre-generation: while the instructor reviews one cycle, we build
  // the NEXT FEW cycles in the background so Finalize is instant. We keep a bounded
  // look-ahead window (PREGEN_LOOKAHEAD) rather than rendering the whole plan at
  // once — the backend only details one cycle per request, and pre-rendering every
  // remaining cycle would fire N requests at the LLM API (rate limits / load). The
  // window slides forward each time the instructor finalizes a cycle.
  const authorPregeneratedRef = useRef({});         // {[0-based index]: {cycle, chatIndex}}
  // Bumped whenever a background cycle finishes, so the "upcoming cycles" preview
  // (which reads the ref) re-renders as each pre-generated cycle becomes ready.
  const [pregenVersion, setPregenVersion] = useState(0);
  const authorPregeneratingRef = useRef(new Set()); // indices currently in flight
  const authorPregenQueueRef = useRef([]);          // indices waiting for a free slot
  const authorPregenActiveRef = useRef(0);          // number of in-flight generations
  const MAX_PREGEN_CONCURRENCY = 3;
  const PREGEN_LOOKAHEAD = 3;                        // how many upcoming cycles to render ahead

  // Generate a single cycle in its own thread. Idempotent: a no-op if the cycle
  // is already cached or in flight. Always resolves (errors are swallowed) so the
  // queue pump can reliably chain off it.
  const pregenerateCycle = (cycleIndex, overview) => {
    if (!user || !paper) return Promise.resolve();
    const totalCycles = overview?.cycles?.length || 0;
    if (cycleIndex >= totalCycles) return Promise.resolve();
    if (cycleIndex in authorPregeneratedRef.current) return Promise.resolve();
    if (authorPregeneratingRef.current.has(cycleIndex)) return Promise.resolve();
    authorPregeneratingRef.current.add(cycleIndex);
    return (async () => {
      try {
        const fresh = await newChat(user.id, paper.id, 0);
        const newIdx = fresh.chat_index;
        const det = await authorCycle({
          userId: user.id, paperId: paper.id, step: 'detail',
          overview, cycleNumber: cycleIndex + 1, chatIndex: newIdx, silent: true,
          message: `Generate the full detailed content for Cycle ${cycleIndex + 1}.`,
        });
        // No cycle means the model's JSON failed to parse even after repair —
        // don't cache an empty result (Finalize would then show a blank card).
        if (!det.cycle) throw new Error('No cycle returned');
        authorPregeneratedRef.current[cycleIndex] = { cycle: det.cycle, chatIndex: newIdx };
        setPregenVersion(v => v + 1);   // reveal it in the upcoming-cycles preview
      } catch (e) {
        // Leave it out of the cache + in-flight set so the next pregenerateAhead
        // call (e.g. at the next Finalize) re-queues and retries it.
        authorPregeneratingRef.current.delete(cycleIndex);
        console.warn(`Background pre-generation for cycle ${cycleIndex + 1} failed:`, e);
      }
    })();
  };

  // Drain the pre-generation queue, keeping at most MAX_PREGEN_CONCURRENCY jobs
  // in flight at once. Re-pumps as each job finishes.
  const pumpPregenQueue = (overview) => {
    while (
      authorPregenActiveRef.current < MAX_PREGEN_CONCURRENCY &&
      authorPregenQueueRef.current.length > 0
    ) {
      const idx = authorPregenQueueRef.current.shift();
      // Skip anything that got cached/started between enqueue and now.
      if (idx in authorPregeneratedRef.current || authorPregeneratingRef.current.has(idx)) continue;
      authorPregenActiveRef.current += 1;
      pregenerateCycle(idx, overview).finally(() => {
        authorPregenActiveRef.current -= 1;
        pumpPregenQueue(overview);
      });
    }
  };

  // Queue the next PREGEN_LOOKAHEAD not-yet-generated cycles from `fromIndex`, then
  // start draining. This keeps a bounded look-ahead buffer (the next few cycles)
  // ready in the background instead of rendering the entire plan at once. Called
  // again with an advanced `fromIndex` on each finalize, so the window slides forward.
  const pregenerateAhead = (fromIndex, overview) => {
    const total = overview?.cycles?.length || 0;
    const end = Math.min(total, fromIndex + PREGEN_LOOKAHEAD);
    for (let i = fromIndex; i < end; i++) {
      if (i in authorPregeneratedRef.current) continue;
      if (authorPregeneratingRef.current.has(i)) continue;
      if (!authorPregenQueueRef.current.includes(i)) authorPregenQueueRef.current.push(i);
    }
    pumpPregenQueue(overview);
  };

  const clearAuthoring = () => {
    setAuthorOverview(null);
    setAuthorCycleIndex(0);
    setAuthorFinalized([]);
    setAuthorDraftCycle(null);
    setAuthorPhase(null);
    setAuthorRegenLoading(false);
    setAuthorReviewingIndex(null);
    authorPregeneratedRef.current = {};
    authorPregeneratingRef.current = new Set();
    authorPregenQueueRef.current = [];
    authorPregenActiveRef.current = 0;
  };

  // Whenever we enter (or restore into) the cycle-detailing phase, make sure the
  // next few cycles are generating in the background. The pregen cache is
  // in-memory only, so this is what resumes background work after a page refresh.
  // Idempotent — harmless even when the live flow already kicked it off.
  useEffect(() => {
    if (authorPhase === 'detailing' && (authorOverview?.cycles?.length || 0) > 1) {
      pregenerateAhead(authorCycleIndex + 1, authorOverview);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorPhase, authorOverview]);

  // Instructor setup form state
const [instructorSetupDepth, setInstructorSetupDepth] = useState(null); // 'surface' | 'intermediate' | 'deep'
  const [instructorSetupCycles, setInstructorSetupCycles] = useState(7); // cycle mode: number of cycles
  const [instructorSetupNumQuestions, setInstructorSetupNumQuestions] = useState(5); // test mode: number of questions (min 3); when a bank is enabled, this is how many each student gets
  const [instructorSetupBankEnabled, setInstructorSetupBankEnabled] = useState(false); // test mode: generate a large pool and randomly sample per student
  const [instructorSetupBankSize, setInstructorSetupBankSize] = useState(30);          // test mode: total questions in the bank
  const [instructorSetupQPerCycle, setInstructorSetupQPerCycle] = useState(1); // 0-5
  const [instructorSetupDifficulty, setInstructorSetupDifficulty] = useState(null); // 'easy' | 'medium' | 'hard'
  const [instructorSetupAccuracy, setInstructorSetupAccuracy] = useState(null); // 'minimal' | 'in-depth' — start unset so the instructor must choose
  const [instructorSetupHints, setInstructorSetupHints] = useState(null); // cycle: 'minimal'|'moderate'|'heavily-involved'; test: 'exam'|'helpful'|'guided'|'relaxed' — start unset
  const [instructorSetupQTypes, setInstructorSetupQTypes] = useState(['mcq', 'short-answer', 'counterfactual']); // multi-select
  const [instructorSetupConfidence, setInstructorSetupConfidence] = useState(false);
  const [instructorSetupInstructions, setInstructorSetupInstructions] = useState('');
  const [instructorSetupSubmitted, setInstructorSetupSubmitted] = useState(false);
  // Topic distribution (test mode only): [{title, weight}] weights sum to 100
  const [testTopics, setTestTopics] = useState([]);
  const [testTopicsLoading, setTestTopicsLoading] = useState(false);
  const [newTopicInput, setNewTopicInput] = useState('');
  const [topicWeightSuggestLoading, setTopicWeightSuggestLoading] = useState(false);
  // Question balance: how questions are split across types; keys match instructorSetupQTypes values
  const [qTypeWeights, setQTypeWeights] = useState({ mcq: 34, 'short-answer': 33, counterfactual: 33 });
  const [qBalanceOpen, setQBalanceOpen] = useState(false);

  const messagesEndRef = useRef(null);

  // Load all-mode chats on mount and whenever the session/paper changes (student mode only)
  useEffect(() => {
    if (mode === 'student' && user && paper && currentSession) {
      loadAllModeChats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, user?.id, paper?.id, currentSession]);

  // ── Instructor custom instructions ────────────────────────────────────────

  useEffect(() => {
    if (mode === 'instructor' && paper?.id) {
      getPaperInstructions(paper.id)
        .then(d => setCustomInstructions(d.custom_instructions || ''))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, paper?.id]);

  // Fetch AI-suggested sections once per paper when in instructor mode
  useEffect(() => {
    if (mode !== 'instructor' || !paper?.id) return;
    if (suggestedSectionsPaperIdRef.current === paper.id) return;
    suggestedSectionsPaperIdRef.current = paper.id;
    setLoadingPaperSections(true);
    setSuggestedPaperSections(null);
    suggestSections(paper.id)
      .then(data => {
        const sects = data.sections || [];
        setSuggestedPaperSections(sects);
        setAiGeneratedSections(sects);
        setSectionSettings(
          Object.fromEntries(sects.map(s => [s.number, { difficulty: 5, importance: 5, notes: '', skipped: false }]))
        );
        if (sects.length > 0) setInstructorSetupCycles(Math.min(sects.length, 20));
      })
      .catch(() => { setSuggestedPaperSections([]); setAiGeneratedSections([]); })
      .finally(() => setLoadingPaperSections(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, paper?.id]);

  // Fetch topic distribution whenever the instructor switches to test-presets.
  // Skip if topics were already set by a goal submission so we don't overwrite them.
  useEffect(() => {
    if (mode !== 'instructor' || instructorSubMode !== 'test' || instructorSetupSubmitted) return;
    if (!paper?.id || testTopics.length > 0) return;
    setTestTopicsLoading(true);
    suggestTopics(paper.id, aiGeneratedSections || [])
      .then(data => setTestTopics(data.topics || []))
      .catch(() => setTestTopics([]))
      .finally(() => setTestTopicsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, instructorSubMode, paper?.id, testTopics.length]);

  // Apply pending guided recommendation once the instructor switches to the recommended tab
  useEffect(() => {
    if (!guidedRecommendation || guidedRecommendation.mode !== instructorSubMode) return;
    const rec = guidedRecommendation;
    setGuidedRecommendation(null);
    const p = rec.presets || {};
    if (rec.mode === 'cycle') {
      if (p.depth) setInstructorSetupDepth(p.depth);
      if (p.num_cycles) setInstructorSetupCycles(Math.min(p.num_cycles, 20));
      if (p.q_per_cycle != null) setInstructorSetupQPerCycle(p.q_per_cycle);
    } else {
      if (p.num_questions) setInstructorSetupNumQuestions(Math.min(Math.max(p.num_questions, 3), 25));
      if (Array.isArray(p.q_types) && p.q_types.length) setInstructorSetupQTypes(p.q_types);
      if (p.confidence != null) setInstructorSetupConfidence(p.confidence);
    }
    if (p.difficulty) setInstructorSetupDifficulty(p.difficulty);
    if (p.accuracy) setInstructorSetupAccuracy(p.accuracy);
    if (p.hints) setInstructorSetupHints(p.hints);
    if (Array.isArray(rec.section_adjustments) && rec.section_adjustments.length) {
      setSectionSettings(
        Object.fromEntries(rec.section_adjustments.map(s => [
          s.number,
          {
            difficulty: Math.min(10, Math.max(0, Math.round(s.difficulty ?? 5))),
            importance: Math.min(10, Math.max(0, Math.round(s.importance ?? 5))),
          },
        ]))
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorSubMode, guidedRecommendation]);

  const handleSaveInstructions = async () => {
    setSavingInstructions(true);
    setInstructorPromptSaved(false);
    try {
      await savePaperInstructions(paper.id, customInstructions);
      setInstructorPromptSaved(true);
      setTimeout(() => setInstructorPromptSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save instructions:', e);
      alert('Failed to save instructions.');
    } finally {
      setSavingInstructions(false);
    }
  };

  const handleSummarizePrompt = async () => {
    setSummarizingPrompt(true);
    setPromptSummary('');
    try {
      const data = await summarizePaperInstructions(paper.id, customInstructions);
      setPromptSummary(data.summary || '');
    } catch (e) {
      console.error('Failed to summarize prompt:', e);
      setPromptSummary('Unable to generate summary. Please try again.');
    } finally {
      setSummarizingPrompt(false);
    }
  };

  // ── Definition / walkthrough / note handlers ──────────────────────────────

  const handleDefine = async () => {
    if (!selectedPdfText || loadingDefinition) return;
    const term = selectedPdfText.trim();
    setLoadingDefinition(true);
    setDefinition({ term, text: null });
    try {
      const data = await defineTerm(paper.id, term);
      const definitionText = data.definition || '(no definition returned)';
      setDefinition({ term, text: definitionText });
      // Silently save to glossary in the background
      if (user?.id && paper?.id) {
        addGlossaryTerm(user.id, paper.id, term, '', currentPage || null, definitionText).catch(() => {});
      }
    } catch (e) {
      console.error('Failed to fetch definition:', e);
      setDefinition({ term, text: 'Sorry — failed to fetch a definition. Try again.' });
    } finally {
      setLoadingDefinition(false);
    }
  };

  const handleDismissDefinition = () => setDefinition(null);

  const handleWalkthrough = async () => {
    if (!selectedPdfText || loadingWalkthrough) return;
    const selection = selectedPdfText.trim();
    setLoadingWalkthrough(true);
    setWalkthrough({ selection, text: null });
    try {
      const data = await getWalkthrough(paper.id, selection);
      setWalkthrough({ selection, text: data.walkthrough || '(no walkthrough returned)' });
    } catch (e) {
      console.error('Failed to fetch walkthrough:', e);
      setWalkthrough({ selection, text: 'Sorry — failed to generate a walkthrough. Try again.' });
    } finally {
      setLoadingWalkthrough(false);
    }
  };

  const handleDismissWalkthrough = () => setWalkthrough(null);

  const handleSaveNote = async () => {
    if (!noteText.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const data = await createNote(
        user.id,
        paper.id,
        currentPage || 1,
        noteText.trim(),
        selectedPdfText || '',
      );
      onNoteCreated?.(data.note);
      setNoteText('');
      setShowNoteInput(false);
      onClearSelection?.();
    } catch (e) {
      console.error('Failed to save note:', e);
      alert('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setShowNoteInput(false);
    setNoteText('');
  };

  useEffect(() => {
    if (!selectedPdfText) {
      setShowNoteInput(false);
      setNoteText('');
      setDefinition(null);
      setWalkthrough(null);
    }
  }, [selectedPdfText]);

  // ── Mode toggle ────────────────────────────────────────────────────────────

  const handleToggleMode = (targetMode) => {
    if (targetMode === chatMode || loading) return;
    setOptionsPanel(null);
    setAwaitingReview(false);
    setShowChatHistory(false);

    // Snapshot current mode before leaving (include all state needed for full restore)
    if (chatMode === 'qa' || chatMode === 'annotation') {
      if (messages.length > 0) {
        modeStateRef.current[chatMode] = { messages, chatIndex };
      }
    } else if (chatMode === 'test') {
      modeStateRef.current.test = {
        messages, chatIndex, testQuestions,
        progress, grades, categoricalGrades, aiFlagCount, hintsUsedCount, testComplete,
        confidence, testCompleteActionReceived, questionIndex: questionIndexRef.current,
        planId: activePlanIdRef.current,
      };
    } else if (chatMode === 'cycle') {
      modeStateRef.current.cycle = {
        messages, chatIndex, cycleQuestions,
        cyclePhase, cycleProgress,
        // Active-cycle index + review state, so a mode round-trip restores the right
        // cycle (navigator, phase gating, header).
        cycleActiveC, cycleIndex: cycleIndexRef.current, viewingCycle,
      };
    }

    // Restore target mode: in-memory snapshot → localStorage fallback → fresh start
    const saved = modeStateRef.current[targetMode];
    if (saved) {
      const shouldSkip = targetMode === 'qa'
        || (targetMode === 'test' && saved.testQuestions != null)
        || (targetMode === 'cycle' && saved.cycleQuestions != null);
      if (shouldSkip) skipNextHistoryRef.current = true;
      setMessages(saved.messages);
      setChatIndex(saved.chatIndex);
      if (targetMode === 'test') {
        setTestQuestions(saved.testQuestions);
        setProgress(saved.progress);
        setGrades(saved.grades);
        setCategoricalGrades(saved.categoricalGrades);
        setAiFlagCount(saved.aiFlagCount);
        setHintsUsedCount(saved.hintsUsedCount);
        setTestComplete(saved.testComplete);
        setConfidence(saved.confidence);
        setTestCompleteActionReceived(saved.testCompleteActionReceived ?? false);
        // Restore the instructor plan id so completing the test after a mode
        // switch still saves results to analytics (a custom test has none).
        if (saved.planId != null) activePlanIdRef.current = saved.planId;
        questionIndexRef.current = saved.questionIndex ?? (saved.grades ?? []).length;
        setTestActiveQ(questionIndexRef.current);
      } else if (targetMode === 'cycle') {
        setCycleQuestions(saved.cycleQuestions);
        setCyclePhase(saved.cyclePhase);
        setCycleProgress(saved.cycleProgress || { current: 0, total: 0 });
        // Restore active-cycle index + review state so the navigator/header match the
        // student's position.
        if (saved.cycleActiveC != null) {
          setCycleActiveC(saved.cycleActiveC);
          cycleIndexRef.current = saved.cycleIndex ?? saved.cycleActiveC;
        }
        setViewingCycle(saved.viewingCycle ?? null);
      }
    } else {
      // No in-memory snapshot — check localStorage before falling back to a fresh start
      const lsTest = targetMode === 'test' ? (() => {
        try { const r = localStorage.getItem(`pb_test_${user.id}_${paper.id}_${currentSession}`); return r ? JSON.parse(r) : null; } catch { return null; }
      })() : null;
      const lsCycle = targetMode === 'cycle' ? (() => {
        try { const r = localStorage.getItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`); return r ? JSON.parse(r) : null; } catch { return null; }
      })() : null;

      if (targetMode === 'test' && lsTest?.testQuestions) {
        restoreChatIndexRef.current = lsTest.chatIndex ?? null;
        // Skip the generic DB reload — with per-question threading there's no
        // single chatIndex that holds the full conversation history. The saved
        // progress state (grades, progress bar, question index) is what matters
        // for continuing; the student will see a blank chat but can keep going.
        skipNextHistoryRef.current = true;
        setGrades(lsTest.grades ?? []);
        setCategoricalGrades(lsTest.categoricalGrades ?? []);
        setProgress(lsTest.progress ?? { current: 0, total: 0 });
        setAiFlagCount(lsTest.aiFlagCount ?? 0);
        setHintsUsedCount(lsTest.hintsUsedCount ?? 0);
        setTestComplete(lsTest.testComplete ?? { open: false, score: null });
        setTestCompleteActionReceived(lsTest.testCompleteActionReceived ?? false);
        setConfidence(lsTest.confidence ?? 3);
        if (lsTest.planId != null) activePlanIdRef.current = lsTest.planId;
        questionIndexRef.current = lsTest.questionIndex ?? (lsTest.grades ?? []).length;
        setTestActiveQ(questionIndexRef.current);
        setTestQuestions(lsTest.testQuestions);
        setMessages([]);
      } else if (targetMode === 'cycle' && lsCycle?.cyclePlan) {
        restoreChatIndexRef.current = lsCycle.chatIndex ?? null;
        setCycleProgress(lsCycle.progress ?? { current: 0, total: 0 });
        setCyclePhase(lsCycle.cyclePhase ?? null);
        setCycleQuestions(lsCycle.cyclePlan);
        setMessages([]);
      } else {
        // Truly fresh start
        setMessages([]);
        if (targetMode === 'test') {
          setTestQuestions(null);
          setProgress({ current: 0, total: 0 });
          setGrades([]);
          setCategoricalGrades([]);
          setAiFlagCount(0);
          setHintsUsedCount(0);
          setTestComplete({ open: false, score: null });
          setConfidence(3);
        } else if (targetMode === 'cycle') {
          setCycleQuestions(null);
          setCyclePhase(null);
          setCycleProgress({ current: 0, total: 0 });
        }
        // For qa with no snapshot: loadHistory effect fires and reloads from DB
      }
    }
    onRecapInvalidate?.();
    setChatMode(targetMode);
  };

  const handleReset = async () => {
    if (loading) return;
    modeStateRef.current[chatMode] = null;
    if (chatMode === 'test') {
      // Reset returns to the test setup screen (the "Begin test" / source toggle).
      // handleRestart clears testQuestions to null, which makes the setup screen render.
      handleRestart();
    } else if (chatMode === 'cycle') {
      handleCycleRestart();
    } else {
      if (mode === 'instructor' && paper) {
        setCurrentPlan(null);
        setPlanApproved(false);
        setPlanApprovalNotes('');
        setPlanDueDate('');
        setPlanName('');
        setPlanSaved(false);
        setActiveInstructorTest(null);
        setActiveCyclePlan(null);
        activePlanIdRef.current = null;
        cyclePlanIdRef.current = null;
        clearAuthoring();
        setInstructorSetupSubmitted(false);
        deactivatePlans(user.id, paper.id, 'testing').catch(() => {});
        deactivatePlans(user.id, paper.id, 'cycles').catch(() => {});
      }
      await handleNewChat();
    }
  };

  // Sanctioned escape from the test lock: ends the current attempt (so it can't be
  // resumed with answers gleaned elsewhere) and drops the student into Q&A.
  const handleExitTest = async () => {
    if (loading) return;
    if (!window.confirm("Exit the test? This ends your attempt. Your instructor still sees what you answered, and starting again follows their retake settings.")) return;
    try {
      await exitTest(user.id, paper.id, activePlanIdRef.current ?? activeInstructorTest?.plan_id ?? null);
    } catch (e) { console.error("exit-test failed", e); }
    modeStateRef.current.test = null;
    handleRestart();
    setChats([]);
    setShowStudentHome(true);
  };

  // ── Test question navigator (jump & revisit) ─────────────────────────────────
  // Open a past question's own thread for review without changing `chatIndex` (which
  // must stay at the run root so the send path's effectiveChatIndex math holds).
  // The live continuous-flow messages are stashed and restored on return.
  // Resolve question `qNum`'s actual thread chat_index. Per-question threads aren't
  // always contiguous (retries/restarts leave gaps), so we look up the real mapping
  // from /test-run-threads/ instead of assuming root+1+qNum. Falls back to the
  // contiguous assumption only if the lookup yields nothing.
  const resolveQuestionChatIndex = async (sn, rootCi, qNum) => {
    try {
      const { questions } = await getTestRunThreads(user.id, paper.id, sn, rootCi);
      const matches = (questions || []).filter(q => q.question_number === qNum);
      if (matches.length) return matches[matches.length - 1].chat_index; // most recent attempt
    } catch (e) {
      console.warn('test-run-threads lookup failed; using contiguous fallback', e);
    }
    return rootCi + 1 + qNum;
  };

  const handleNavigateToQuestion = async (i) => {
    if (loading || !user || !paper) return;
    if (i === testActiveQ) { handleReturnToCurrent(); return; }
    try {
      if (viewingQuestion == null) continuousMessagesRef.current = messages;
      const sn = effSessionNumber('test');
      const ci = await resolveQuestionChatIndex(sn, chatIndex, i);
      const data = await getConversationHistory(user.id, paper.id, sn, ci);
      setMessages(data.messages || []);
      setViewingQuestion(i);
    } catch (e) {
      console.error('Failed to open question thread:', e);
    }
  };

  const handleReturnToCurrent = () => {
    if (continuousMessagesRef.current != null) {
      setMessages(continuousMessagesRef.current);
      continuousMessagesRef.current = null;
    }
    setViewingQuestion(null);
  };

  // Cycle navigator functions (mirrors test question navigator)
  const handleNavigateToCycle = async (i) => {
    if (loading || !user || !paper) return;
    if (i === cycleActiveC && viewingCycle == null) { handleReturnToCurrentCycleView(); return; }
    if (i > cycleActiveC) return; // future cycle — not yet reached
    try {
      if (viewingCycle == null) continuousMessagesRef.current = messages;
      const sn = effSessionNumber('cycle');
      const data = await getConversationHistory(user.id, paper.id, sn, chatIndex + 1 + i);
      setMessages(data.messages || []);
      setViewingCycle(i);
    } catch (e) {
      console.error('Failed to load cycle thread:', e);
    }
  };

  const handleReturnToCurrentCycleView = () => {
    if (continuousMessagesRef.current != null) {
      setMessages(continuousMessagesRef.current);
      continuousMessagesRef.current = null;
    }
    setViewingCycle(null);
  };

  // ── History loading ────────────────────────────────────────────────────────

  // Restore test or cycle mode on page refresh.
  // useLayoutEffect runs synchronously before any useEffect in the same commit,
  // guaranteeing that skipNextHistoryRef is set before the loadHistory useEffect
  // fires — regardless of definition order or React's scheduling.
  useLayoutEffect(() => {
    if (!user || !paper || !currentSession) return;
    // Instructor authoring restore — bring back the wizard selections, the generated
    // (possibly unsaved) plan, and which screen was open. The chat reloads via the
    // generic loadHistory effect (session 0), so no skip flag is needed here.
    if (mode === 'instructor') {
      try {
        const raw = localStorage.getItem(`pb_instructor_${user.id}_${paper.id}`);
        const s = raw ? JSON.parse(raw) : null;
        const su = s?.setup || {};
        const hasContent = !!s && (!!s.currentPlan || !!s.instructorSubMode || su.submitted
          || !!su.depth || !!su.difficulty || !!su.accuracy || !!su.hints
          || !!s.authorPhase
          || !!(su.instructions && su.instructions.trim()));
        if (hasContent) {
          instructorRestoredRef.current = true;
          if (s.chatIndex != null) restoreChatIndexRef.current = s.chatIndex;
          // Incremental cycle-authoring progress
          if (s.authorOverview !== undefined) setAuthorOverview(s.authorOverview);
          if (s.authorCycleIndex !== undefined) setAuthorCycleIndex(s.authorCycleIndex);
          if (s.authorFinalized !== undefined) setAuthorFinalized(s.authorFinalized || []);
          if (s.authorDraftCycle !== undefined) setAuthorDraftCycle(s.authorDraftCycle);
          if (s.authorPhase !== undefined) setAuthorPhase(s.authorPhase);
          if (s.instructorSubMode !== undefined) setInstructorSubMode(s.instructorSubMode);
          setShowInstructorHome(true);
          if (s.instructorTab) setInstructorTab(s.instructorTab);
          if (s.currentPlan !== undefined) setCurrentPlan(s.currentPlan);
          if (s.planApproved !== undefined) setPlanApproved(s.planApproved);
          if (s.planName !== undefined) setPlanName(s.planName);
          if (s.planDueDate !== undefined) setPlanDueDate(s.planDueDate);
          if (s.planApprovalNotes !== undefined) setPlanApprovalNotes(s.planApprovalNotes);
          if (s.planSaved !== undefined) setPlanSaved(s.planSaved);
          if (su.depth !== undefined) setInstructorSetupDepth(su.depth);
          if (su.cycles !== undefined) setInstructorSetupCycles(su.cycles);
          if (su.qPerCycle !== undefined) setInstructorSetupQPerCycle(su.qPerCycle);
          if (su.difficulty !== undefined) setInstructorSetupDifficulty(su.difficulty);
          if (su.accuracy !== undefined) setInstructorSetupAccuracy(su.accuracy);
          if (su.hints !== undefined) setInstructorSetupHints(su.hints);
          if (su.qTypes !== undefined) setInstructorSetupQTypes(su.qTypes);
          if (su.confidence !== undefined) setInstructorSetupConfidence(su.confidence);
          if (su.bankEnabled !== undefined) setInstructorSetupBankEnabled(su.bankEnabled);
          if (su.bankSize !== undefined) setInstructorSetupBankSize(su.bankSize);
          if (su.instructions !== undefined) setInstructorSetupInstructions(su.instructions);
          if (su.submitted !== undefined) setInstructorSetupSubmitted(su.submitted);
        }
      } catch {}
      return;
    }
    const dropIntoMode = () => {
      // Skip the home picker when the student was mid-activity.
      if (localStorage.getItem(`pb_student_home_dismissed_${paper.id}`) === '1') setShowStudentHome(false);
    };

    // Restore the test snapshot. Returns true if it fired.
    const restoreTest = () => {
      try {
        const raw = localStorage.getItem(`pb_test_${user.id}_${paper.id}_${currentSession}`);
        if (!raw) return false;
        const s = JSON.parse(raw);
        if (!s.testQuestions) return false;
        dropIntoMode();
        restoreChatIndexRef.current = s.chatIndex ?? null;
        skipNextHistoryRef.current = true;
        restoreTestLoadRef.current = true;
        setGrades(s.grades ?? []);
        setCategoricalGrades(s.categoricalGrades ?? []);
        setProgress(s.progress ?? { current: 0, total: 0 });
        setAiFlagCount(s.aiFlagCount ?? 0);
        setHintsUsedCount(s.hintsUsedCount ?? 0);
        setTestComplete(s.testComplete ?? { open: false, score: null });
        setTestCompleteActionReceived(s.testCompleteActionReceived ?? false);
        setConfidence(s.confidence ?? 3);
        if (s.planId != null) activePlanIdRef.current = s.planId;
        questionIndexRef.current = s.questionIndex ?? (s.grades ?? []).length;
        setTestActiveQ(questionIndexRef.current);
        setTestQuestions(s.testQuestions);
        setChatMode('test');
        return true;
      } catch { return false; }
    };

    // Restore the cycle snapshot. Returns true if it fired.
    const restoreCycle = () => {
      try {
        const raw = localStorage.getItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`);
        if (!raw) return false;
        const s = JSON.parse(raw);
        if (!s.cyclePlan) return false;
        // Suppress the generic history effect and trigger a dedicated one-shot load
        // that repaints the conversation from the DB using the cycle's session number.
        dropIntoMode();
        restoreChatIndexRef.current = s.chatIndex ?? null;
        skipNextHistoryRef.current = true;
        restoreCycleLoadRef.current = true;
        setCycleProgress(s.progress ?? { current: 0, total: 0 });
        setCyclePhase(s.cyclePhase ?? null);
        if (s.cycleActiveC != null) {
          setCycleActiveC(s.cycleActiveC);
          cycleIndexRef.current = s.cycleIndex ?? s.cycleActiveC;
        }
        setViewingCycle(s.viewingCycle ?? null);
        setCycleQuestions(s.cyclePlan);
        setChatMode('cycle');
        return true;
      } catch { return false; }
    };

    // Q&A has no snapshot to load — staying put is a successful "restore".
    const restoreQa = () => { dropIntoMode(); return true; };

    // Resume the most-recently-active mode. If its snapshot is gone (e.g. an exited
    // test), fall through to the next-most-recent mode rather than a fixed priority.
    const restorers = { test: restoreTest, cycle: restoreCycle, qa: restoreQa };
    const ts = readModeTimestamps(user.id, paper.id, currentSession);
    for (const m of modesByRecency(ts)) {
      if (restorers[m]()) return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, paper?.id, currentSession]);


  useEffect(() => {
    if (user && paper && currentSession) {
      if (effectiveMode === 'test' && !testQuestions) return;
      if (effectiveMode === 'cycle' && !cycleQuestions) return;
      if (skipNextHistoryRef.current) {
        skipNextHistoryRef.current = false;
        return;
      }
      const restoreIdx = restoreChatIndexRef.current;
      restoreChatIndexRef.current = null;
      loadHistory(restoreIdx);
      onRecapInvalidate?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, paper?.id, currentSession, mode, chatMode]);

  // Dedicated one-shot loader for cycle refresh-restore. The generic effect above
  // is suppressed during restore (skipNextHistoryRef); this fires once cycle state
  // is in place to repaint the saved conversation from the DB.
  useEffect(() => {
    if (!restoreCycleLoadRef.current) return;
    if (effectiveMode === 'cycle' && cycleQuestions) {
      restoreCycleLoadRef.current = false;
      const idx = restoreChatIndexRef.current;
      restoreChatIndexRef.current = null;
      loadHistory(idx);
      onRecapInvalidate?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, cycleQuestions]);

  // Same one-shot loader for test refresh-restore — repaints the test Q&A thread.
  useEffect(() => {
    if (!restoreTestLoadRef.current) return;
    if (effectiveMode === 'test' && testQuestions) {
      restoreTestLoadRef.current = false;
      const base = restoreChatIndexRef.current;
      restoreChatIndexRef.current = null;
      // Resume into the CURRENT question's thread (root+1+questionIndex), not the intro
      // thread, while keeping chatIndex at the root so the send path stays correct.
      (async () => {
        try {
          const sn = effSessionNumber('test');
          const qIdx = questionIndexRef.current || 0;
          // Use the real per-question thread mapping (robust to non-contiguous threads).
          const ci = await resolveQuestionChatIndex(sn, base, qIdx);
          let data = await getConversationHistory(user.id, paper.id, sn, ci);
          if (!(data.messages || []).length) {
            data = await getConversationHistory(user.id, paper.id, sn, base);
          }
          setMessages(data.messages || []);
          setChatIndex(base);
          const chatData = await listChats(user.id, paper.id, sn);
          setChats(chatData.chats || []);
          onRecapInvalidate?.();
        } catch (e) {
          console.error('Test resume load failed:', e);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, testQuestions]);

  useEffect(() => {
    if (mode !== 'student') {
      onRecapAvailableChange?.({ available: false, chatIndex: null, sessionNumber: currentSession });
      return;
    }
    if (chatMode === 'qa') {
      qaChatIndexRef.current = chatIndex;
      if (messages.length > 0) qaHasMessagesRef.current = true;
    }
    onRecapAvailableChange?.({
      available: qaHasMessagesRef.current,
      chatIndex: qaChatIndexRef.current,
      sessionNumber: currentSession,
    });
  }, [mode, chatMode, messages.length, chatIndex, currentSession, onRecapAvailableChange]);

  // Stamp the student's active mode so a refresh resumes it (read by the restore
  // effect above). Only while actually in a mode — not on the study-mode picker.
  useEffect(() => {
    if (mode !== 'student' || showStudentHome || !user || !paper) return;
    try {
      const key = `pb_mode_ts_${user.id}_${paper.id}_${currentSession}`;
      const map = JSON.parse(localStorage.getItem(key) || '{}');
      map[chatMode] = Date.now();
      localStorage.setItem(key, JSON.stringify(map));
    } catch {}
  }, [mode, chatMode, showStudentHome, user?.id, paper?.id, currentSession]);

  // Keep the cycle localStorage snapshot in sync with phase/progress.
  // Also mirror it to the DB (SessionProgress) so resume works cross-device /
  // after localStorage is cleared. The localStorage write is the source of truth
  // for same-device restore; the DB write is a durable, fire-and-forget backup.
  useEffect(() => {
    if (!user || !paper || !cycleQuestions || effectiveMode !== 'cycle') return;
    const snapshot = {
      cyclePlan: cycleQuestions, chatIndex, cyclePhase, progress: cycleProgress,
      // Active-cycle index + review state so a refresh restores the exact cycle
      // (header + navigator).
      cycleActiveC, cycleIndex: cycleIndexRef.current, viewingCycle,
    };
    try {
      localStorage.setItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`, JSON.stringify(snapshot));
    } catch {}
    saveSessionProgress({
      userId: user.id, paperId: paper.id, mode: 'cycle',
      sessionNumber: currentSession, chatIndex,
      currentPosition: cycleProgress?.current ?? 0,
      status: cyclePhase === 'done' ? 'completed' : 'in_progress',
      snapshot,
    }).catch(() => {});
    // cycleActiveC/viewingCycle in deps so the snapshot re-saves on cycle advance/review.
  }, [user, paper, cycleQuestions, chatIndex, cyclePhase, cycleProgress, cycleActiveC, viewingCycle, currentSession, effectiveMode]);

  // Keep the test localStorage snapshot in sync so refreshing mid-test restores state.
  // Also mirror it to the DB (SessionProgress) for durable cross-device resume.
  useEffect(() => {
    if (!user || !paper || !testQuestions || effectiveMode !== 'test') return;
    const snapshot = {
      testQuestions, chatIndex,
      progress, grades, categoricalGrades,
      aiFlagCount, hintsUsedCount,
      testComplete: { open: false, score: testComplete.score },
      confidence,
      questionIndex: questionIndexRef.current,
      testCompleteActionReceived,
      // Persist the instructor plan id so a refresh/cross-device resume can still
      // save results to analytics on completion (null for a custom test).
      planId: activePlanIdRef.current,
    };
    try {
      localStorage.setItem(`pb_test_${user.id}_${paper.id}_${currentSession}`, JSON.stringify(snapshot));
    } catch {}
    saveSessionProgress({
      userId: user.id, paperId: paper.id, mode: 'test',
      sessionNumber: currentSession, chatIndex,
      currentPosition: questionIndexRef.current ?? 0,
      // Use the real completion state (all questions answered), not a bare
      // test_complete flag — a premature test_complete must not mark the run done.
      status: testFinished ? 'completed' : 'in_progress',
      snapshot,
    }).catch(() => {});
  }, [user, paper, testQuestions, chatIndex, progress, grades, categoricalGrades,
      aiFlagCount, hintsUsedCount, testComplete, confidence, testCompleteActionReceived, testFinished,
      currentSession, effectiveMode]);

  // Keep the instructor authoring snapshot in sync (wizard selections + generated
  // plan + which screen), so refreshing mid-authoring resumes exactly where the
  // instructor left off. The chat itself reloads separately (session 0); this only
  // captures the React UI state the chat can't reconstruct. Mirrored to the DB for
  // cross-device durability, same as test/cycle.
  useEffect(() => {
    if (!user || !paper || mode !== 'instructor') return;
    // Only persist a snapshot that actually represents in-progress authoring.
    // Without this, a fresh mount (state still at defaults, but chatIndex already
    // set by loadHistory) would write a BLANK snapshot over a real saved one.
    const hasContent = !!currentPlan || !!instructorSubMode || instructorSetupSubmitted
      || !!instructorSetupDepth || !!instructorSetupDifficulty || !!instructorSetupAccuracy
      || !!instructorSetupHints || !!authorPhase || !!(instructorSetupInstructions && instructorSetupInstructions.trim());
    if (!hasContent) return;
    const snapshot = {
      instructorSubMode, showInstructorHome, instructorTab, chatIndex,
      currentPlan, planApproved, planName, planDueDate, planApprovalNotes, planSaved,
      // Incremental cycle-authoring progress
      authorOverview, authorCycleIndex, authorFinalized, authorDraftCycle, authorPhase,
      setup: {
        depth: instructorSetupDepth, cycles: instructorSetupCycles,
        qPerCycle: instructorSetupQPerCycle, difficulty: instructorSetupDifficulty,
        accuracy: instructorSetupAccuracy, hints: instructorSetupHints,
        qTypes: instructorSetupQTypes, confidence: instructorSetupConfidence,
        bankEnabled: instructorSetupBankEnabled, bankSize: instructorSetupBankSize,
        instructions: instructorSetupInstructions, submitted: instructorSetupSubmitted,
      },
    };
    try {
      localStorage.setItem(`pb_instructor_${user.id}_${paper.id}`, JSON.stringify(snapshot));
    } catch {}
    saveSessionProgress({
      userId: user.id, paperId: paper.id, mode: 'instructor',
      sessionNumber: 0, chatIndex,
      currentPosition: 0,
      status: planSaved ? 'completed' : 'in_progress',
      snapshot,
    }).catch(() => {});
  }, [user, paper, mode, instructorSubMode, showInstructorHome, instructorTab, chatIndex,
      currentPlan, planApproved, planName, planDueDate, planApprovalNotes, planSaved,
      authorOverview, authorCycleIndex, authorFinalized, authorDraftCycle, authorPhase,
      instructorSetupDepth, instructorSetupCycles, instructorSetupQPerCycle,
      instructorSetupDifficulty, instructorSetupAccuracy, instructorSetupHints,
      instructorSetupQTypes, instructorSetupConfidence, instructorSetupInstructions,
      instructorSetupBankEnabled, instructorSetupBankSize,
      instructorSetupSubmitted]);

  // Persist MCQ data from the last AI message that contained option lines, so
  // that "point me to the text" responses (which have no options) don't clear
  // the answer choices. Cleared explicitly on question_complete and test reset.
  useEffect(() => {
    // Don't update while reviewing a past question (viewingQuestion != null) — parsing
    // the past thread's options would overwrite the CURRENT question's MCQ data and
    // corrupt the panel when the student returns to current.
    if (effectiveMode !== 'test' || testFinished || viewingQuestion != null) return;
    const lastAi = messages.filter(m => m.is_ai).at(-1);
    const parsed = lastAi ? parseMcqOptionsFromMessage(lastAi.message) : null;
    if (parsed) setPersistedMcqData(parsed);
  }, [messages, effectiveMode, testFinished, viewingQuestion]);

  // Clear any stale localStorage from a previous test/cycle session whenever
  // the student enters a setup screen, so the new toggle UI is always shown.
  useEffect(() => {
    if (!user || !paper || !currentSession) return;
    if (chatMode === 'test' && !testQuestions) {
      try { localStorage.removeItem(`pb_test_${user.id}_${paper.id}_${currentSession}`); } catch {}
    } else if (chatMode === 'cycle' && !cycleQuestions) {
      try { localStorage.removeItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`); } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, paper?.id, currentSession, chatMode]);

  // Also scroll for the two AI-trouble notices: neither lives in `messages`, so
  // without them in the deps the notice renders below the fold, half-hidden behind
  // the composer, and the student never sees it.
  useEffect(() => {
    scrollToBottom();
  }, [messages, llmDown, aiStalled]);

  // Show instructor home screen and load persisted mode settings when entering instructor mode
  useEffect(() => {
    if (mode === 'instructor' && paper) {
      // Don't force the home screen when we've just restored an in-progress
      // authoring session — that restore decides which screen to show.
      if (!instructorRestoredRef.current) setShowInstructorHome(true);
      modeSettingsLoadedRef.current = false;
      getModeSettings(paper.id)
        .then(data => {
          setCycleEnabled(data.cycle_enabled);
          setTestEnabled(data.test_enabled);
          modeSettingsLoadedRef.current = true;
        })
        .catch(() => { modeSettingsLoadedRef.current = true; });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, paper?.id]);

  // Deactivate plans and persist when a mode is disabled (skip during initial settings load)
  useEffect(() => {
    if (!cycleEnabled && user && paper && modeSettingsLoadedRef.current) {
      deactivatePlans(user.id, paper.id, 'cycles').catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleEnabled]);

  useEffect(() => {
    if (!testEnabled && user && paper && modeSettingsLoadedRef.current) {
      deactivatePlans(user.id, paper.id, 'testing').catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testEnabled]);

  // Pre-fetch mode availability for student view (populates sidebar instructor items)
  useEffect(() => {
    if (mode === 'student' && paper) {
      getActiveTest(paper.id, user?.id)
        .then(data => {
          setHasActiveTest(data.has_test);
          if (data.has_test) {
            const tests = data.tests?.length
              ? data.tests
              : [{ plan_id: data.plan_id, plan: data.plan, due_date: data.due_date, name: data.name || '' }];
            setActiveTests(tests);
            const firstTest = tests[0];
            const numQ = firstTest.plan?.questions?.length ?? firstTest.plan?.num_questions ?? null;
            setInstructorTestMeta({ due_date: firstTest.due_date || null, num_questions: numQ });
          } else {
            setActiveTests([]);
          }
        })
        .catch(() => { setHasActiveTest(false); setActiveTests([]); });
      getActiveCycle(paper.id)
        .then(data => {
          setHasActiveCycle(data.has_cycle);
          if (data.has_cycle) {
            const cycles = data.cycles?.length
              ? data.cycles
              : [{ plan_id: data.plan_id, plan: data.plan, due_date: data.due_date, name: data.name || '' }];
            setActiveCycles(cycles);
            setInstructorCycleMeta({ due_date: cycles[0].due_date || null });
          } else {
            setActiveCycles([]);
          }
        })
        .catch(() => { setHasActiveCycle(false); setActiveCycles([]); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, paper?.id]);

  // Auto-restore cycle mode on mount if the student has a saved in-progress cycle.
  // This prevents returning students from landing on the mode-picker home screen
  // or Q&A view when they should resume their cycle with the progress bar visible.
  useEffect(() => {
    if (!user || !paper || !currentSession || mode !== 'student') return;
    if (cycleAutoRestoredRef.current) return;
    try {
      // Only auto-resume the cycle if it's the most-recently-active mode. Otherwise a
      // lingering cycle snapshot would override the mode the student left off in.
      const ts = readModeTimestamps(user.id, paper.id, currentSession);
      const hasAnyTs = ['test', 'cycle', 'qa'].some(m => ts[m]);
      const cycleMostRecent = (ts.cycle || 0) > 0
        && (ts.cycle || 0) >= (ts.test || 0) && (ts.cycle || 0) >= (ts.qa || 0);
      if (hasAnyTs && !cycleMostRecent) return;
      const lsKey = `pb_cycle_${user.id}_${paper.id}_${currentSession}`;
      const saved = JSON.parse(localStorage.getItem(lsKey) || 'null');
      if (saved?.cyclePlan) {
        cycleAutoRestoredRef.current = true;
        handleToggleMode('cycle');
        setShowStudentHome(false);
        try { localStorage.setItem(`pb_student_home_dismissed_${paper.id}`, '1'); } catch {}
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, paper?.id, currentSession]);

  // Fetch instructor's active test(s) when entering test mode
  useEffect(() => {
    if (chatMode === 'test' && paper && !loadingActiveTest) {
      setLoadingActiveTest(true);
      setActiveInstructorTest(null);
      setActiveTests([]);
      getActiveTest(paper.id, user?.id)
        .then(data => {
          if (data.has_test) {
            const tests = data.tests?.length
              ? data.tests
              : [{ plan_id: data.plan_id, plan: data.plan, due_date: data.due_date || null, name: data.name || '' }];
            setActiveTests(tests);
            // Auto-select if exactly one active test
            if (tests.length === 1) {
              setActiveInstructorTest({ plan_id: tests[0].plan_id, plan: tests[0].plan, due_date: tests[0].due_date || null, name: tests[0].name || '' });
            }
            // Clear any localStorage snapshot that was for a plan no longer active
            try {
              const lsKey = `pb_test_${user?.id}_${paper.id}_${currentSession}`;
              const stored = JSON.parse(localStorage.getItem(lsKey) || 'null');
              if (stored?.plan_id && !tests.some(t => t.plan_id === stored.plan_id)) {
                setTestQuestions(null);
                localStorage.removeItem(lsKey);
              }
            } catch {}
          } else {
            // Clear any stale instructor-plan snapshot when no active test exists
            try {
              const lsKey = `pb_test_${user?.id}_${paper.id}_${currentSession}`;
              const stored = JSON.parse(localStorage.getItem(lsKey) || 'null');
              if (stored?.plan_id) {
                setTestQuestions(null);
                localStorage.removeItem(lsKey);
              }
            } catch {}
          }
        })
        .finally(() => setLoadingActiveTest(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMode, paper?.id]);

  // Fetch instructor's active cycle plan(s) when entering cycle mode
  useEffect(() => {
    if (chatMode === 'cycle' && paper && !activeCyclePlan && !loadingActiveCycle) {
      setLoadingActiveCycle(true);
      setActiveCycles([]);
      getActiveCycle(paper.id)
        .then(data => {
          if (data.has_cycle) {
            const cycles = data.cycles?.length
              ? data.cycles
              : [{ plan_id: data.plan_id, plan: data.plan, due_date: data.due_date || null, name: data.name || '' }];
            setActiveCycles(cycles);
            // Auto-select if exactly one active cycle plan
            if (cycles.length === 1) {
              setActiveCyclePlan({ plan_id: cycles[0].plan_id, plan: cycles[0].plan, due_date: cycles[0].due_date || null, name: cycles[0].name || '' });
            }
          }
        })
        .finally(() => setLoadingActiveCycle(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMode, paper?.id]);

  // Test mode: trigger TestCompleteModal + save results when all questions are graded
  useEffect(() => {
    if (testFinished && !testComplete.open && testComplete.score === null && grades.length > 0) {
      const avg = grades.reduce((a, b) => a + b, 0) / grades.length;
      setTestComplete({ open: true, score: Math.round(avg) });
      // Auto-save results to DB (only once)
      if (!resultsSavedRef.current && activePlanIdRef.current) {
        resultsSavedRef.current = true;
        saveTestResults(
          user.id, paper.id, activePlanIdRef.current,
          categoricalGrades, Math.round(avg), aiFlagCount, hintsUsedCount, chatIndex
        ).catch(e => console.error('Failed to save test results:', e));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testFinished, testComplete.open, testComplete.score, grades]);

  // Cycle mode: show confetti modal when all cycles are finished, at most once ever per user+paper.
  useEffect(() => {
    if (
      chatMode === 'cycle' &&
      cycleProgress.total > 0 &&
      cycleProgress.current >= cycleProgress.total &&
      !cycleComplete.shown &&
      !(cycleConfettiKey && localStorage.getItem(cycleConfettiKey))
    ) {
      if (cycleConfettiKey) localStorage.setItem(cycleConfettiKey, '1');
      setCycleComplete({ open: true, shown: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleProgress.current, cycleProgress.total, cycleComplete.shown, chatMode]);

  // Deterministically draw the active cycle/question's highlights — the
  // instructor's pinned ones, or the AI-suggested ai_highlights when the
  // instructor didn't pin any — at the start of the session and whenever the
  // active item advances. This does NOT depend on the LLM emitting a highlight
  // action.
  const lastDrawnHighlightKeyRef = useRef(null);
  const [rehighlightSeq, setRehighlightSeq] = useState(0);
  useEffect(() => {
    if (mode === 'instructor' || !paper?.id) return;

    let planStr = null;
    let activeIdx = -1;
    if (effectiveMode === 'cycle' && cycleQuestions) {
      planStr = cycleQuestions;
      activeIdx = cycleProgress.current;           // 0-based; cycle 1 at start
    } else if (effectiveMode === 'test' && testQuestions) {
      planStr = testQuestions;
      activeIdx = testActiveQ; // canonical 0-based question index (not AI-driven)
    } else {
      return;
    }

    const items = getPlanItems(planStr);
    const highlights = getEffectiveItemHighlights(items[activeIdx]);

    // Draw each active item's highlights once per run (chatIndex pins the run, so
    // a restarted cycle/test re-draws even with an identical plan).
    const key = `${effectiveMode}:${chatIndex}:${activeIdx}`;
    if (lastDrawnHighlightKeyRef.current === key) return;
    lastDrawnHighlightKeyRef.current = key;

    // Advancing to an item with no highlights must clear the previous item's
    // persistent plan highlights (they don't auto-clear on click/scroll).
    if (!highlights.length) {
      onHighlightResolved?.(null, [], [], { persistent: true });
      return;
    }

    let cancelled = false;
    resolveDrawGroups(paper.id, highlights)
      .then(groups => {
        if (!cancelled && groups.length && onHighlightResolved) {
          onHighlightResolved(groups[0].page, groups[0].rects, groups, { persistent: true });
        }
      })
      .catch(e => console.warn('resolveHighlights failed:', e));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, cycleProgress.current, testActiveQ, cycleQuestions, testQuestions, mode, paper?.id, chatIndex, rehighlightSeq]);

  // Instructor cycle authoring: live preview of the exact highlighting students
  // will see for the cycle being detailed. Draws the draft cycle's effective
  // highlights (instructor-pinned override AI-suggested) through the same
  // resolveHighlights path student mode uses, and redraws whenever the set
  // changes — on first load, after a regenerate, and when the instructor
  // adds/removes a highlight. An empty set clears the preview.
  const lastAuthorPreviewKeyRef = useRef(null);
  useEffect(() => {
    if (mode !== 'instructor' || authorPhase !== 'detailing' || !paper?.id || !authorDraftCycle) return;
    const highlights = getEffectiveItemHighlights(authorDraftCycle);
    const key = `${authorCycleIndex}:${JSON.stringify(highlights)}`;
    if (lastAuthorPreviewKeyRef.current === key) return;
    lastAuthorPreviewKeyRef.current = key;
    if (!highlights.length) {
      onHighlightResolved?.(null, [], [], { persistent: true }); // explicit clear of stale preview paint
      return;
    }
    let cancelled = false;
    resolveDrawGroups(paper.id, highlights)
      .then(groups => {
        if (cancelled || !onHighlightResolved) return;
        if (groups.length) onHighlightResolved(groups[0].page, groups[0].rects, groups, { persistent: true });
        else onHighlightResolved(null, [], [], { persistent: true }); // nothing located — clear stale paint
      })
      .catch(e => console.warn('resolveHighlights (author preview) failed:', e));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, authorPhase, authorDraftCycle, authorCycleIndex, paper?.id]);

  // Map the in-memory mode to the session_number the backend STORES messages under.
  // send-message re-encodes these server-side, but the read endpoints
  // (conversation-history, chat-list) do NOT — so the client must pass the
  // already-encoded number or it reads the wrong (empty) conversation.
  //   instructor → 0  |  test → -base  |  cycle → -(base+100)  |  qa → base
  const effSessionNumber = (em) => {
    if (em === 'instructor') return 0;
    const base = parseInt(currentSession, 10) || 1;
    if (em === 'test') return base > 0 ? -base : base - 1;
    if (em === 'cycle') return -(Math.abs(base) + 100);
    return base;
  };

  const loadHistory = async (targetChatIndex) => {
    // Switching/loading a thread starts fresh for the context-health nudge.
    contextNudgeDismissedRef.current = false;
    setContextDegrading(false);
    try {
      const sessionNum = effSessionNumber(effectiveMode);
      let data = await getConversationHistory(user.id, paper.id, sessionNum, targetChatIndex);
      let msgs = data.messages || [];
      // Resume-fallback: a resume pointer may reference a thread the user explicitly
      // deleted. Rather than wiping, fall back to the latest non-empty thread for
      // this mode so the student keeps a usable conversation.
      if (msgs.length === 0 && targetChatIndex != null) {
        const fb = await getConversationHistory(user.id, paper.id, sessionNum, null);
        if ((fb.messages || []).length > 0) {
          data = fb;
          msgs = fb.messages;
        }
      }
      const loadedIndex = data.chat_index ?? 0;
      setMessages(msgs);
      setChatIndex(loadedIndex);
      // A refresh restores whichever thread was last open; without this the panel
      // comes back as 'qa' and a marker thread never matches its sidebar row.
      // Read the ref, not the closure: the student may have changed mode while
      // this request was in flight, and restoring here would undo it.
      const liveMode = chatModeRef.current;
      if (mode === 'student' && (liveMode === 'qa' || liveMode === 'annotation')) {
        setChatMode(data.annotation_id ? 'annotation' : 'qa');
      }

      const chatData = await listChats(user.id, paper.id, sessionNum);
      setChats(chatData.chats || []);

      if (effectiveMode === 'instructor') {
        setInstructorSetupSubmitted(msgs.length > 0);
      }

      // Stale-state recovery: localStorage may have restored testQuestions/cycleQuestions
      // from a previous run whose conversation no longer exists in the DB. Without this,
      // the test/cycle setup screen never renders and the user sees a blank/Q&A state.
      if (msgs.length === 0) {
        if (effectiveMode === 'test' && testQuestions) {
          setTestQuestions(null);
          try { localStorage.removeItem(`pb_test_${user.id}_${paper.id}_${currentSession}`); } catch {}
        } else if (effectiveMode === 'cycle' && cycleQuestions) {
          setCycleQuestions(null);
          try { localStorage.removeItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`); } catch {}
        }
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  // ── Thread sidebar helpers ──────────────────────────────────────────────────

  const loadAllModeChats = async () => {
    if (!user || !paper || !currentSession) return;
    try {
      const [qaData, testData, cycleData] = await Promise.all([
        listChats(user.id, paper.id, effSessionNumber('student')),
        listChats(user.id, paper.id, effSessionNumber('test')),
        listChats(user.id, paper.id, effSessionNumber('cycle')),
      ]);
      // Marker threads share the Q&A session number, so they are split out here.
      const qaAll = qaData.chats || [];
      setAllModeChats({
        qa: qaAll.filter(c => !c.annotation_id),
        test: testData.chats || [],
        cycle: cycleData.chats || [],
        annotation: qaAll.filter(c => c.annotation_id),
      });
    } catch (e) {
      console.error('Failed to load all mode chats:', e);
    }
  };

  const saveThreadTitle = (key, title) => {
    setThreadTitles(prev => {
      const next = { ...prev, [key]: title };
      try { localStorage.setItem('pb_thread_titles', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleSidebarSwitchToThread = (targetMode, targetChatIndex) => {
    // Test integrity lock: don't let the student leave an active test for another
    // mode via the sidebar (they must finish or "Exit test").
    // Any other thread, including an earlier attempt at this same test: a previous
    // transcript holds its questions, the answers given, and the AI's feedback.
    if (activeTestLock && !(targetMode === 'test' && targetChatIndex === chatIndex)) {
      window.alert('Finish or exit your test before opening another conversation.');
      return;
    }
    setShowThreadSidebar(false);
    // Switching away from a Q&A thread is the ideal moment to refresh its cross-thread
    // memory summary, so any thread we open next recalls it. Fire-and-forget; skip when
    // we're just reloading the same thread (freshness guard also no-ops unchanged threads).
    if (chatMode === 'qa' && messages.length > 0 && chatIndex != null
        && !(targetMode === 'qa' && targetChatIndex === chatIndex)) {
      summarizeThread(user.id, paper.id, effSessionNumber('student'), chatIndex).catch(() => {});
    }
    if (targetMode === chatMode) {
      if (targetChatIndex !== null) {
        loadHistory(targetChatIndex);
      } else {
        // "Go to mode setup". Already in this mode, so nothing reloads — but a
        // past attempt may be on screen, and its transcript is what hides the
        // setup card. Drop it so the assignment click actually lands somewhere.
        setMessages([]);
      }
      return;
    }
    // Returning to cycle with a live run in memory resumes it: restore the full snapshot
    // (messages/phase/progress/active-cycle) rather than re-entering fresh. handleToggleMode
    // sets skipNextHistoryRef=true, so no DB reload runs.
    const resumeLiveCycle = targetMode === 'cycle' && !!modeStateRef.current.cycle?.cycleQuestions;
    if (resumeLiveCycle) {
      handleToggleMode('cycle');
      return;
    }
    // Cross-mode: clear any in-memory snapshot so handleToggleMode doesn't restore stale state,
    // then set the chat index to restore and override any skip flag set by handleToggleMode.
    modeStateRef.current[targetMode] = null;
    // null targetChatIndex means "enter mode fresh" (instructor test/cycle setup screen)
    restoreChatIndexRef.current = targetChatIndex;
    handleToggleMode(targetMode);
    // Override skip flag synchronously (runs before React's next useEffect flush)
    skipNextHistoryRef.current = false;
  };

  const handleSidebarNewChat = async () => {
    // Fresh thread clears the context-health nudge. The new thread still recalls this
    // one via cross-thread memory.
    contextNudgeDismissedRef.current = false;
    setContextDegrading(false);
    // New Chat switches to Q&A without going through handleToggleMode, so snapshot a
    // running cycle here to preserve it for a later return.
    if (chatMode === 'cycle' && cycleQuestions) {
      modeStateRef.current.cycle = {
        messages, chatIndex, cycleQuestions, cyclePhase, cycleProgress,
        cycleActiveC, cycleIndex: cycleIndexRef.current, viewingCycle,
      };
    }
    // Always create a new Q&A thread and switch to Q&A tab
    const sessionNum = effSessionNumber('student');
    // Fold the outgoing Q&A thread into cross-thread memory now (rather than waiting for
    // the periodic backstop), so the fresh chat genuinely recalls the last few turns.
    // Fire-and-forget; the backend has a freshness guard and only summarizes Q&A/cycle.
    if (chatMode === 'qa' && messages.length > 0 && chatIndex != null) {
      summarizeThread(user.id, paper.id, sessionNum, chatIndex).catch(() => {});
    }
    try {
      const data = await newChat(user.id, paper.id, sessionNum);
      const idx = data.chat_index;
      const updatedChats = data.chats || [];
      setChatIndex(idx);
      setChats(updatedChats);
      setMessages([]);
      // Immediately update sidebar so the new thread appears without waiting for loadAllModeChats
      setAllModeChats(prev => ({ ...prev, qa: updatedChats }));
      setChatMode('qa');
      qaHasMessagesRef.current = false;
      qaChatIndexRef.current = idx;
      onRecapInvalidate?.();
      // Full refresh in background to pick up any other changes
      loadAllModeChats();
    } catch (e) {
      console.error('Failed to create new chat from sidebar:', e);
    }
  };

  const handleSidebarDeleteChat = async (targetMode, targetChatIndex) => {
    const sessionNum = effSessionNumber(targetMode === 'qa' ? 'student' : targetMode);
    try {
      const data = await deleteChat(user.id, paper.id, sessionNum, targetChatIndex);
      const remaining = data.chats || [];
      setAllModeChats(prev => ({ ...prev, [targetMode]: remaining }));
      // If deleted the active thread and we're in that mode, load another
      if (targetMode === chatMode && targetChatIndex === chatIndex) {
        setChats(remaining);
        if (remaining.length > 0) {
          await loadHistory(remaining[0].chat_index);
        } else {
          setMessages([]);
          setChatIndex(null);
        }
      }
    } catch (e) {
      console.error('Failed to delete chat from sidebar:', e);
    }
  };

const updateSectionSetting = (sectionNum, key, value) => {
    setSectionSettings(prev => ({
      ...prev,
      [sectionNum]: { difficulty: 5, importance: 5, ...(prev[sectionNum] || {}), [key]: value },
    }));
  };

  const handleAiSuggestSectionSettings = async () => {
    if (!paper?.id || loadingAiSectionSettings) return;
    setLoadingAiSectionSettings(true);
    try {
      const data = await suggestSectionSettings(paper.id, guidedGoals || null);
      const raw = data.settings || {};
      // Merge AI suggestions, clamping values to 0–10
      setSectionSettings(prev => {
        const next = { ...prev };
        Object.entries(raw).forEach(([key, val]) => {
          next[key] = {
            notes: prev[key]?.notes || '',
            skipped: prev[key]?.skipped || false,
            difficulty: Math.min(10, Math.max(0, Math.round(val.difficulty ?? 5))),
            importance: Math.min(10, Math.max(0, Math.round(val.importance ?? 5))),
          };
        });
        return next;
      });
    } catch (e) {
      console.error('AI section settings suggestion failed:', e);
    } finally {
      setLoadingAiSectionSettings(false);
    }
  };

  const handleRegenerateSections = async () => {
    if (!paper?.id || regeneratingSections) return [];
    setRegeneratingSections(true);
    setLoadingPaperSections(true);
    setSuggestedPaperSections(null);
    let sects = [];
    try {
      const data = await suggestSections(paper.id, true);
      sects = data.sections || [];
      setSuggestedPaperSections(sects);
      setAiGeneratedSections(sects);
      setSectionSettings(
        Object.fromEntries(sects.map(s => [s.number, { difficulty: 5, importance: 5, notes: '', skipped: false }]))
      );
      if (sects.length > 0) setInstructorSetupCycles(Math.min(sects.length, 20));
    } catch (e) {
      console.error('Failed to regenerate sections:', e);
      setSuggestedPaperSections([]);
      setAiGeneratedSections([]);
    } finally {
      setRegeneratingSections(false);
      setLoadingPaperSections(false);
    }
    return sects;
  };

  const handleTopicWeightChange = (idx, newWeight) => {
    setTestTopics(prev => {
      if (!prev.length) return prev;
      newWeight = Math.max(0, Math.min(100, newWeight));
      const oldWeight = prev[idx].weight;
      const delta = newWeight - oldWeight;
      if (delta === 0) return prev;

      const otherCount = prev.length - 1;
      let updated;

      if (delta < 0) {
        // Lowering: spread freed weight evenly across all other topics.
        const freed = -delta;
        const base = Math.floor(freed / otherCount);
        let remainder = freed - base * otherCount;
        let othersSeen = 0;
        updated = prev.map((t, i) => {
          if (i === idx) return { ...t, weight: newWeight };
          const add = base + (remainder-- > 0 ? 1 : 0);
          othersSeen++;
          return { ...t, weight: t.weight + add };
        });
      } else {
        // Raising: take proportionally from others (preserves relative balance).
        const othersTotal = prev.reduce((sum, t, i) => i !== idx ? sum + t.weight : sum, 0);
        updated = prev.map((t, i) => {
          if (i === idx) return { ...t, weight: newWeight };
          const share = othersTotal > 0
            ? Math.round(t.weight - delta * (t.weight / othersTotal))
            : Math.round((100 - newWeight) / otherCount);
          return { ...t, weight: Math.max(0, share) };
        });
      }

      // Fix any rounding error by nudging the largest non-changed topic.
      const total = updated.reduce((s, t) => s + t.weight, 0);
      const diff = 100 - total;
      if (diff !== 0) {
        let largestIdx = -1;
        updated.forEach((t, i) => {
          if (i !== idx && (largestIdx === -1 || t.weight > updated[largestIdx].weight)) largestIdx = i;
        });
        if (largestIdx !== -1) {
          updated[largestIdx] = { ...updated[largestIdx], weight: Math.max(0, updated[largestIdx].weight + diff) };
        }
      }
      return updated;
    });
  };

  const handleDeleteTopic = (idx) => {
    setTestTopics(prev => {
      if (prev.length <= 1) return prev;
      const removed = prev[idx].weight;
      const remaining = prev.filter((_, i) => i !== idx);
      if (removed === 0) return remaining;
      // Spread removed weight evenly across survivors.
      const base = Math.floor(removed / remaining.length);
      let remainder = removed - base * remaining.length;
      return remaining.map(t => {
        const add = base + (remainder-- > 0 ? 1 : 0);
        return { ...t, weight: t.weight + add };
      });
    });
  };

  const handleAddTopic = () => {
    const title = newTopicInput.trim();
    if (!title) return;
    setTestTopics(prev => {
      const n = prev.length + 1;
      // New topic gets an equal share; take proportionally from others.
      const newWeight = Math.round(100 / n);
      const leftover = 100 - newWeight;
      const oldTotal = prev.reduce((s, t) => s + t.weight, 0);
      let scaled = prev.map(t => ({
        ...t,
        weight: oldTotal > 0 ? Math.round(t.weight * leftover / oldTotal) : Math.floor(leftover / prev.length),
      }));
      // Fix rounding
      const scaledTotal = scaled.reduce((s, t) => s + t.weight, 0);
      const diff = leftover - scaledTotal;
      if (diff !== 0 && scaled.length) {
        const largest = scaled.reduce((bi, t, i) => t.weight > scaled[bi].weight ? i : bi, 0);
        scaled[largest] = { ...scaled[largest], weight: Math.max(0, scaled[largest].weight + diff) };
      }
      return [...scaled, { title, weight: newWeight }];
    });
    setNewTopicInput('');
  };

  const handleSuggestTopicWeights = async () => {
    if (!testTopics.length || topicWeightSuggestLoading) return;
    setTopicWeightSuggestLoading(true);
    try {
      const data = await suggestTopicWeights(paper.id, testTopics.map(t => t.title));
      if (data.topics?.length === testTopics.length) {
        setTestTopics(prev => prev.map((t, i) => ({ ...t, weight: data.topics[i]?.weight ?? t.weight })));
      }
    } catch {
      // silently ignore — topics unchanged
    } finally {
      setTopicWeightSuggestLoading(false);
    }
  };

  const handleResetTopicWeights = () => {
    setTestTopics(prev => {
      if (!prev.length) return prev;
      const n = prev.length;
      const base = Math.floor(100 / n);
      let rem = 100 - base * n;
      return prev.map(t => ({ ...t, weight: base + (rem-- > 0 ? 1 : 0) }));
    });
  };

  const handleToggleQType = (val) => {
    setInstructorSetupQTypes(prev => {
      const isRemoving = prev.includes(val);
      if (isRemoving && prev.length <= 1) return prev;
      const next = isRemoving ? prev.filter(t => t !== val) : [...prev, val];
      setQTypeWeights(prevW => {
        const n = next.length;
        if (n === 0) return prevW;
        if (isRemoving) {
          const removed = prevW[val] || 0;
          const base = Math.floor(removed / n);
          let rem = removed - base * n;
          const updated = { ...prevW, [val]: 0 };
          next.forEach(t => { updated[t] = (prevW[t] || 0) + base + (rem-- > 0 ? 1 : 0); });
          return updated;
        } else {
          const newWeight = Math.round(100 / n);
          const leftover = 100 - newWeight;
          const existingTotal = prev.reduce((s, t) => s + (prevW[t] || 0), 0);
          const updated = { ...prevW, [val]: newWeight };
          prev.forEach(t => {
            updated[t] = existingTotal > 0
              ? Math.round((prevW[t] || 0) * leftover / existingTotal)
              : Math.floor(leftover / prev.length);
          });
          const total = next.reduce((s, t) => s + (updated[t] || 0), 0);
          const diff = 100 - total;
          if (diff !== 0 && prev.length > 0) {
            const largest = prev.reduce((a, b) => (updated[a] || 0) > (updated[b] || 0) ? a : b, prev[0]);
            updated[largest] = Math.max(0, (updated[largest] || 0) + diff);
          }
          return updated;
        }
      });
      return next;
    });
  };

  const handleQTypeWeightChange = (type, newWeight) => {
    const activeTypes = instructorSetupQTypes;
    if (activeTypes.length < 2) return;
    setQTypeWeights(prev => {
      newWeight = Math.max(0, Math.min(100, newWeight));
      const oldWeight = prev[type] || 0;
      const delta = newWeight - oldWeight;
      if (delta === 0) return prev;
      const others = activeTypes.filter(t => t !== type);
      const othersTotal = others.reduce((s, t) => s + (prev[t] || 0), 0);
      const updated = { ...prev, [type]: newWeight };
      if (delta > 0) {
        others.forEach(t => {
          const share = othersTotal > 0
            ? Math.round((prev[t] || 0) - delta * ((prev[t] || 0) / othersTotal))
            : Math.round((100 - newWeight) / others.length);
          updated[t] = Math.max(0, share);
        });
      } else {
        const freed = -delta;
        const base = Math.floor(freed / others.length);
        let rem = freed - base * others.length;
        others.forEach(t => { updated[t] = (prev[t] || 0) + base + (rem-- > 0 ? 1 : 0); });
      }
      const total = activeTypes.reduce((s, t) => s + (updated[t] || 0), 0);
      const diff = 100 - total;
      if (diff !== 0 && others.length > 0) {
        const largest = others.reduce((a, b) => (updated[a] || 0) > (updated[b] || 0) ? a : b, others[0]);
        updated[largest] = Math.max(0, (updated[largest] || 0) + diff);
      }
      return updated;
    });
  };

  const handleResetQTypeWeights = () => {
    const n = instructorSetupQTypes.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    let rem = 100 - base * n;
    const updated = {};
    ['mcq', 'short-answer', 'counterfactual'].forEach(t => {
      updated[t] = instructorSetupQTypes.includes(t) ? base + (rem-- > 0 ? 1 : 0) : 0;
    });
    setQTypeWeights(updated);
  };

  const handleInstructorSetupSubmit = async () => {
    const isTesting = instructorSubMode === 'test';
    if ((isTesting ? !instructorSetupNumQuestions : !instructorSetupCycles) || !instructorSetupAccuracy || !instructorSetupHints || (isTesting && instructorSetupQTypes.length === 0) || loading) return;
    const modeLabel = isTesting ? 'Assessment / Testing Questions' : 'Guided Reading Cycles';
    const depthLabels = { surface: 'Surface (high-level overview, minimal technical detail)', intermediate: 'Intermediate (solid understanding, some technical depth)', deep: 'Deep (thorough technical explanation, all mechanisms covered)' };
    const difficultyLabels = { easy: 'Easy (recall-based, straightforward)', medium: 'Medium (some inference required)', hard: 'Hard (analysis, synthesis, or application required)' };
    const accuracyLabels = { minimal: 'Minimal is sufficient (a short correct answer is fine)', 'in-depth': 'Needs in-depth answer (students must explain reasoning and detail)' };
    const hintsLabels = { exam: 'Exam (no AI involvement; student answers independently, grade compiled at end)', helpful: 'Helpful (AI confirms right/wrong and points to relevant text; no hints)', guided: 'Guided (AI gives hints and pushes toward the answer without giving it directly)', relaxed: 'Relaxed (AI awaits genuine attempt then reveals correct answer; grading is effort-based and forgiving)' };
    const aiHelpLabels = { minimal: 'Minimal (brief nudges only, student drives independently)', moderate: 'Moderate (guided hints and clarifying explanations)', 'heavily-involved': 'Heavily Involved (detailed walkthroughs and worked examples)' };
    const instructions = instructorSetupInstructions.trim();
    const qTypeLabels = { mcq: 'Multiple Choice (MCQ)', 'short-answer': 'Short Answer', counterfactual: 'Counterfactual' };
    const qTypesLine = isTesting && instructorSetupQTypes.length > 0
      ? `\n- Question types: ${instructorSetupQTypes.map(t => qTypeLabels[t]).join(', ')}`
      : '';
    const qTypeBalanceLine = isTesting && instructorSetupQTypes.length >= 2
      ? `\n- Question type balance: ${instructorSetupQTypes.map(t => `${qTypeLabels[t]} ${qTypeWeights[t] || 0}%`).join(', ')}`
      : '';
    // When a question bank is enabled, generate the full pool (bankSize) and tell
    // the model these are independent, standalone questions — a per-student random
    // subset is sampled server-side, so no question may depend on another.
    const useBank = isTesting && instructorSetupBankEnabled;
    const bankSize = Math.max(instructorSetupNumQuestions, instructorSetupBankSize);
    const cycleKey = isTesting ? 'Number of questions' : 'Number of cycles';
    const cycleOrQCount = isTesting ? (useBank ? bankSize : instructorSetupNumQuestions) : instructorSetupCycles;
    const bankLine = useBank
      ? `\n- Question bank: generate a pool of ${bankSize} INDEPENDENT, standalone questions. Each must stand on its own (no cross-references between questions), because each student is randomly given ${instructorSetupNumQuestions} of them. Aim for varied coverage across the paper so any sampled subset is balanced.`
      : '';
    const depthLine = !isTesting ? `\n- Depth: ${depthLabels[instructorSetupDepth]}` : '';
    const helpLine = isTesting
      ? `\n- AI format: ${hintsLabels[instructorSetupHints]}`
      : `\n- AI tutor involvement: ${aiHelpLabels[instructorSetupHints]}`;
    const confidenceLine = isTesting ? `\n- Confidence meter: ${instructorSetupConfidence ? 'Enabled' : 'Disabled'}` : '';
    const sectionSettingsBlock = (suggestedPaperSections && suggestedPaperSections.length > 0)
      ? '\n\nSection-specific difficulty and importance settings:\n' +
        'HOW TO INTERPRET SECTION DIFFICULTY: The per-section difficulty slider (0–10) OVERRIDES the global difficulty preset entirely for that section. ' +
        '0 = easiest possible, 10 = hardest possible, 5 = medium. Ignore the global difficulty setting for any section that has a slider value set.\n' +
        (isTesting
          ? 'HOW TO INTERPRET IMPORTANCE: 5/10 is neutral (normal question proportion). Below 5 = draw fewer questions from this section; above 5 = draw more. This overrides any global scope setting for the section.\n'
          : 'HOW TO INTERPRET CYCLE DEPTH: The per-section depth slider (0–10) OVERRIDES the global depth preset entirely for that section. ' +
            '0 = surface-level only, 10 = maximum depth, 5 = intermediate. Ignore the global depth setting for any section that has a slider value set.\n') +
        'HOW TO INTERPRET INSTRUCTOR NOTES: Any notes provided for a section take full precedence over all presets and sliders for that section. Follow them exactly.\n' +
        suggestedPaperSections.map(s => {
          const cfg = sectionSettings[s.number] || { difficulty: 5, importance: 5, q_per_cycle: 1, notes: '', skipped: false };
          const importanceDesc = isTesting
            ? `question proportion ${cfg.importance}/10`
            : `cycle depth/length ${cfg.importance}/10`;
          if (cfg.skipped) return `- Section ${s.number} "${s.title}" (${fmtPages(s.start_page, s.end_page)}): SKIPPED — do not assign any cycles or questions to this section`;
          return `- Section ${s.number} "${s.title}" (${fmtPages(s.start_page, s.end_page)}): difficulty slider=${cfg.difficulty}/10, importance=${importanceDesc}, comprehension questions=${cfg.q_per_cycle ?? 1}/section${cfg.notes ? `, instructor notes: "${cfg.notes}"` : ''}`;
        }).join('\n')
      : '';
    const cycleSectionMapping = (!isTesting && suggestedPaperSections && suggestedPaperSections.length === cycleOrQCount)
      ? '\n\nCycle-to-section mapping: The number of cycles matches the number of detected paper sections exactly. Each cycle MUST cover exactly one section in this order — do not merge or split sections:\n' +
        suggestedPaperSections.map((s, i) =>
          `- Cycle ${i + 1}: "${s.title}" (${fmtPages(s.start_page, s.end_page)})`
        ).join('\n')
      : '';
    const goalsLine = guidedGoals ? `\n- Instructor's learning goals: "${guidedGoals}"` : '';
    const topicDistributionLine = (isTesting && testTopics.length > 0)
      ? '\n\nQuestion distribution — you MUST allocate questions across topics in exactly these proportions:\n' +
        testTopics.map(t => `- ${t.title}: ${t.weight}%`).join('\n') +
        '\nRound to the nearest whole question per topic; if rounding leaves the total off by one, adjust the highest-weight topic.'
      : '';
    const difficultyText = difficultyLabels[instructorSetupDifficulty] || difficultyLabels.medium;
    const message = `Please create ${modeLabel} for this paper.\n\nConfiguration:${depthLine}\n- ${cycleKey}: ${cycleOrQCount}${qTypesLine}${qTypeBalanceLine}\n- Question difficulty: ${difficultyText}\n- Student response accuracy: ${accuracyLabels[instructorSetupAccuracy]}${helpLine}${confidenceLine}${bankLine}${goalsLine}${instructions ? `\n- Additional instructions: ${instructions}` : ''}${sectionSettingsBlock}${cycleSectionMapping}${topicDistributionLine}`;
    setInstructorSetupSubmitted(true);
    setLoading(true);

    // Cycle plans are authored incrementally: generate a lightweight overview
    // skeleton first, then detail cycle 1 in a fresh thread. The instructor
    // refines/finalizes each cycle one at a time (see handleFinalizeCycle).
    if (!isTesting) {
      const cycleConfig = {
        depth: instructorSetupDepth,
        num_cycles: instructorSetupCycles,
        difficulty: instructorSetupDifficulty,
        accuracy: instructorSetupAccuracy,
        hints: instructorSetupHints,
      };
      try {
        // 1. Overview (derives from the section mapping baked into `message`).
        const ov = await authorCycle({ userId: user.id, paperId: paper.id, step: 'overview', message, chatIndex });
        const overview = { cycles: (ov.overview?.cycles) || [], config: cycleConfig };
        setAuthorOverview(overview);
        // 2. Fresh thread + detail cycle 1.
        const fresh = await newChat(user.id, paper.id, 0);
        const newIdx = fresh.chat_index;
        setChatIndex(newIdx);
        const det = await authorCycle({
          userId: user.id, paperId: paper.id, step: 'detail',
          overview, cycleNumber: 1, chatIndex: newIdx, silent: true,
          message: 'Generate the full detailed content for Cycle 1.',
        });
        if (!det.cycle) throw new Error('Cycle 1 generation returned no cycle (response could not be parsed).');
        setAuthorFinalized([]);
        setAuthorCycleIndex(0);
        setAuthorDraftCycle(det.cycle || null);
        setAuthorPhase('detailing');
        setMessages(det.ai_response ? [det.ai_response] : []);
        dispatchActions(det.ai_response?.actions);
        setChats((await listChats(user.id, paper.id, 0)).chats || []);
        // Pre-generate the next few cycles in the background while the instructor
        // reviews cycle 1, so the upcoming Finalize steps are instant.
        if (overview.cycles.length > 1) pregenerateAhead(1, overview);
      } catch (e) {
        console.error('Cycle authoring setup failed:', e);
        setInstructorSetupSubmitted(false);
        clearAuthoring();
        alert('Could not generate the first cycle. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const data = await sendMessage(user.id, paper.id, 0, message, null, null, 'instructor', null, false, chatIndex);
      setMessages(prev => [
        ...prev,
        ...(data.student_message ? [data.student_message] : []),
        data.ai_response,
      ]);
      if (data.plan) {
        setCurrentPlan({
          ...data.plan,
          ...(instructorSubMode === 'test' && {
            show_confidence: instructorSetupConfidence,
            hint_style: instructorSetupHints,
          }),
          ...(useBank && { is_bank: true, sample_size: instructorSetupNumQuestions, bank_size: bankSize }),
        });
        setPlanPreviewScreen('edit');
        setPlanSaved(false);
        setPlanAiReply(null);
        setPlanDueDate('');
      }
      const chatData = await listChats(user.id, paper.id, 0);
      setChats(chatData.chats || []);
    } catch (e) {
      console.error('Instructor setup failed:', e);
      setInstructorSetupSubmitted(false);
    } finally {
      setLoading(false);
    }
  };
  handleInstructorSetupSubmitRef.current = handleInstructorSetupSubmit;

  useEffect(() => {
    if (triggerAutoGenerate === 0) return;
    handleInstructorSetupSubmitRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAutoGenerate]);

  // Inline edit of the draft/revisited cycle.
  const handleAuthorEdit = (_index, updatedCycle) => {
    if (authorReviewingIndex !== null) {
      setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? updatedCycle : c));
    } else {
      setAuthorDraftCycle(updatedCycle);
    }
  };

  // Regenerate the whole current (or revisited) cycle, optionally with instructor notes.
  const SECTION_REGEN_PREFIX = {
    core_ideas: 'Regenerate ONLY the core_ideas field — replace it with fresh, high-quality entries. Return the full cycle JSON but change ONLY core_ideas. Keep title, read_sections, recommendations, diagrams, questions, and ai_highlights exactly as they are.',
    recommendations: 'Regenerate ONLY the recommendations field — replace it with fresh, actionable guiding tips. Return the full cycle JSON but change ONLY recommendations. Keep everything else exactly as it is.',
    diagrams: 'Regenerate ONLY the diagrams field — replace it with a new diagram. Return the full cycle JSON but change ONLY diagrams. Keep everything else exactly as it is.',
    highlights: 'Regenerate ONLY the ai_highlights field — replace it with fresh relevant passages from the paper. Return the full cycle JSON but change ONLY ai_highlights. Keep everything else exactly as it is.',
  };

  const SECTION_FIELDS = {
    core_ideas: ['core_ideas'],
    recommendations: ['recommendations'],
    diagrams: ['diagrams', 'mermaid'],
    highlights: ['ai_highlights'],
  };

  const handleAuthorRegenerateSection = async (_index, section, notes) => {
    if (loading) return;
    const isRevisiting = authorReviewingIndex !== null;
    const cycleNum = isRevisiting ? authorReviewingIndex + 1 : authorCycleIndex + 1;
    const prevCycle = isRevisiting ? authorFinalized[authorReviewingIndex] : authorDraftCycle;
    setAuthorRegenLoading(true);
    setLoading(true);
    try {
      let prefix = SECTION_REGEN_PREFIX[section] || 'Regenerate this cycle with fresh content.';
      if (section === 'highlights') {
        const existing = (prevCycle?.ai_highlights || []).map(h => `"${h.text}"`).join('; ');
        prefix = existing
          ? `Regenerate ONLY the ai_highlights field with fresh passages from the paper. The current highlights are: ${existing}. You may keep some of these if they are truly the most important passages, but AT LEAST ONE highlight must be a passage not already in that list — find a different part of the text worth highlighting. Return the full cycle JSON but change ONLY ai_highlights. Keep everything else exactly as it is.`
          : SECTION_REGEN_PREFIX.highlights;
      }
      const msg = notes && notes.trim() ? `${prefix} Additional instructions: ${notes.trim()}` : prefix;
      const det = await authorCycle({
        userId: user.id, paperId: paper.id, step: 'detail',
        overview: authorOverview, cycleNumber: cycleNum,
        chatIndex, message: msg,
      });
      if (det.cycle) {
        const fields = SECTION_FIELDS[section] || [];
        const patch = {};
        fields.forEach(f => { if (det.cycle[f] !== undefined) patch[f] = det.cycle[f]; });
        const updater = prev => ({ ...prev, ...patch });
        if (isRevisiting) {
          setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? updater(c) : c));
        } else {
          setAuthorDraftCycle(updater);
        }
      }
      dispatchActions(det.ai_response?.actions);
    } catch (e) {
      console.error('Failed to regenerate section:', e);
      alert('Failed to regenerate. Please try again.');
    } finally {
      setAuthorRegenLoading(false);
      setLoading(false);
    }
  };

  const handleAuthorRegenerate = async (_index, notes) => {
    if (loading) return;
    const isRevisiting = authorReviewingIndex !== null;
    const cycleNum = isRevisiting ? authorReviewingIndex + 1 : authorCycleIndex + 1;
    const prevCycle = isRevisiting ? authorFinalized[authorReviewingIndex] : authorDraftCycle;
    setAuthorRegenLoading(true);
    setLoading(true);
    try {
      const msg = notes && notes.trim()
        ? `Regenerate this cycle with these changes: ${notes.trim()}`
        : 'Regenerate this cycle with fresh content.';
      const det = await authorCycle({
        userId: user.id, paperId: paper.id, step: 'detail',
        overview: authorOverview, cycleNumber: cycleNum,
        chatIndex, message: msg,
      });
      if (det.cycle) {
        const merged = mergeDraftCycle(prevCycle, det.cycle);
        if (isRevisiting) {
          setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? merged : c));
        } else {
          setAuthorDraftCycle(merged);
        }
      }
      dispatchActions(det.ai_response?.actions);
    } catch (e) {
      console.error('Failed to regenerate cycle:', e);
      alert('Failed to regenerate cycle. Please try again.');
    } finally {
      setAuthorRegenLoading(false);
      setLoading(false);
    }
  };

  // Regenerate a single question within the current (or revisited) draft cycle.
  // The detail step returns the whole cycle, so we splice ONLY the targeted
  // question back in and leave every other question (and its pinned highlights) untouched.
  const handleAuthorRegenerateQuestion = async (_cycleIndex, qIndex, notes) => {
    if (loading || authorRegenQuestionIndex !== null) return;
    const isRevisiting = authorReviewingIndex !== null;
    const activeCycle = isRevisiting ? authorFinalized[authorReviewingIndex] : authorDraftCycle;
    const q = activeCycle?.questions?.[qIndex];
    if (!q) return;
    const total = activeCycle.questions.length;
    const cycleNum = isRevisiting ? authorReviewingIndex + 1 : authorCycleIndex + 1;
    setAuthorRegenQuestionIndex(qIndex);
    setLoading(true);
    try {
      const isBlank = !q.question?.trim();
      const msg = isBlank
        ? `Generate a new unique comprehension question for this cycle and place it at position ${qIndex + 1}. It must be meaningfully different from the ${total - 1} existing question${total - 1 !== 1 ? 's' : ''} already in this cycle. Keep every other question and all other cycle content exactly as it is, and return all ${total} questions in the same order.${notes && notes.trim() ? ` Additional instructions: ${notes.trim()}` : ''}`
        : `Regenerate ONLY question ${qIndex + 1} ("${q.question}") in this cycle — replace it with a brand-new question covering the same idea. Keep every other question and all other cycle content exactly as it is, and return all ${total} questions in the same order.${notes && notes.trim() ? ` Additional instructions: ${notes.trim()}` : ''}`;
      const det = await authorCycle({
        userId: user.id, paperId: paper.id, step: 'detail',
        overview: authorOverview, cycleNumber: cycleNum,
        chatIndex, message: msg,
      });
      const regenerated = Array.isArray(det.cycle?.questions) ? det.cycle.questions : null;
      const newQ = regenerated
        ? (regenerated.length === total ? regenerated[qIndex]
          : regenerated.length === 1 ? regenerated[0]
          : regenerated[qIndex])
        : null;
      if (newQ) {
        const updater = prev => ({
          ...prev,
          questions: (prev.questions || []).map((qq, j) => (j === qIndex ? newQ : qq)),
        });
        if (isRevisiting) {
          setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? updater(c) : c));
        } else {
          setAuthorDraftCycle(updater);
        }
      }
      dispatchActions(det.ai_response?.actions);
    } catch (e) {
      console.error('Failed to regenerate question:', e);
      alert('Failed to regenerate the question. Please try again.');
    } finally {
      setAuthorRegenQuestionIndex(null);
      setLoading(false);
    }
  };

  // "Add question" in cycle authoring: append a question and AUTO-GENERATE it via
  // the LLM (same path as regenerating a blank), so the instructor gets a real
  // draft question to then edit — not an empty box. We compute everything from a
  // locally-built `withBlank` (not state) to avoid a stale-closure race between
  // the setState and the generate call.
  const handleAuthorAddQuestion = async () => {
    if (loading || authorRegenQuestionIndex !== null) return;
    const isRevisiting = authorReviewingIndex !== null;
    const activeCycle = isRevisiting ? authorFinalized[authorReviewingIndex] : authorDraftCycle;
    if (!activeCycle) return;
    const existing = activeCycle.questions || [];
    const newIndex = existing.length;
    const total = newIndex + 1;
    const cycleNum = isRevisiting ? authorReviewingIndex + 1 : authorCycleIndex + 1;
    // Show the new row immediately (blank + spinner) while the LLM fills it in.
    const withBlank = {
      ...activeCycle,
      questions: [...existing, { question: '', hints: [], answer: null, type: 'short-answer', number: total }],
    };
    if (isRevisiting) setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? withBlank : c));
    else setAuthorDraftCycle(withBlank);
    setAuthorRegenQuestionIndex(newIndex);
    setLoading(true);
    try {
      const msg = `Generate a new unique comprehension question for this cycle and place it at position ${total}. It must be meaningfully different from the ${newIndex} existing question${newIndex !== 1 ? 's' : ''} already in this cycle. Keep every other question and all other cycle content exactly as it is, and return all ${total} questions in the same order.`;
      const det = await authorCycle({
        userId: user.id, paperId: paper.id, step: 'detail',
        overview: authorOverview, cycleNumber: cycleNum, chatIndex, message: msg,
      });
      const regenerated = Array.isArray(det.cycle?.questions) ? det.cycle.questions : null;
      const newQ = regenerated
        ? (regenerated.length === total ? regenerated[newIndex]
          : regenerated.length === 1 ? regenerated[0]
          : regenerated[newIndex] ?? regenerated[regenerated.length - 1])
        : null;
      if (newQ) {
        const updater = prev => ({
          ...prev,
          questions: (prev.questions || []).map((qq, j) => (j === newIndex ? newQ : qq)),
        });
        if (isRevisiting) setAuthorFinalized(prev => prev.map((c, i) => i === authorReviewingIndex ? updater(c) : c));
        else setAuthorDraftCycle(updater);
      }
      dispatchActions(det.ai_response?.actions);
    } catch (e) {
      console.error('Failed to add question:', e);
      alert('Failed to add a question. Please try again.');
    } finally {
      setAuthorRegenQuestionIndex(null);
      setLoading(false);
    }
  };

  // Open a previously finalized cycle for review/editing without losing the
  // current draft. The current draft is untouched; we just show a different card.
  const handleRevisitCycle = (pastIndex) => {
    if (loading || pastIndex >= authorFinalized.length) return;
    setAuthorReviewingIndex(pastIndex);
  };

  // Return from a revisited past cycle back to the current cycle being authored.
  const handleReturnToCurrentCycle = () => setAuthorReviewingIndex(null);

  // Lock in the current draft cycle, then either start the next cycle in a fresh
  // thread (keeps history short) or — on the last cycle — assemble the full plan
  // and drop into the standard approve-and-save preview.
  const handleFinalizeCycle = async () => {
    if (!authorDraftCycle || loading) return;
    const finalized = [...authorFinalized, { ...authorDraftCycle, number: authorCycleIndex + 1 }];
    const totalCycles = authorOverview?.cycles?.length || finalized.length;

    if (finalized.length < totalCycles) {
      const nextIndex = authorCycleIndex + 1;
      const pregenned = authorPregeneratedRef.current[nextIndex];
      if (pregenned) {
        // Ready immediately — no wait.
        setAuthorFinalized(finalized);
        setAuthorCycleIndex(nextIndex);
        setAuthorDraftCycle(pregenned.cycle || null);
        setChatIndex(pregenned.chatIndex);
        setMessages([]);
        // Slide the look-ahead window forward (retries any that failed earlier).
        if (nextIndex + 1 < totalCycles) pregenerateAhead(nextIndex + 1, authorOverview);
        setChats((await listChats(user.id, paper.id, 0)).chats || []);
      } else {
        // Background job didn't finish in time — generate on demand.
        setLoading(true);
        try {
          const fresh = await newChat(user.id, paper.id, 0);
          const newIdx = fresh.chat_index;
          const det = await authorCycle({
            userId: user.id, paperId: paper.id, step: 'detail',
            overview: authorOverview, cycleNumber: nextIndex + 1, chatIndex: newIdx, silent: true,
            message: `Generate the full detailed content for Cycle ${nextIndex + 1}.`,
          });
          if (!det.cycle) throw new Error(`Cycle ${nextIndex + 1} generation returned no cycle.`);
          setAuthorFinalized(finalized);
          setAuthorCycleIndex(nextIndex);
          setAuthorDraftCycle(det.cycle || null);
          setChatIndex(newIdx);
          setMessages([]);
          dispatchActions(det.ai_response?.actions);
          // Slide the look-ahead window forward (retries any that failed earlier).
          if (nextIndex + 1 < totalCycles) pregenerateAhead(nextIndex + 1, authorOverview);
          setChats((await listChats(user.id, paper.id, 0)).chats || []);
        } catch (e) {
          console.error('Failed to start next cycle:', e);
          alert('Failed to start the next cycle. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    } else {
      // Last cycle finalized — assemble the standard cycles plan and review.
      const plan = {
        plan_type: 'cycles',
        config: authorOverview?.config || {},
        cycles: finalized.map((c, i) => ({ ...c, number: i + 1 })),
      };
      setAuthorFinalized(finalized);
      setAuthorPhase('review');
      setCurrentPlan(plan);
      setPlanPreviewScreen('edit');
      setPlanSaved(false);
      setPlanApproved(false);
      setPlanAiReply(null);
      setPlanDueDate('');
    }
  };

  const handleNewChat = async () => {
    if (loading) return;
    setOptionsPanel(null);
    setAwaitingReview(false);
    setShowChatHistory(false);
    const sessionNum = effectiveMode === 'instructor' ? 0 : currentSession;
    try {
      const data = await newChat(user.id, paper.id, sessionNum);
      const idx = data.chat_index;
      setChatIndex(idx);
      setChats(data.chats || []);
      setMessages([]);
      if (effectiveMode === 'student') {
        qaHasMessagesRef.current = false;
        qaChatIndexRef.current = idx;
      }
      onRecapInvalidate?.();
      if (effectiveMode === 'instructor') {
        setInstructorSetupSubmitted(false);
      }
      // Refresh sidebar chat lists
      if (mode === 'student') loadAllModeChats();
    } catch (e) {
      console.error('Failed to create new chat:', e);
    }
  };

  const resetSetupForm = () => {
    setInstructorSetupDepth(null);
    setInstructorSetupCycles(suggestedPaperSections?.length > 0 ? Math.min(suggestedPaperSections.length, 20) : 7);
    setInstructorSetupNumQuestions(5);
    setInstructorSetupQPerCycle(1);
    setInstructorSetupDifficulty(null);
    setInstructorSetupAccuracy(null); // unset — instructor must pick before Generate
    setInstructorSetupHints(instructorSubMode === 'test' ? 'guided' : null);
    setInstructorSetupQTypes(['mcq', 'short-answer', 'counterfactual']);
    setQTypeWeights({ mcq: 34, 'short-answer': 33, counterfactual: 33 });
    setInstructorSetupConfidence(false); // default: hide the confidence meter
    setInstructorSetupInstructions('');
    setTestTopics([]);
    setGuidedGoals('');
    setSectionSettings(
      suggestedPaperSections?.length > 0
        ? Object.fromEntries(suggestedPaperSections.map(s => [s.number, { difficulty: 5, importance: 5, notes: '', skipped: false }]))
        : {}
    );
    setShowAdvancedSetup(false);
  };

  const updateGuidedStructureSection = (idx, key, value) => {
    setGuidedStructureSections(prev => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  };

  const removeGuidedStructureSection = (idx) => {
    setGuidedStructureSections(prev => prev.filter((_, i) => i !== idx));
  };

  const moveGuidedStructureSection = (idx, direction) => {
    setGuidedStructureSections(prev => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleAddGuidedStructureSection = () => {
    setGuidedStructureSections(prev => [
      ...prev,
      { number: prev.length + 1, title: 'New Section', start_page: null, end_page: null, description: '', difficulty: 5, importance: 5, q_per_cycle: 1 },
    ]);
  };

  const withSectionDefaults = (sections) =>
    sections.map(s => ({ difficulty: 5, importance: 5, q_per_cycle: 1, ...s }));

  const describeStructureChange = (before, after) => {
    const beforeSet = new Set(before.map(s => s.title));
    const afterSet  = new Set(after.map(s => s.title));
    const removed   = before.filter(s => !afterSet.has(s.title));
    const added     = after.filter(s => !beforeSet.has(s.title));

    if (after.length < before.length) {
      // Merge
      if (removed.length >= 2 && added.length === 1) {
        const nums = removed.map(s => `§${s.number}`).join(' & ');
        return `${nums} merged into "${added[0].title}"`;
      }
      if (removed.length >= 2) {
        return `Sections ${removed.map(s => s.number).join(' & ')} merged`;
      }
      return `${before.length - after.length} section${before.length - after.length > 1 ? 's' : ''} merged`;
    }

    if (after.length > before.length) {
      // Split
      if (removed.length === 1 && added.length >= 2) {
        return `"${removed[0].title}" split into ${added.slice(0, 2).map(s => `"${s.title}"`).join(' and ')}`;
      }
      if (added.length >= 2) {
        return `Split into ${added.slice(0, 2).map(s => `"${s.title}"`).join(' and ')}`;
      }
      return 'Section split';
    }

    // Same count — rename or reorder
    if (added.length === 1 && removed.length === 1) {
      return `"${removed[0].title}" renamed to "${added[0].title}"`;
    }
    if (added.length > 1) {
      return `${added.length} sections renamed`;
    }
    const beforeOrder = before.map(s => s.title).join('|');
    const afterOrder  = after.map(s => s.title).join('|');
    if (beforeOrder !== afterOrder) return 'Sections reordered';
    return 'Sections updated';
  };

  const handleStructureAiRequest = async () => {
    const req = structureAiInput.trim();
    if (!req || structureAiLoading || !paper?.id) return;
    setStructureAiLoading(true);
    setStructureAiMessage('');
    const before = [...guidedStructureSections];
    try {
      const data = await modifySections(paper.id, before, req, guidedGoals || null);
      if (Array.isArray(data.sections) && data.sections.length > 0) {
        setGuidedStructureSections(data.sections);
        setStructureAiMessage(describeStructureChange(before, data.sections));
        setStructureAiInput('');
      }
    } catch (e) {
      console.error('Structure AI request failed:', e);
      setStructureAiMessage('Request failed — please try again.');
    } finally {
      setStructureAiLoading(false);
    }
  };

  const handleContinueToSetup = async () => {
    const renumbered = guidedStructureSections.map((s, i) => ({ ...s, number: i + 1 }));
    setGuidedRecommendation(null);

    setInstructorSetupDepth('intermediate');
    setInstructorSetupCycles(Math.min(renumbered.length, 20));
    setInstructorSetupQPerCycle(1);
    setInstructorSetupDifficulty(null);
    setInstructorSetupNumQuestions(5);
    setInstructorSetupQTypes(['mcq', 'short-answer', 'counterfactual']);
    setInstructorSetupConfidence(false); // default: hide the confidence meter
    setInstructorSetupInstructions('');
    setShowAdvancedSetup(false);

    setSuggestedPaperSections(renumbered);

    // Sliders were set by the AI in the goals step; carry them through from guidedStructureSections
    setSectionSettings(
      Object.fromEntries(renumbered.map(s => [s.number, {
        notes: '',
        skipped: false,
        difficulty: Math.min(10, Math.max(0, Math.round(s.difficulty ?? 5))),
        importance: Math.min(10, Math.max(0, Math.round(s.importance ?? 5))),
        q_per_cycle: s.q_per_cycle ?? 1,
      }]))
    );

    setInstructorSubMode('cycle');
    setGuidedTargetMode('cycle');
    setCurrentPlan(null);
    setPlanApproved(false);
    setPlanApprovalNotes('');
    setOptionsPanel(null);
    setAwaitingReview(false);
    setShowChatHistory(false);
    setInstructorSetupSubmitted(false);
    setGuidedStep(null);
    setInstructorTab('chat');
    // Ensure required fields have defaults in case the instructor left the
    // structure-page dropdowns untouched (value={null} renders the first option
    // visually but never fires onChange, so the state stays null and the submit
    // guard returns early, leaving a blank page).
    if (!instructorSetupAccuracy) setInstructorSetupAccuracy('in-depth');
    if (!instructorSetupHints) setInstructorSetupHints('moderate');

    try {
      const data = await newChat(user.id, paper.id, 0);
      setChatIndex(data.chat_index);
      setChats(data.chats || []);
      setMessages([]);
      setTriggerAutoGenerate(v => v + 1);
    } catch (e) {
      console.error('handleContinueToSetup newChat failed:', e);
    }
  };

  const handleTestGoalsSubmit = async (goals) => {
    setGuidedGoals(goals);

    let aiRec = null;
    if (goals && paper?.id) {
      setGuidedPresetsLoading(true);
      try {
        const sections = aiGeneratedSections || [];
        const data = await suggestPresets(paper.id, goals, sections, 'test');
        aiRec = data.recommendation || null;
      } catch (e) {
        console.error('suggestPresets failed (test mode):', e);
      } finally {
        setGuidedPresetsLoading(false);
      }
    }

    // Apply AI-recommended presets
    const p = aiRec?.presets || {};
    setInstructorSetupNumQuestions(p.num_questions ? Math.min(Math.max(p.num_questions, 3), 25) : 5);
    setInstructorSetupQTypes(Array.isArray(p.q_types) && p.q_types.length ? p.q_types : ['mcq', 'short-answer', 'counterfactual']);
    setInstructorSetupDifficulty(p.difficulty || null);
    setInstructorSetupAccuracy(p.accuracy || null);
    setInstructorSetupHints(p.hints || 'guided');
    // Default the confidence meter OFF unless a preset explicitly recommends it on.
    setInstructorSetupConfidence(p.confidence === true);
    setInstructorSetupInstructions('');
    setShowAdvancedSetup(false);

    // Apply AI-suggested topic distribution if the goal response included topics
    if (Array.isArray(aiRec?.presets?.topics) && aiRec.presets.topics.length > 0) {
      const rawTopics = aiRec.presets.topics;
      const total = rawTopics.reduce((s, t) => s + (t.weight || 0), 0);
      let normalized = total > 0 && total !== 100
        ? rawTopics.map(t => ({ ...t, weight: Math.round(t.weight * 100 / total) }))
        : rawTopics;
      const rTotal = normalized.reduce((s, t) => s + t.weight, 0);
      const diff = 100 - rTotal;
      if (diff !== 0 && normalized.length > 0) {
        const maxIdx = normalized.reduce((bi, t, i) => t.weight > normalized[bi].weight ? i : bi, 0);
        normalized = normalized.map((t, i) => i === maxIdx ? { ...t, weight: t.weight + diff } : t);
      }
      setTestTopics(normalized.map(t => ({ title: String(t.title), weight: Number(t.weight) || 0 })));
    }

    // Apply section adjustments for advanced setup
    const sections = aiGeneratedSections || [];
    if (sections.length > 0) {
      const adjustments = Array.isArray(aiRec?.section_adjustments) ? aiRec.section_adjustments : [];
      const adjustMap = Object.fromEntries(adjustments.map(s => [s.number, s]));
      setSuggestedPaperSections(sections);
      setSectionSettings(
        Object.fromEntries(sections.map(s => [s.number, {
          notes: '',
          skipped: false,
          difficulty: Math.min(10, Math.max(0, Math.round(adjustMap[s.number]?.difficulty ?? 5))),
          importance: Math.min(10, Math.max(0, Math.round(adjustMap[s.number]?.importance ?? 5))),
        }]))
      );
    }

    // Transition to presets page (mirrors handleInstructorModeToggle without resetSetupForm)
    setInstructorSubMode('test');
    setCurrentPlan(null);
    setPlanApproved(false);
    setPlanApprovalNotes('');
    setOptionsPanel(null);
    setAwaitingReview(false);
    setShowChatHistory(false);
    setInstructorSetupSubmitted(false);
    clearAuthoring();
    setInstructorTab('chat');
    setShowInstructorHome(false);

    try {
      const data = await newChat(user.id, paper.id, 0);
      setChatIndex(data.chat_index);
      setChats(data.chats || []);
      setMessages([]);
      onRecapInvalidate?.();
    } catch (e) {
      console.error('handleTestGoalsSubmit newChat failed:', e);
    }
  };

  const handleCycleGoalsSubmit = async (goals) => {
    setGuidedGoals(goals);

    let aiRec = null;
    if (goals && paper?.id) {
      setGuidedPresetsLoading(true);
      try {
        const data = await suggestPresets(paper.id, goals, aiGeneratedSections || [], 'cycle', tocSections);
        aiRec = data.recommendation || null;
      } catch (e) {
        console.error('suggestPresets failed (cycle goals):', e);
      } finally {
        setGuidedPresetsLoading(false);
      }
    }

    const p = aiRec?.presets || {};
    if (p.accuracy) setInstructorSetupAccuracy(p.accuracy);
    if (p.hints) setInstructorSetupHints(p.hints);

    // Use the AI-built cycle structure; fall back to raw sections if the AI didn't return cycles
    const base = Array.isArray(aiRec?.cycles) && aiRec.cycles.length > 0
      ? aiRec.cycles
      : (aiGeneratedSections || []);

    setGuidedStructureSections(base.map((s, i) => ({
      ...s,
      number: i + 1,
      difficulty: Math.min(10, Math.max(0, Math.round(s.difficulty ?? 5))),
      importance: Math.min(10, Math.max(0, Math.round(s.importance ?? 5))),
      q_per_cycle: s.q_per_cycle ?? 1,
    })));

    setGuidedStep('structure');
  };

  const startGuidedForMode = (targetMode) => {
    setGuidedTargetMode(targetMode);
    setGuidedStep(null);
    setGuidedRecommendation(null);
    setGuidedStructureSections([]);
    setGuidedInput('');
    setGuidedGoals('');
    setStructureAiMessage('');
    handleInstructorModeToggle('guided');
    setInstructorTab('chat');
    setShowInstructorHome(false);
    setShowModeHome(null);
  };

  const handleInstructorModeToggle = async (subMode) => {
    if (loading) return;
    setInstructorSubMode(subMode);
    setCurrentPlan(null);
    setPlanApproved(false);
    setPlanApprovalNotes('');
    setOptionsPanel(null);
    setAwaitingReview(false);
    setShowChatHistory(false);
    setInstructorSetupSubmitted(false);
    clearAuthoring();
    resetSetupForm();
    try {
      const data = await newChat(user.id, paper.id, 0);
      const idx = data.chat_index;
      setChatIndex(idx);
      setChats(data.chats || []);
      setMessages([]);
      onRecapInvalidate?.();
    } catch (e) {
      console.error('Failed to switch instructor sub-mode:', e);
    }
  };

  const handleApprovePlan = async () => {
    if (!currentPlan || loading) return;
    setLoading(true);
    try {
      const result = await savePlan(user.id, paper.id, currentPlan, planDueDate || null, planName.trim());
      if (result?.plan_id) {
        await setActivePlan(result.plan_id, user.id, paper.id, planDueDate || null);
      }
      setPlanApproved(true);
      setPlanSaved(true);
      // The plan is locked in — clear the persistent highlight previews from the PDF.
      onHighlightResolved?.(null, [], [], { persistent: true });
      setTimeout(() => {
        setPlanSaved(false);
        setCurrentPlan(null);
        setPlanApproved(false);
        setPlanName('');
        setMessages([]);
        setInstructorSetupSubmitted(false);
        setShowInstructorHome(true);
        clearAuthoring();
        resetSetupForm();
      }, 1500);
    } catch (e) {
      console.error('Failed to save plan:', e);
      alert('Failed to save plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPlanChanges = async () => {
    const notes = planApprovalNotes.trim();
    if (!notes || loading || !currentPlan) return;
    setLoading(true);
    try {
      // Embed the full assembled plan in the message. In the cycle-authoring
      // review phase the active chat thread is the last per-cycle author thread,
      // which never saw the assembled plan — so without this the LLM replies "I
      // don't have an existing lesson plan to modify." (Same approach as
      // handleRegenerateSection.)
      const planJson = JSON.stringify(currentPlan);
      const message = `Current plan: ${planJson}\n\nThe instructor requests the following change(s): ${notes}\n\nApply the requested change(s) and return the COMPLETE updated plan, keeping everything else exactly as it is.`;
      const data = await sendMessage(user.id, paper.id, 0, message, null, null, 'instructor', null, false, chatIndex);
      const aiMsg = data.ai_response?.message || null;
      setMessages(prev => [
        ...prev,
        ...(data.student_message ? [data.student_message] : []),
        data.ai_response,
      ]);
      if (data.plan) {
        setCurrentPlan(prev => ({ ...preserveHighlightsOnRegen(prev, data.plan), show_confidence: prev?.show_confidence ?? true, hint_style: prev?.hint_style ?? null, ...(prev?.retake_policy && { retake_policy: prev.retake_policy, retake_mix_pct_new: prev.retake_mix_pct_new }), ...(prev?.is_bank && { is_bank: true, sample_size: prev.sample_size, bank_size: prev.bank_size }) }));
        setPlanSaved(false);
    setPlanApprovalNotes('');
      }
      setPlanAiReply(aiMsg);
      // Dismiss the callout on the next click anywhere on the page
      const t = setTimeout(() => {
        document.addEventListener('click', () => setPlanAiReply(null), { once: true });
      }, 150);
      const chatData = await listChats(user.id, paper.id, 0);
      setChats(chatData.chats || []);
    } catch (e) {
      console.error('Failed to request plan changes:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSection = (index, updatedSection) => {
    if (!currentPlan) return;
    const isTest = currentPlan.plan_type === 'testing';
    const key = isTest ? 'questions' : 'cycles';
    setCurrentPlan(prev => ({ ...prev, [key]: prev[key].map((s, i) => i === index ? updatedSection : s) }));
  };

  const handleRemoveSection = (index) => {
    if (!currentPlan) return;
    const isTest = currentPlan.plan_type === 'testing';
    const key = isTest ? 'questions' : 'cycles';
    setCurrentPlan(prev => ({
      ...prev,
      [key]: prev[key]
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, number: i + 1 })),
    }));
  };

  const handleReorderSection = (fromIndex, toIndex) => {
    if (!currentPlan) return;
    setCurrentPlan(prev => {
      const questions = [...prev.questions];
      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);
      return { ...prev, questions: questions.map((q, i) => ({ ...q, number: i + 1 })) };
    });
  };

  const handleRegenerateSection = async (index, notes) => {
    if (!currentPlan || regenIndex !== null) return;
    const isTest = currentPlan.plan_type === 'testing';
    const sections = isTest ? currentPlan.questions : currentPlan.cycles;
    const s = sections[index];
    const label = isTest
      ? `question ${s.number ?? index + 1}`
      : `cycle ${s.number ?? index + 1} ("${s.title}")`;
    const planJson = JSON.stringify(currentPlan);
    const message = `Current plan: ${planJson}\n\nPlease regenerate ${label} only — replace it with a completely new ${isTest ? 'question' : 'cycle'} on the same topic. Keep every other ${isTest ? 'question' : 'cycle'} exactly as it is. Return the complete updated plan.${notes ? ` Additional instructions: ${notes}` : ''}`;
    setRegenIndex(index);
    try {
      const data = await sendMessage(user.id, paper.id, 0, message, null, null, 'instructor', null, false, chatIndex);
      // The regenerated item (index) gets fresh content; preserve highlights on every other item.
      if (data.plan) { setCurrentPlan(prev => ({ ...preserveHighlightsOnRegen(prev, data.plan, index), show_confidence: prev?.show_confidence ?? true, hint_style: prev?.hint_style ?? null, ...(prev?.is_bank && { is_bank: true, sample_size: prev.sample_size, bank_size: prev.bank_size }) })); setPlanSaved(false); }
      setMessages(prev => [
        ...prev,
        ...(data.student_message ? [data.student_message] : []),
        data.ai_response,
      ]);
      const chatData = await listChats(user.id, paper.id, 0);
      setChats(chatData.chats || []);
    } catch (e) {
      console.error('Section regeneration failed:', e);
    } finally {
      setRegenIndex(null);
    }
  };

  // Regenerate a single question inside a cycle from the final review preview.
  // Embeds the full plan (the review thread may not hold it) and asks for the
  // complete updated plan back — same approach as handleRegenerateSection.
  const handleRegenerateQuestion = async (cycleIndex, qIndex, notes) => {
    if (!currentPlan || regenQuestionKey !== null) return;
    const cycle = currentPlan.cycles?.[cycleIndex];
    const q = cycle?.questions?.[qIndex];
    if (!q) return;
    const planJson = JSON.stringify(currentPlan);
    const qLabel = q.question ? ` ("${q.question}")` : '';
    const message = `Current plan: ${planJson}\n\nPlease regenerate ONLY question ${qIndex + 1}${qLabel} in cycle ${cycle.number ?? cycleIndex + 1} ("${cycle.title || ''}") — replace it with a brand-new question covering the same idea. Keep every other question, every other cycle, and all other content exactly as it is. Return the COMPLETE updated plan.${notes ? ` Additional instructions: ${notes}` : ''}`;
    setRegenQuestionKey({ cycleIndex, qIndex });
    try {
      const data = await sendMessage(user.id, paper.id, 0, message, null, null, 'instructor', null, false, chatIndex);
      // Only a sub-question changed — the cycle's own highlights still apply, so preserve every cycle's.
      if (data.plan) { setCurrentPlan(prev => ({ ...preserveHighlightsOnRegen(prev, data.plan), show_confidence: prev?.show_confidence ?? true, hint_style: prev?.hint_style ?? null, ...(prev?.is_bank && { is_bank: true, sample_size: prev.sample_size, bank_size: prev.bank_size }) })); setPlanSaved(false); }
      setMessages(prev => [
        ...prev,
        ...(data.student_message ? [data.student_message] : []),
        data.ai_response,
      ]);
      const chatData = await listChats(user.id, paper.id, 0);
      setChats(chatData.chats || []);
    } catch (e) {
      console.error('Question regeneration failed:', e);
    } finally {
      setRegenQuestionKey(null);
    }
  };

  const handleShowAnalytics = () => setShowAnalytics(v => !v);

  const handleCopyPlanJson = async () => {
    try {
      let plan = currentPlan;
      if (!plan) {
        const data = await getActiveTest(paper.id);
        if (data.has_test) plan = data.plan;
      }
      if (!plan) return;
      await navigator.clipboard.writeText(JSON.stringify(plan, null, 2));
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 2000);
    } catch (e) {
      console.error('Failed to copy plan JSON:', e);
    }
  };

  // Nonce so asking twice for the same thread still fires.
  const openThreadNonceRef = useRef(null);
  // Sent with the first message, which creates the conversation row.
  const pendingAnnotationRef = useRef(null);
  useEffect(() => {
    if (!openThreadRequest || openThreadRequest.nonce === openThreadNonceRef.current) return;
    openThreadNonceRef.current = openThreadRequest.nonce;
    pendingAnnotationRef.current = openThreadRequest.annotationId ?? null;
    // Fold the outgoing thread into cross-thread memory before leaving it.
    if (chatMode === 'qa' && messages.length > 0 && chatIndex != null) {
      summarizeThread(user.id, paper.id, effSessionNumber('student'), chatIndex).catch(() => {});
    }
    // Blank thread at the reserved index; nothing is persisted until a message.
    setShowThreadSidebar(false);
    // The home picker hides the composer, so a marker click has to leave it.
    dismissStudentHome();
    // Same session and prompt as Q&A, but its own mode so it never becomes the
    // thread Q&A resumes.
    setActiveAnnotation(openThreadRequest.annotation || null);
    // The mode change would otherwise reload the previous thread over this one.
    skipNextHistoryRef.current = true;
    setChatMode(openThreadRequest.annotationId ? 'annotation' : 'qa');
    setChatIndex(openThreadRequest.chatIndex);
    if (openThreadRequest.resumed) {
      loadHistory(openThreadRequest.chatIndex);
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openThreadRequest]);

  const handleSwitchChat = async (targetIndex) => {
    if (loading || targetIndex === chatIndex) return;
    setShowChatHistory(false);
    await loadHistory(targetIndex);
  };

  const handleDeleteChat = async (targetIndex) => {
    setChatCtxMenu(null);
    try {
      const sessionNum = effSessionNumber(effectiveMode);
      const data = await deleteChat(user.id, paper.id, sessionNum, targetIndex);
      setChats(data.chats || []);
      // If the deleted chat was the active one, load the next available chat
      if (targetIndex === chatIndex) {
        const remaining = data.chats || [];
        if (remaining.length > 0) {
          await loadHistory(remaining[0].chat_index);
        } else {
          setMessages([]);
          setChatIndex(null);
        }
      }
    } catch (e) {
      console.error('Failed to delete chat:', e);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Test mode handlers ─────────────────────────────────────────────────────

  const handleBeginTest = () => {
    handleStartInstructorTest();
  };

  const handleStartInstructorTest = async () => {
    if (!activeInstructorTest || loading) return;
    const questionsJson = JSON.stringify(activeInstructorTest.plan, null, 2);
    skipNextHistoryRef.current = true;
    setTestQuestions(questionsJson);
    resultsSavedRef.current = false;
    activePlanIdRef.current = activeInstructorTest.plan_id;
    retakePlanRef.current = { plan_id: activeInstructorTest.plan_id, plan: activeInstructorTest.plan };
    setGrades([]);
    setCategoricalGrades([]);
    setProgress({ current: 0, total: 0 });
    setAiFlagCount(0);
    setHintsUsedCount(0);
    setTestComplete({ open: false, score: null });
    setTestCompleteActionReceived(false);
    setPersistedMcqData(null);
    questionIndexRef.current = 0;
    setTestActiveQ(0);
    testQPrefetchRef.current = new Map(); // drop any stale prefetched questions from a prior test
    setLoading(true);
    let createdIdx = null;
    try {
      // Allocate the chat index in the SAME (encoded) session where test
      // messages are stored. Passing the raw positive currentSession allocated
      // against the Q&A session, so the index could collide with leftover
      // per-question threads from a prior test — making the AI reuse a stale
      // thread (it would see Q1 already asked and jump to Q2).
      const chatData = await newChat(user.id, paper.id, effSessionNumber('test'),
                                     activeInstructorTest?.plan_id ?? null);
      const newIdx = chatData.chat_index;
      createdIdx = newIdx;
      setChatIndex(newIdx);
      setChats(chatData.chats || []);
      setMessages([]);
      try {
        localStorage.setItem(`pb_test_${user.id}_${paper.id}_${currentSession}`, JSON.stringify({
          testQuestions: questionsJson, chatIndex: newIdx,
          progress: { current: 0, total: 0 }, grades: [], categoricalGrades: [],
          aiFlagCount: 0, hintsUsedCount: 0, testComplete: { open: false, score: null }, confidence: 3,
          planId: activePlanIdRef.current,
        }));
      } catch {}
      const data = await sendMessage(user.id, paper.id, currentSession, 'Start the test.', currentPage, null, 'test', questionsJson, false, newIdx);
      processResponse(data);
      if (!(data.ai_response.actions || []).some(a => a.action_type === 'test_complete')) {
        const followUp = await sendMessage(user.id, paper.id, currentSession, 'Next question.', currentPage, null, 'test', questionsJson, false, newIdx + 1, null, null, 0);
        processQuestionOnly(followUp);
        // Warm the next question(s) in the background so the student doesn't wait on advance.
        prefetchQuestionsAhead(1, newIdx);
      }
    } catch (error) {
      console.error('Error starting instructor test:', error);
      alert('Failed to start test. Please try again.');
      setTestQuestions(null);
      skipNextHistoryRef.current = false;
      // Clean up the empty root we just created so a failed start never leaves an
      // orphan test thread behind (this is what caused empty test runs to pile up).
      if (createdIdx != null) {
        try { await deleteChat(user.id, paper.id, effSessionNumber('test'), createdIdx); } catch {}
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setMessages([]);
    setProgress({ current: 0, total: 0 });
    setGrades([]);
    setCategoricalGrades([]);
    setAiFlagCount(0);
    setHintsUsedCount(0);
    setTestComplete({ open: false, score: null });
    setTestCompleteActionReceived(false);
    setConfidence(3);
    setTestQuestions(null);
    resultsSavedRef.current = false;
    activePlanIdRef.current = null;
    savedTestStateRef.current = null;
    questionIndexRef.current = 0;
    setTestActiveQ(0);
    testQPrefetchRef.current = new Map(); // drop any stale prefetched questions from a prior test
    try { localStorage.removeItem(`pb_test_${user.id}_${paper.id}_${currentSession}`); } catch {}
  };

  const handleRetakeTest = async () => {
    const saved = retakePlanRef.current;
    if (!saved || loading) return;

    const policy = saved.plan?.retake_policy;
    let planToUse = saved.plan;

    if (policy === 'new' || policy === 'random') {
      setLoading(true);
      try {
        const data = await retakeTest(paper.id, saved.plan_id, user.id);
        planToUse = { ...saved.plan, questions: data.questions };
      } catch (err) {
        console.error('Failed to get retake questions:', err);
        alert('Failed to get retake questions. Please try again.');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    const questionsJson = JSON.stringify(planToUse, null, 2);
    setMessages([]);
    setProgress({ current: 0, total: 0 });
    setGrades([]);
    setCategoricalGrades([]);
    setAiFlagCount(0);
    setHintsUsedCount(0);
    setTestComplete({ open: false, score: null });
    setTestCompleteActionReceived(false);
    setConfidence(3);
    setPersistedMcqData(null);
    resultsSavedRef.current = false;
    savedTestStateRef.current = null;
    questionIndexRef.current = 0;
    setTestActiveQ(0);
    setViewingQuestion(null);

    skipNextHistoryRef.current = true;
    setTestQuestions(questionsJson);
    activePlanIdRef.current = saved.plan_id;
    retakePlanRef.current = { ...saved, plan: planToUse };
    setLoading(true);
    try {
      const chatData = await newChat(user.id, paper.id, effSessionNumber('test'));
      const newIdx = chatData.chat_index;
      setChatIndex(newIdx);
      setChats(chatData.chats || []);
      setMessages([]);
      try {
        localStorage.setItem(`pb_test_${user.id}_${paper.id}_${currentSession}`, JSON.stringify({
          testQuestions: questionsJson, chatIndex: newIdx,
          progress: { current: 0, total: 0 }, grades: [], categoricalGrades: [],
          aiFlagCount: 0, hintsUsedCount: 0, testComplete: { open: false, score: null }, confidence: 3,
          plan_id: retakePlanRef.current?.plan_id ?? null,
        }));
      } catch {}
      const data = await sendMessage(user.id, paper.id, currentSession, 'Start the test.', currentPage, null, 'test', questionsJson, false, newIdx);
      processResponse(data);
      if (!(data.ai_response.actions || []).some(a => a.action_type === 'test_complete')) {
        const followUp = await sendMessage(user.id, paper.id, currentSession, 'Next question.', currentPage, null, 'test', questionsJson, false, newIdx + 1, null, null, 0);
        processQuestionOnly(followUp);
      }
    } catch (error) {
      console.error('Error starting retake:', error);
      alert('Failed to start retake. Please try again.');
      setTestQuestions(null);
      skipNextHistoryRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // ── Cycle mode handlers ────────────────────────────────────────────────────

  // Counts cycles in a plan JSON string. Returns 0 if the JSON can't be parsed
  // or no cycles array is found — caller can fall back to incrementing without
  // a known total.
  const countCyclesInPlan = (planJsonStr) => {
    try {
      const parsed = JSON.parse(planJsonStr);
      if (Array.isArray(parsed?.cycles)) return parsed.cycles.length;
      if (Array.isArray(parsed?.plan?.cycles)) return parsed.plan.cycles.length;
      if (Array.isArray(parsed?.cycling_mode?.cycles)) return parsed.cycling_mode.cycles.length;
    } catch {}
    return 0;
  };

  // Shared cycle-start logic for both instructor and custom sources.
  const startCycleSession = async (planJsonStr, planId = null) => {
    skipNextHistoryRef.current = true;
    setCycleQuestions(planJsonStr);
    const total = countCyclesInPlan(planJsonStr);
    setCycleProgress({ current: 0, total });
    setCycleComplete({ open: false });
    setCyclePhase('reading');
    cycleMessageCountRef.current = 0;
    cyclePlanIdRef.current = planId;
    // Reset cycle navigator
    cycleIndexRef.current = 0;
    setCycleActiveC(0);
    setViewingCycle(null);
    continuousMessagesRef.current = null;
    setLoading(true);
    try {
      // Allocate in the encoded cycle session (same place cycle messages live).
      const chatData = await newChat(user.id, paper.id, effSessionNumber('cycle'));
      const newIdx = chatData.chat_index;
      setChatIndex(newIdx);
      setChats(chatData.chats || []);
      setMessages([]);
      try {
        localStorage.setItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`, JSON.stringify({
          cyclePlan: planJsonStr, chatIndex: newIdx, cyclePhase: 'reading',
          progress: { current: 0, total },
          cycleActiveC: 0, cycleIndex: 0, viewingCycle: null,
        }));
      } catch {}
      // Register the cycle run server-side so analytics can pick it up. Fire-and-forget;
      // a failed save shouldn't block the student from starting the cycle.
      saveCycleProgress({
        userId: user.id, paperId: paper.id, planId,
        chatIndex: newIdx, event: 'start', totalCycles: total,
      }).catch(e => console.warn('saveCycleProgress(start) failed:', e));
      const data = await sendMessage(
        user.id, paper.id, currentSession, 'Start the cycling session.',
        currentPage, null, 'cycle', null, false, newIdx, planJsonStr
      );
      const pullContexts = (data.ai_response.actions || []).filter(a => a.action_type === 'pull_context');
      const aiMsg = pullContexts.length ? { ...data.ai_response, pullContexts } : data.ai_response;
      setMessages([...(data.student_message ? [data.student_message] : []), aiMsg]);
      dispatchActions(data.ai_response.actions || []);
    } catch (error) {
      console.error('Error starting cycle:', error);
      alert('Failed to start cycle. Please try again.');
      setCycleQuestions(null);
      setCyclePhase(null);
      skipNextHistoryRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleStartInstructorCycle = async () => {
    if (!activeCyclePlan || loading) return;
    await startCycleSession(JSON.stringify(activeCyclePlan.plan, null, 2), activeCyclePlan.plan_id);
  };

  const handleBeginCycle = () => {
    handleStartInstructorCycle();
  };

  const handleCycleRestart = () => {
    setMessages([]);
    setCycleQuestions(null);
    setCyclePhase(null);
    setCycleProgress({ current: 0, total: 0 });
    setCycleComplete({ open: false });
    try { localStorage.removeItem(`pb_cycle_${user.id}_${paper.id}_${currentSession}`); } catch {}
  };

  const extractInstructorActions = (actions) => {
    const optAct = (actions || []).find(a => a.action_type === 'options');
    if (optAct?.questions?.length) {
      const selections = {};
      for (const q of optAct.questions) {
        if (INSTRUCTOR_OPTION_DEFAULTS[q.id] !== undefined) {
          selections[q.id] = INSTRUCTOR_OPTION_DEFAULTS[q.id];
        } else {
          selections[q.id] = q.choices ? q.choices[0] : '';
        }
      }
      setOptionsPanel({ questions: optAct.questions, selections });
    }
    setAwaitingReview(!!(actions || []).find(a => a.action_type === 'awaiting_review'));
  };

  const handleOptionSelect = (questionId, choice) => {
    setOptionsPanel(prev => ({
      ...prev,
      selections: { ...prev.selections, [questionId]: choice },
    }));
    // Auto-submit for single-question panels (Step 1: mode selection)
    if (optionsPanel?.questions.length === 1) {
      if (effectiveMode === 'instructor' && questionId === 'mode') {
        const subMode = choice === 'Cycle Mode' ? 'cycle' : 'test';
        setInstructorSubMode(subMode);
        setGuidedTargetMode(subMode);
      }
      setOptionsPanel(null);
      sendUserMessage(choice);
    }
  };

  const handleOptionsSubmit = () => {
    if (!optionsPanel) return;
    const parts = optionsPanel.questions.map(q => optionsPanel.selections[q.id] || '');
    const msg = parts.join(', ');
    setOptionsPanel(null);
    sendUserMessage(msg);
  };

  // A hint should also SHOW the student where to look. We pass allowHighlights so
  // the hint's highlight action draws (and persists) even when the question has
  // instructor-pinned highlights — the hint points to a more specific spot.
  const handleGiveHint = () => sendUserMessage('Give me a hint for the current question.', { allowHighlights: true });

  const dispatchActions = (actions, opts = {}) => {
    // In test/cycle mode the item's plan highlights (instructor-pinned, or the
    // AI-authored ai_highlights) are authoritative for the current item. When
    // the active question/cycle has them, drop the live AI's own
    // highlight/scroll actions so they can't asynchronously
    // clobber the plan highlight (the plan-highlight effect, keyed on
    // testActiveQ / cycleProgress, is then the single source of truth). This is
    // what was wiping the 2nd question's instructor highlight: the AI's grading
    // response emitted its own highlight that resolved after and overrode it.
    // Exception: an explicit navigation request (point-me-to-text, allowHighlights)
    // SHOULD draw its highlight — that's the whole point — even when the question
    // already has plan highlights (the student may have scrolled away from them).
    if (!opts.allowHighlights && (effectiveMode === 'test' || effectiveMode === 'cycle')) {
      const planStr = effectiveMode === 'cycle' ? cycleQuestions : testQuestions;
      // Use the ref (not testActiveQ state) for test: within a single
      // sendUserMessage call the state closure is stale, but questionIndexRef is
      // updated synchronously in the question_complete handler, so it reflects the
      // question that processQuestionOnly is about to present.
      const activeIdx = effectiveMode === 'cycle' ? cycleProgress.current : questionIndexRef.current;
      const items = getPlanItems(planStr);
      if (getEffectiveItemHighlights(items[activeIdx]).length > 0) {
        actions = (actions || []).filter(a => {
          const t = a.action_type || a.action;
          return !['highlight', 'highlight_paragraph', 'highlight_sentence', 'scroll_to'].includes(t);
        });
      }
    }

    // The LLM may emit several highlight actions in one response (e.g. a section
    // heading plus a subsection). Each draw clears prior highlights, so collect
    // every resolved span and paint them together once, after the loop — that
    // way they accumulate instead of clobbering each other.
    const highlightGroups = [];
    for (const action of (actions || [])) {
      // DB-resolved highlight (paragraph_id → server bounding boxes).
      // Accept `action_type: 'highlight'` too — the LLM sometimes emits that
      // variant. Fall back to a text search, then a plain page scroll, so a
      // failed id lookup still takes the reader somewhere instead of nothing.
      if (action.action === 'highlight' || action.action_type === 'highlight') {
        if (action.resolved?.rects?.length > 0) {
          // resolved.pages carries the full multi-page span of a section.
          const pages = action.resolved.pages?.length
            ? action.resolved.pages
            : [{ page: action.resolved.page, rects: action.resolved.rects }];
          highlightGroups.push(...pages);
        } else if (action.paragraph_search) {
          onHighlightRequest?.(action.page, action.paragraph_search);
        } else if (action.page != null) {
          onHighlightRequest?.(action.page);
        } else {
          console.warn('Highlight action could not be resolved or located:', action);
        }
        continue;
      }
      if (action.action_type === 'scroll_to' || action.action === 'scroll_to') {
        if (action.target === 'page' && action.value != null) {
          onHighlightRequest?.(action.value);
        } else if (action.page != null) {
          onHighlightRequest?.(action.page);
        }
      } else if (action.action_type === 'highlight_paragraph') {
        onHighlightRequest?.(action.page, action.paragraph);
      } else if (action.action_type === 'highlight_sentence') {
        onHighlightRequest?.(action.page, action.paragraph);
      } else if (action.action_type === 'highlight_figure') {
        const hint = action.caption_preview
          || (action.figure_number != null ? `Figure ${action.figure_number}` : null);
        onZoomRequest?.(action.page ?? currentPage ?? 1, hint, true);
      } else if (action.action_type === 'zoom') {
        const isFigure = action.region === 'figure' || action.figure_number != null;
        const hint = action.caption_preview
          || (action.figure_number != null ? `Figure ${action.figure_number}` : action.region);
        onZoomRequest?.(action.page ?? currentPage ?? 1, hint, isFigure);
      } else if (action.action_type === 'progress_bar') {
        const total = Number(action.total_questions);
        const current = Number(action.current_question);
        if (!Number.isNaN(total) && total > 0) {
          setProgress(prev => ({
            current: Number.isNaN(current) ? prev.current : current,
            total: Math.max(prev.total, total),
          }));
        }
      } else if (action.action_type === 'feedback') {
        // Capture rich feedback so it gets attached to the next question_complete.
        pendingFeedbackRef.current = {
          strengths: action.strengths || '',
          weaknesses: action.weaknesses || '',
          improvements: action.improvements || '',
        };
      } else if (action.action_type === 'question_complete') {
        const studentResponse = pendingStudentResponseRef.current;
        const studentConfidence = pendingConfidenceRef.current;
        const feedback = pendingFeedbackRef.current;
        pendingStudentResponseRef.current = '';
        pendingConfidenceRef.current = 3;
        pendingFeedbackRef.current = null;
        const grade = Number(action.numerical_grade);
        const categorical = action.categorical_grade || '';
        // Derive is_correct from categorical grade for MCQs (Strong→true, Weak/Missing→false).
        // Partial is intentionally null — neither fully correct nor wrong.
        let isCorrect = null;
        if (categorical === 'Strong') isCorrect = true;
        else if (categorical === 'Weak' || categorical === 'Missing') isCorrect = false;
        if (!Number.isNaN(grade)) {
          setGrades(g => [...g, grade]);
          setCategoricalGrades(cg => {
            // Always use the next sequential slot — the AI's explicit question_number
            // is unreliable (it sometimes skips numbers, e.g. 1,3,4,5,6 for a 5-question
            // test), which leaves gaps in the analytics question summary.
            const qNum = cg.length + 1;
            return [
              ...cg,
              {
                question_number: qNum,
                categorical_grade: categorical,
                numerical_grade: grade,
                student_response: studentResponse,
                confidence: studentConfidence,
                is_correct: isCorrect,
                llm_feedback: feedback,
              },
            ];
          });
        }
        questionIndexRef.current += 1;
        setTestActiveQ(questionIndexRef.current);
        setProgress(prev => ({ ...prev, current: prev.current + 1 }));
        setConfidence(3);
        setSelectedMcqOption(null);
        setSelectedMcqOptions(new Set());
        setPersistedMcqData(null);
      } else if (action.action_type === 'cycle_complete') {
        setCyclePhase('done');
        const explicitCycleNum = Number(action.cycle_number);
        setCycleProgress(prev => {
          const next = Math.min(prev.current + 1, prev.total || prev.current + 1);
          const reportedNum = !Number.isNaN(explicitCycleNum) && explicitCycleNum > 0
            ? explicitCycleNum
            : next;
          const msgCount = cycleMessageCountRef.current;
          // Persist this cycle's progress so instructor analytics can pick it up.
          saveCycleProgress({
            userId: user.id, paperId: paper.id, planId: cyclePlanIdRef.current,
            chatIndex, event: 'complete', cycleNumber: reportedNum, messageCount: msgCount,
            totalCycles: prev.total || null,
          }).catch(e => console.warn('saveCycleProgress(complete) failed:', e));
          cycleMessageCountRef.current = 0;
          return { ...prev, current: next };
        });
      } else if (action.action_type === 'test_complete') {
        setTestCompleteActionReceived(true);
      } else if (action.action_type === 'ai_flag') {
        setAiFlagCount(n => n + 1);
        if (effectiveMode === 'cycle') {
          saveCycleProgress({
            userId: user.id, paperId: paper.id, planId: cyclePlanIdRef.current,
            chatIndex, event: 'ai_flag',
          }).catch(e => console.warn('saveCycleProgress(ai_flag) failed:', e));
        }
      } else if (action.action_type === 'hints') {
        const given = Number(action.hints_given);
        if (!Number.isNaN(given) && given > 0) setHintsUsedCount(n => n + given);
      }
    }
    // Paint all collected highlight spans in one pass (clears once, then draws).
    // `persistentHighlights` (e.g. point-me-to-text) paints them in the layer that
    // clicks/scrolling don't dismiss, so the student keeps seeing where to look.
    if (highlightGroups.length > 0) {
      onHighlightResolved?.(
        highlightGroups[0].page, highlightGroups[0].rects, highlightGroups,
        opts.persistentHighlights ? { persistent: true } : undefined,
      );
    }
  };

  const processResponse = (data, opts = {}) => {
    const pullContexts = (data.ai_response.actions || []).filter(
      a => a.action_type === 'pull_context'
    );
    const aiMessage = pullContexts.length
      ? { ...data.ai_response, pullContexts }
      : data.ai_response;
    setMessages(prev => [...prev, data.student_message, aiMessage]);
    // For a hint (allowHighlights) the hint's highlight should draw even when the
    // question is instructor-pinned, and persist so the student keeps seeing it.
    dispatchActions(data.ai_response.actions, opts.allowHighlights
      ? { allowHighlights: true, persistentHighlights: true }
      : undefined);
  };

  // Process a question-only AI response: update MCQ state and dispatch actions,
  // but never add the message to the chat history so it never appears as a bubble.
  const processQuestionOnly = (data) => {
    setPersistedMcqData(parseMcqOptionsFromMessage(data.ai_response?.message));
    dispatchActions(data.ai_response.actions);
    // Re-assert the instructor's pinned highlight for the now-current question
    // AFTER the AI's own actions have been dispatched. The highlight effect may
    // have already fired (and drawn) when testActiveQ advanced, only for this
    // follow-up's dispatchActions to clear/override it — so we reset the dedup
    // key and bump the sequence to force a final redraw that always wins.
    lastDrawnHighlightKeyRef.current = null;
    setRehighlightSeq(n => n + 1);
  };

  // ── Test-mode question prefetch ──────────────────────────────────────────────

  // Kick off (or reuse) a background presentation of test question `qIdx` into its
  // own isolated thread. Idempotent — returns the existing in-flight/resolved
  // promise if one exists, so we never double-fetch. Resolves to the presentation
  // response (or null on failure, which is cleared so a later attempt can retry).
  // `baseIdx` defaults to the live chatIndex but is passed explicitly at test
  // start, where the chatIndex state hasn't settled yet.
  const prefetchTestQuestion = (qIdx, baseIdx = chatIndex) => {
    const total = getTotalQuestions(testQuestions);
    // Q0 is presented synchronously at test start; never request past the last question.
    if (qIdx < 1 || (total && qIdx >= total)) return null;
    if (testQPrefetchRef.current.has(qIdx)) return testQPrefetchRef.current.get(qIdx);
    const p = sendMessage(
      user.id, paper.id, currentSession, 'Next question.',
      currentPage, null, 'test', testQuestions, false, baseIdx + 1 + qIdx,
      null, null, qIdx
    ).catch(e => {
      testQPrefetchRef.current.delete(qIdx); // drop so a later attempt retries
      console.warn(`Prefetch of test question ${qIdx} failed:`, e);
      return null;
    });
    testQPrefetchRef.current.set(qIdx, p);
    return p;
  };

  // Keep a small window of upcoming questions warming in the background.
  const prefetchQuestionsAhead = (fromIdx, baseIdx = chatIndex) => {
    for (let i = fromIdx; i < fromIdx + TEST_Q_PREFETCH_LOOKAHEAD; i++) {
      prefetchTestQuestion(i, baseIdx);
    }
  };

  // Present question `qIdx`, using the background-prefetched response if it's ready
  // (instant) or awaiting it if still in flight. Falls back to a fresh live fetch
  // if prefetch was never started or errored, so the student always advances.
  // After presenting, slides the prefetch window forward.
  const presentNextQuestion = async (qIdx) => {
    let p = testQPrefetchRef.current.get(qIdx) || prefetchTestQuestion(qIdx);
    let data = p ? await p : null;
    if (!data) {
      data = await sendMessage(
        user.id, paper.id, currentSession, 'Next question.',
        currentPage, null, 'test', testQuestions, false, chatIndex + 1 + qIdx,
        null, null, qIdx
      );
    }
    processQuestionOnly(data);
    prefetchQuestionsAhead(qIdx + 1);
  };

  // ── Message sending ────────────────────────────────────────────────────────

  const sendUserMessage = async (text, opts = {}) => {
    if (!text.trim() || loading) return;
    setOptionsPanel(null);
    setAwaitingReview(false);
    setLlmDown(null);   // retrying clears any previous "AI is down" notice

    // Cycle authoring: instructor follow-up about the cycle currently being
    // detailed. Stays in the current thread; the LLM returns the full updated
    // cycle, which replaces the draft.
    if (mode === 'instructor' && authorPhase === 'detailing') {
      setLoading(true);
      try {
        const det = await authorCycle({
          userId: user.id, paperId: paper.id, step: 'detail',
          overview: authorOverview, cycleNumber: authorCycleIndex + 1,
          chatIndex, message: text,
        });
        if (det.cycle) setAuthorDraftCycle(prev => mergeDraftCycle(prev, det.cycle));
        dispatchActions(det.ai_response?.actions);
      } catch (error) {
        console.error('Error refining cycle:', error);
        alert(sendErrorMessage(error));
      } finally {
        setLoading(false);
      }
      onClearSelection?.();
      return;
    }

    if (effectiveMode === 'test') {
      pendingStudentResponseRef.current = text.trim();
      pendingConfidenceRef.current = confidence;
    }
    setLoading(true);
    if (effectiveMode === 'cycle') {
      // 2 exchanges per turn: student message + AI reply. Counted before await
      // so a cycle_complete returned in this same response includes this turn.
      cycleMessageCountRef.current += 2;
    }
    try {
      const sessionNum = effectiveMode === 'instructor' ? 0 : currentSession;
      const messageToSend = (effectiveMode === 'test' && getShowConfidence(testQuestions))
        ? `${text} (Confidence: ${confidence}/5)`
        : text;

      // In test mode each question gets its own conversation thread so the LLM
      // only sees that question's exchange when grading. chatIndex is the base
      // index allocated at test start. We add 1 so that question threads start
      // at chatIndex+1, keeping the initialization thread (chatIndex+0, which
      // contains "Start the test." + intro) separate. Without this offset the AI
      // sees "Start the test." as prior context when grading Q0 and re-introduces
      // itself. processResponse() increments questionIndexRef on question_complete,
      // so by the time the "Next question." follow-up is sent the index has already
      // advanced to the next question's slot.
      const effectiveChatIndex = effectiveMode === 'test'
        ? chatIndex + 1 + questionIndexRef.current
        : effectiveMode === 'cycle'
        ? chatIndex + 1 + cycleIndexRef.current
        : chatIndex;

      // Per-question / per-cycle threads get a number so _chat_list can hide them from sidebar
      const questionNumberParam = effectiveMode === 'test'
        ? questionIndexRef.current
        : effectiveMode === 'cycle'
        ? cycleIndexRef.current
        : null;

      const pendingAnnotationId = pendingAnnotationRef.current;
      // Whether this send is what makes the thread visible in the sidebar.
      const threadWasEmpty = messages.length === 0;
      const data = await sendMessage(
        user.id, paper.id, sessionNum, messageToSend,
        currentPage, selectedPdfText, apiMode, testQuestions, false, effectiveChatIndex,
        effectiveMode === 'cycle' ? cycleQuestions : null,
        apiMode === 'guided' ? guidedTargetMode : null,
        questionNumberParam,
        pendingAnnotationId
      );
      // Only the first message needs it; the thread carries the link from here on.
      if (pendingAnnotationId) pendingAnnotationRef.current = null;
      // The sidebar hides empty threads, and the other refresh runs before this.
      if (mode === 'student' && threadWasEmpty) loadAllModeChats();

      if (effectiveMode === 'test') {
        processResponse(data, { allowHighlights: opts.allowHighlights });
        const responseActions = data.ai_response.actions || [];
        const testDone = responseActions.some(a => a.action_type === 'test_complete');
        const qDone = responseActions.some(a => a.action_type === 'question_complete');
        // Present the next question whenever the plan still has unanswered questions.
        // questionIndexRef was incremented in the question_complete handler, so it now
        // points at the next (0-based) question — if that's still within the plan,
        // there's more to ask. A premature test_complete (the AI miscounting on an
        // isolated per-question thread) must NOT suppress the remaining questions; we
        // only honor test_complete as a stop signal when the plan count is unknown.
        const planCount = getPlanItems(testQuestions).length;
        const moreQuestionsRemain = planCount > 0
          ? questionIndexRef.current < planCount
          : !testDone;
        if (qDone && moreQuestionsRemain) {
          // Each question lives on its own isolated thread (chatIndex + 1 + index),
          // tagged with question_number. The presentation was very likely prefetched
          // in the background while the student worked on the previous question, so
          // this resolves instantly. Presenting it advances testActiveQ, which fires
          // the deterministic plan-highlight effect to draw this question's highlight.
          await presentNextQuestion(questionIndexRef.current);
        } else if (!qDone && !opts.allowHighlights) {
          // Wrong answer — the AI may have emitted its own highlight action that
          // overrides the instructor's pinned highlight. Reset the dedup key and
          // bump the sequence counter so the highlight effect re-fires and
          // re-draws the instructor's pinned highlight for the current question.
          // Skipped for hints (allowHighlights): the hint's own precise highlight
          // is the point — re-asserting the question highlight would clobber it.
          lastDrawnHighlightKeyRef.current = null;
          setRehighlightSeq(n => n + 1);
        }
      } else {
        const pullContextsElse = (data.ai_response.actions || []).filter(a => a.action_type === 'pull_context');
        const aiMsgElse = pullContextsElse.length ? { ...data.ai_response, pullContexts: pullContextsElse } : data.ai_response;
        setMessages(prev => [
          ...prev,
          ...(data.student_message ? [data.student_message] : []),
          aiMsgElse,
        ]);
        // Q&A only: once the backend reports the thread's context is degrading, surface
        // the "start a fresh conversation" nudge (it stays until acted on or dismissed).
        // Never in marker threads: they are excluded from cross-thread memory, so
        // "I'll still remember what we covered" would be untrue there.
        if (effectiveMode === 'student' && chatMode !== 'annotation'
            && data.context_health === 'degrading' && !contextNudgeDismissedRef.current) {
          setContextDegrading(true);
        }
        if (data.plan) {
          setCurrentPlan(prev => preserveHighlightsOnRegen(prev, data.plan));
          setPlanApproved(false);
          setPlanSaved(false);
        }

        if (data.recommendation) {
          setGuidedRecommendation(data.recommendation);
          if (guidedTargetMode === 'cycle') {
            setGuidedStructureSections(withSectionDefaults(aiGeneratedSections));
            setGuidedStep('structure');
          }
        }

        if (effectiveMode === 'instructor') {
          extractInstructorActions(data.ai_response.actions);
        }

        // Single source of truth for action handling (highlights, scroll, zoom,
        // cycle/test events). Shared with the test-mode path via processResponse
        // so highlight behavior can never drift between modes. A hint
        // (allowHighlights) draws its precise highlight even over a cycle's pinned
        // highlights, and persists it so the student keeps seeing where to look.
        dispatchActions(data.ai_response.actions, opts.allowHighlights
          ? { allowHighlights: true, persistentHighlights: true }
          : undefined);
      }

      onClearSelection?.();
    } catch (error) {
      console.error('Error sending message:', error);
      if (isLlmUnavailable(error)) setLlmDown(sendErrorMessage(error));
      else alert(sendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = inputMessage.trim();
    if (!text) return;
    setInputMessage('');
    await sendUserMessage(text);
  };

  const handleChipClick = (question) => {
    sendUserMessage(question);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const currentSessionInfo = paper.sessions?.find(s => s.number === currentSession);
  const showInstructorAnalytics = mode === 'instructor' && showAnalytics;
  const showStudentOverview = mode === 'instructor' && showStudents && !showInstructorAnalytics;
  const showInstructorPanel = showInstructorAnalytics || showStudentOverview;
  // Clickable MCQ options: derive from the test plan's CURRENT question — reliable
  // regardless of how the AI phrased its message — and fall back to parsing the AI
  // message text only when the plan doesn't give us usable options.
  const planQuestion = getQuestionFromPlan(testQuestions, questionIndexRef.current);
  const mcqDataFromPlan = (() => {
    if (!planQuestion || planQuestion.type !== 'mcq' || !Array.isArray(planQuestion.options)) return null;
    const options = planQuestion.options
      .map((o, i) => {
        const str = String(o).trim();
        const m = str.match(/^\*{0,2}([A-H])[.)]\*{0,2}\s*(.+)$/);
        return m ? { letter: m[1], text: m[2].trim() } : { letter: 'ABCDEFGH'[i] || String(i + 1), text: str };
      })
      .filter(o => o.text);
    if (options.length < 2) return null;
    const answer = String(planQuestion.answer || '');
    const isMultiAnswer = /,/.test(answer) || /select all( that apply| correct| that are correct)/i.test(planQuestion.question || '');
    return { options, isMultiAnswer, questionText: planQuestion.question || '' };
  })();
  // Prefer plan-derived options. Fall back to AI-message-parsed options ONLY when
  // the plan can't classify the current question — never for one the plan says is
  // non-MCQ (e.g. short-answer/FRQ), which would otherwise render stale MCQ options
  // parsed from a previous question's message on top of a free-response question.
  const planClassifiesNonMcq = !!(planQuestion && planQuestion.type && planQuestion.type !== 'mcq');
  const mcqData = (effectiveMode === 'test' && !testFinished)
    ? (mcqDataFromPlan || (planClassifiesNonMcq ? null : persistedMcqData))
    : null;
  const showConfidenceMeter = getShowConfidence(testQuestions);
  const currentQuestionText = planQuestion?.question || mcqData?.questionText || '';
  const hintStyle = getHintStyle(testQuestions);
  const showHintButton = hintStyle === 'guided' || hintStyle === 'relaxed';
  // Cycle mode offers hints too, gated by the instructor's cycle hint preset.
  const showCycleHintButton = hintsEnabled(getCycleHintStyle(cycleQuestions));

  // Step breadcrumb — shown on guided setup steps
  const CYCLE_STEPS = ['Home', 'Goals', 'Cycle Structure', 'Generate & Edit', 'Finalize'];
  const TEST_STEPS  = ['Home', 'Goals', 'Presets', 'Generate & Edit', 'Finalize'];

  // Jump back to an already-completed guided step.
  const goToGuidedStep = (target) => {
    if (loading) return;
    // Leaving the generate step — tear down any in-progress cycle authoring.
    if (authorPhase === 'detailing') clearAuthoring();
    if (target === 1) { // Home
      setShowInstructorHome(true);
      return;
    }
    setShowInstructorHome(false);
    setInstructorSetupSubmitted(false);
    setMessages([]);
    if (target === 2) {        // Goals (both modes)
      setInstructorSubMode('guided');
      setGuidedStep(null);
    } else if (guidedTargetMode === 'test') {
      if (target === 3) {      // Presets (test — no structure step)
        setInstructorSubMode('test');
        setGuidedStep(null);
      }
    } else {
      if (target === 3) {      // Structure (cycle)
        setInstructorSubMode('guided');
        setGuidedStep('structure');
      }
    }
  };

  const renderGuidedStepBar = (currentStep, steps = CYCLE_STEPS) => (
    <div className="guided-step-bar">
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const isPast = stepNum < currentStep;
        const isCurrent = stepNum === currentStep;
        return (
          <React.Fragment key={stepNum}>
            {i > 0 && <span className="guided-step-sep">›</span>}
            {isPast ? (
              <button
                type="button"
                className="guided-step-label past guided-step-label--link"
                onClick={() => goToGuidedStep(stepNum)}
                disabled={loading}
                title={`Back to ${label}`}
              >
                {label}
              </button>
            ) : (
              <span className={`guided-step-label${isCurrent ? ' current' : ''}`}>
                {label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Universal guided-flow step — computed once, rendered once in the header.
  let guidedHeaderStep = null;
  if (mode === 'instructor' && guidedTargetMode && !showInstructorHome && !showModeHome) {
    const isTest = guidedTargetMode === 'test';
    if (instructorSubMode === 'guided' && !guidedStep)               guidedHeaderStep = 2; // Goals
    else if (instructorSubMode === 'guided' && guidedStep === 'structure') guidedHeaderStep = 3; // Structure (cycle)
    else if (isTest  && instructorSubMode === 'test')                 guidedHeaderStep = instructorSetupSubmitted || currentPlan ? (currentPlan && planPreviewScreen === 'final' ? 5 : 4) : 3; // Presets / Generate / Finalize
    else if (!isTest && instructorSubMode === 'cycle')                guidedHeaderStep = currentPlan ? 5 : 4; // Generate / Finalize
    else if (!isTest && authorPhase === 'detailing')                  guidedHeaderStep = 4; // Generate (cycle authoring)
  }
  const guidedHeaderSteps = guidedTargetMode === 'test' ? TEST_STEPS : CYCLE_STEPS;

  return (
    <div className={`chat-section${showInstructorAnalytics || showStudentOverview || showAnalyticsAssistant ? ' chat-section--analytics' : ''}${guidedTargetMode === 'test' ? ' chat-section--test-guided' : ''}`}>
      {!showInstructorAnalytics && !showStudentOverview && !showAnalyticsAssistant && (
        <div className={`chat-thinking-bar${loading ? ' chat-thinking-bar--active' : ''}`} />
      )}
      {showAnalyticsAssistant ? (
        <AnalyticsAssistant paper={paper} onBack={() => { setShowAnalyticsAssistant(false); setShowInstructorHome(true); }} />
      ) : showInstructorAnalytics ? (
        <InstructorAnalytics paper={paper} onBack={() => setShowAnalytics(false)} />
      ) : showStudentOverview ? (
        <StudentOverview paper={paper} onBack={() => setShowStudents(false)} />
      ) : (
      <>
      <div className={`chat-header ${effectiveMode === 'instructor' ? 'chat-header--instructor' : ''}`}>
        <div className={`chat-header-top${effectiveMode === 'instructor' ? ' chat-header-top--centered' : ''}`}>
          <h3 className={effectiveMode === 'instructor' ? 'chat-header-h3--center' : ''}>
            <Icon name={effectiveMode === 'instructor' ? 'cap'
              : effectiveMode === 'test' ? 'clipboard'
              : effectiveMode === 'cycle' ? 'book' : 'chat'} />
            {effectiveMode === 'instructor' ? 'Instructor Tools'
              : effectiveMode === 'test' ? 'Test Mode'
              : effectiveMode === 'cycle' ? 'Cycle Mode'
              : 'Ask Questions'}
          </h3>
          <div className="chat-header-actions">
            {/* During an active test the student is hard-locked in: Reset/Home are
                hidden and the only way out is "Exit test" (which ends the attempt). */}
            {mode === 'student' && !showStudentHome && !activeTestLock && (
              <button
                type="button"
                className="chat-history-btn"
                onClick={() => setShowStudentHome(true)}
                disabled={loading}
                title="Back to study options"
              >
                Home
              </button>
            )}
            {/* After a test is finished, View results replaces Reset (Reset is
                hidden in test mode below). */}
            {mode === 'student' && !showStudentHome && !activeTestLock && effectiveMode === 'test' && testFinished && (
              <button
                type="button"
                className="chat-reset-btn"
                onClick={() => setTestComplete(prev => ({ ...prev, open: true }))}
                disabled={loading}
                title="View your results"
              >
                🏆 View results
              </button>
            )}
            {/* Reset returns the mode to its setup screen (Q&A only). */}
            {mode === 'student' && !showStudentHome && !activeTestLock && effectiveMode !== 'test' && effectiveMode !== 'cycle' && (messages.length > 0 || cycleQuestions) && (
              <button
                type="button"
                className="chat-reset-btn"
                onClick={handleReset}
                disabled={loading}
                title="Reset current mode to its initial state"
              >
                ↺ Reset
              </button>
            )}
            {false && (
              <span />
            )}
            {mode === 'student' && activeTestLock && (
              <button
                type="button"
                className="chat-reset-btn"
                onClick={handleExitTest}
                disabled={loading}
                title="End this test attempt and leave"
              >
                🚪 Exit test
              </button>
            )}
            {mode === 'instructor' && !(currentPlan && !planApproved && currentPlan?.plan_type === 'testing') && (
              <>
                <button
                  type="button"
                  className="chat-history-btn"
                  onClick={() => {
                    setShowModeHome(null);
                    setShowInstructorHome(true);
                    setCurrentPlan(null);
                    setPlanApproved(false);
                    setPlanApprovalNotes('');
                    setMessages([]);
                    setInstructorSetupSubmitted(false);
                    clearAuthoring();
                  }}
                  title="Instructor home"
                >
                  Home
                </button>
              </>
            )}
          </div>
        </div>
        {effectiveMode !== 'instructor' && !(mode === 'student' && showStudentHome) && (
          <p>
            {paper?.session_count
              ? `About Session ${currentSession}${currentSessionInfo ? ` (${currentSessionInfo.title})` : ''}`
              : effectiveMode === 'test'
                ? 'Check your understanding with a short test'
                : effectiveMode === 'cycle'
                  ? 'Work through the paper section by section'
                  : 'Ask questions about this paper'}
          </p>
        )}
        {effectiveMode === 'test' && testQuestions && !(mode === 'student' && showStudentHome) && (() => {
          // Drive the bar off testActiveQ (the reliable 0-based current-question
          // counter) and the plan's actual question count — NOT the AI's
          // progress_bar action. With per-question threading the AI is on a fresh
          // thread each question and reports current_question: 1 every time, which
          // would peg the bar at 33%.
          const total = getPlanItems(testQuestions).length || progress.total;
          if (!total) return null;
          const displayCurrent = Math.min(testActiveQ + 1, total);
          const pct = Math.round((displayCurrent / total) * 100);
          return (
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Question {displayCurrent} of {total}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>{pct}%</span>
              </div>
              <div style={{ background: '#eee', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
                <div style={{
                  background: '#7F77DD',
                  borderRadius: '99px',
                  height: '6px',
                  width: `${pct}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            </div>
          );
        })()}
        {/* Question navigator — jump to / revisit any answered question */}
        {effectiveMode === 'test' && testQuestions && !(mode === 'student' && showStudentHome) && (() => {
          const total = getPlanItems(testQuestions).length || progress.total;
          if (!total) return null;
          const answered = Math.max(grades.length, categoricalGrades.length);
          return (
            <div className="q-navigator">
              <div className="q-nav-chips">
                {Array.from({ length: total }, (_, i) => {
                  const isAnswered = i < answered;
                  const isCurrent = i === testActiveQ;
                  const isViewing = viewingQuestion == null ? isCurrent : viewingQuestion === i;
                  const clickable = !loading && i <= testActiveQ;
                  const cls = ['q-nav-chip',
                    isViewing ? 'q-nav-chip--viewing' : '',
                    isAnswered ? 'q-nav-chip--done' : '',
                    isCurrent ? 'q-nav-chip--current' : ''].join(' ').trim();
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cls}
                      disabled={!clickable}
                      onClick={() => handleNavigateToQuestion(i)}
                      title={`Question ${i + 1}${isAnswered ? ' — answered' : isCurrent ? ' — current' : ''}`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              {viewingQuestion != null && (
                <div className="q-nav-reviewing">
                  <span>Reviewing Question {viewingQuestion + 1}</span>
                  <button type="button" className="q-nav-return" onClick={handleReturnToCurrent}>
                    ← Back to current
                  </button>
                </div>
              )}
            </div>
          );
        })()}
        {effectiveMode === 'cycle' && cycleProgress.total > 0 && (() => {
          // Progress = cycles actually completed. The current cycle counts as done
          // once its cycle_complete fired (cyclePhase 'done'); earlier cycles are
          // already done. Capped at total so finishing the last cycle reads 100%,
          // never 133% from over-advancing.
          const doneCount = Math.min(
            cycleProgress.total,
            cycleActiveC + (cyclePhase === 'done' ? 1 : 0),
          );
          const pct = Math.min(100, Math.round((doneCount / cycleProgress.total) * 100));
          return (
            <div style={{ marginTop: '8px' }}>
              {/* Progress label + bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Cycle {Math.min(cycleActiveC + 1, cycleProgress.total)} of {cycleProgress.total}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>{pct}%</span>
              </div>
              <div style={{ background: '#eee', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
                <div style={{
                  background: '#7F77DD',
                  borderRadius: '99px',
                  height: '6px',
                  width: `${pct}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              {/* Cycle pill navigator */}
              <div className="q-navigator" style={{ marginTop: '8px' }}>
                <div className="q-nav-chips">
                  {Array.from({ length: cycleProgress.total }, (_, i) => {
                    const isDone = i < cycleActiveC;
                    const isCurrent = i === cycleActiveC;
                    const isViewing = viewingCycle == null ? isCurrent : viewingCycle === i;
                    const clickable = !loading && i <= cycleActiveC;
                    const cls = ['q-nav-chip',
                      isViewing ? 'q-nav-chip--viewing' : '',
                      isDone ? 'q-nav-chip--done' : '',
                      isCurrent ? 'q-nav-chip--current' : '',
                    ].filter(Boolean).join(' ');
                    return (
                      <button
                        key={i}
                        type="button"
                        className={cls}
                        disabled={!clickable}
                        onClick={() => handleNavigateToCycle(i)}
                        title={`Cycle ${i + 1}`}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
                {viewingCycle != null && (
                  <div className="q-nav-reviewing">
                    <span>Reviewing Cycle {viewingCycle + 1}</span>
                    <button type="button" className="q-nav-return" onClick={handleReturnToCurrentCycleView}>
                      ← Back to current
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {guidedHeaderStep !== null && (
        <div className="guided-flow-stepbar">
          {renderGuidedStepBar(guidedHeaderStep, guidedHeaderSteps)}
        </div>
      )}


      {/* Instructor prompt tab */}
      {mode === 'instructor' && instructorTab === 'prompt' && !showInstructorHome && !showModeHome && (
        <div className="instructor-prompt-panel">
          <p className="instructor-prompt-hint">
            These instructions are added to every student conversation for this paper.
            Use them to set tone, focus areas, or behavioural guidelines for the AI tutor.
          </p>
          <textarea
            className="instructor-prompt-textarea"
            value={customInstructions}
            onChange={e => setCustomInstructions(e.target.value)}
            placeholder="e.g. Encourage students to connect concepts to real-world applications. Be especially patient with methodology questions. Students are second-year undergraduates with no prior ML background."
            rows={9}
          />
          <div className="instructor-prompt-actions">
            {instructorPromptSaved && <span className="instructor-prompt-saved">✓ Saved</span>}
            <button
              type="button"
              className="instructor-prompt-summarize-btn"
              onClick={handleSummarizePrompt}
              disabled={summarizingPrompt || savingInstructions}
            >
              {summarizingPrompt ? 'Summarizing…' : 'Summarize Prompt'}
            </button>
            <button
              type="button"
              className="instructor-prompt-save-btn"
              onClick={handleSaveInstructions}
              disabled={savingInstructions || summarizingPrompt}
            >
              {savingInstructions ? 'Saving…' : 'Save'}
            </button>
          </div>
          {promptSummary && (
            <div className="instructor-prompt-summary">
              <div className="instructor-prompt-summary-label">Prompt summary</div>
              {promptSummary}
            </div>
          )}
        </div>
      )}

      {/* Student home screen — Q&A / Test / Cycle picker (occupies the main
          content area in place of the chat body). */}
      {mode === 'student' && showStudentHome && (
        <div className="instructor-home">
          <p className="instructor-home-subtitle">Choose how you'd like to study <strong>{paper?.name || 'this paper'}</strong>.</p>
          <div className="instructor-home-cards">
            <div className="instructor-home-card instructor-home-card--qa">
              <div className="instructor-home-card-icon"><Icon name="chat" size={22} /></div>
              <h3>Ask Questions</h3>
              <p>Ask anything about the paper and get instant, context-aware answers as you read.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--qa"
                onClick={() => { handleToggleMode('qa'); dismissStudentHome(); }}
                disabled={loading}
              >
                Start Q&amp;A
              </button>
            </div>
            <div className="instructor-home-card instructor-home-card--test">
              <div className="instructor-home-card-icon"><Icon name="clipboard" size={22} /></div>
              <h3>Test Mode</h3>
              <p>Check your understanding — take the test your instructor assigned, or paste your own questions.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--test"
                onClick={() => { handleToggleMode('test'); dismissStudentHome(); }}
                disabled={loading}
                title={hasActiveTest ? 'Take the test your instructor assigned' : 'Paste your own test questions'}
              >
                Go to Test Mode
              </button>
            </div>
            <div className="instructor-home-card instructor-home-card--featured">
              <div className="instructor-home-card-icon"><Icon name="book" size={22} /></div>
              <h3>Cycle Mode</h3>
              <p>Work through the paper section by section with guided prompts and concept checks.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--featured"
                onClick={() => { handleToggleMode('cycle'); dismissStudentHome(); }}
                disabled={loading}
                title={hasActiveCycle ? 'Use the cycle plan your instructor assigned' : 'Paste your own cycle plan'}
              >
                Go to Cycle Mode
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persistent thread sidebar + main chat body (hidden on the student home picker) */}
      {!(mode === 'student' && showStudentHome) && (
      <div className="chat-body">
        {mode === 'student' && (
          <div className={`thread-sidebar${sidebarCollapsed ? ' thread-sidebar--collapsed' : ''}`}>
            <div className="thread-sidebar-header">
              {!sidebarCollapsed && <span className="thread-sidebar-title">Conversations</span>}
              <button
                type="button"
                className="thread-sidebar-collapse-btn"
                onClick={() => setSidebarCollapsed(c => !c)}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? '‹' : '›'}
              </button>
            </div>
            {!sidebarCollapsed && (
            <button
              type="button"
              className="thread-sidebar-new-btn"
              onClick={handleSidebarNewChat}
            >
              + New Chat
            </button>
            )}
            {!sidebarCollapsed && <div className="thread-sidebar-body">
              {/* Q&A, Cycle, Test, then threads started from a marker. */}
              {(['qa', 'cycle', 'test', 'annotation']).map(modeKey => {
                const labels = { qa: 'Free Q&A', test: 'Test', cycle: 'Cycle',
                                 annotation: 'Annotation' };
                const labelIcons = { qa: 'chat', test: 'clipboard', cycle: 'book',
                                     annotation: 'question' };
                const modeChatList = allModeChats[modeKey] || [];
                const isOpen = sidebarSectionOpen[modeKey];

                // Instructor-assigned items: one per active test/cycle plan
                const instructorTestItems = modeKey === 'test' && activeTests.length > 0
                  ? activeTests.map(t => ({
                      plan_id: t.plan_id,
                      label: t.name || 'Instructor Test',
                      badge: t.plan?.questions?.length ? `${t.plan.questions.length}q` : null,
                      due: t.due_date
                        ? new Date(t.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : null,
                    }))
                  : [];

                const instructorCycleItems = modeKey === 'cycle' && activeCycles.length > 0
                  ? activeCycles.map(c => ({
                      plan_id: c.plan_id,
                      label: c.name || 'Instructor Cycle',
                      badge: c.plan?.cycles?.length ? `${c.plan.cycles.length}c` : null,
                      due: c.due_date
                        ? new Date(c.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : null,
                    }))
                  : [];

                const isEmpty = modeChatList.length === 0 && instructorTestItems.length === 0 && instructorCycleItems.length === 0;

                // Only startable from the paper, so an empty section offers nothing.
                if (modeKey === 'annotation' && isEmpty) return null;

                const renderThreadRow = (c, ci, list) => {
                  const attemptNo = list.length - ci;
                          const key = `${modeKey}_${c.chat_index}`;
                          // Not on a setup screen: chatIndex still points at the last
                          // thread there, which would light a row you are not in.
                          const isActive = chatMode === modeKey && c.chat_index === chatIndex
                            && messages.length > 0;
                          // Opening message, matching the instructor panel.
                          const displayTitle = (modeKey === 'test' ? `Attempt ${attemptNo}` : null)
                            || threadTitles[key]
                            || (c.annotation_id && c.title)
                            || c.preview || 'New conversation';
                          const truncated = displayTitle.length > 28 ? displayTitle.slice(0, 28) + '…' : displayTitle;
                          return (
                            <div key={c.chat_index} className={`thread-item${isActive ? ' thread-item--active' : ''}`}>
                              {editingThread?.key === key ? (
                                <input
                                  className="thread-title-input"
                                  value={editingThread.value}
                                  onChange={e => setEditingThread(prev => ({ ...prev, value: e.target.value }))}
                                  onBlur={() => { saveThreadTitle(key, editingThread.value); setEditingThread(null); }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { saveThreadTitle(key, editingThread.value); setEditingThread(null); }
                                    if (e.key === 'Escape') setEditingThread(null);
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="thread-item-btn"
                                    onClick={() => handleSidebarSwitchToThread(modeKey, c.chat_index)}
                                    title="Open conversation"
                                  >
                                    <span className="thread-item-title">{truncated}</span>
                                    <span className="thread-item-date">
                                      {new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                      {c.score != null && <> · {Math.round(c.score)}%</>}
                                      {c.status === 'abandoned' && <> · unfinished</>}
                                    </span>
                                  </button>
                                  <div className="thread-item-actions">
                                    {modeKey !== 'test' && (
                                      <button
                                        type="button"
                                        className="thread-item-rename"
                                        onClick={() => setEditingThread({ key, value: displayTitle })}
                                        title="Rename"
                                      >✎</button>
                                    )}
                                    {mode === 'instructor' && (
                                      <button
                                        type="button"
                                        className="thread-item-delete"
                                        onClick={() => handleSidebarDeleteChat(modeKey, c.chat_index)}
                                        title="Delete"
                                      >×</button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                };

                return (
                  <div className="thread-sidebar-section" key={modeKey}>
                    <button
                      type="button"
                      className="thread-section-header"
                      onClick={() => setSidebarSectionOpen(prev => ({ ...prev, [modeKey]: !prev[modeKey] }))}
                    >
                      <span><Icon name={labelIcons[modeKey]} size={13} /> {labels[modeKey]}</span>
                      <span>{isOpen ? '▾' : '▸'}</span>
                    </button>
                    {isOpen && (
                      <div className="thread-section-items">
                        {/* Instructor-assigned test items (one per active test) */}
                        {/* An assignment is not a conversation: it is the container
                            its attempts belong to. */}
                        {instructorTestItems.map(item => {
                          const attempts = modeChatList.filter(c => c.plan_id === item.plan_id);
                          const onSetup = chatMode === 'test' && messages.length === 0
                            && activeInstructorTest?.plan_id === item.plan_id;
                          return (
                            <div key={item.plan_id} className={`assignment${onSetup ? ' assignment--current' : ''}`}>
                              <button
                                type="button"
                                className="assignment-head"
                                onClick={() => {
                                  const t = activeTests.find(x => x.plan_id === item.plan_id);
                                  if (t) setActiveInstructorTest({ plan_id: t.plan_id, plan: t.plan, due_date: t.due_date || null, name: t.name || '' });
                                  handleSidebarSwitchToThread('test', null);
                                }}
                                title="Open this test"
                              >
                                <span className="assignment-title">{item.label}</span>
                                <span className="assignment-meta">
                                  {item.badge}
                                  {item.due && <> · due {item.due}</>}
                                </span>
                              </button>
                              {attempts.length > 0 && (
                                <div className="assignment-attempts">
                                  {attempts.map((c, ci) => renderThreadRow(c, ci, attempts))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {/* Instructor-assigned cycle items (one per active cycle plan) */}
                        {instructorCycleItems.map(item => (
                          <div key={item.plan_id} className={`thread-item thread-item--instructor${chatMode === 'cycle' && messages.length === 0 && activeCyclePlan?.plan_id === item.plan_id ? ' thread-item--active' : ''}`}>
                            <button
                              type="button"
                              className="thread-item-btn"
                              onClick={() => {
                                const c = activeCycles.find(x => x.plan_id === item.plan_id);
                                if (c) setActiveCyclePlan({ plan_id: c.plan_id, plan: c.plan, due_date: c.due_date || null, name: c.name || '' });
                                handleSidebarSwitchToThread('cycle', null);
                              }}
                              title="Open instructor-assigned cycle"
                            >
                              <span className="thread-item-title">
                                {item.label}
                                {item.badge && <span className="thread-item-badge">{item.badge}</span>}
                              </span>
                              {item.due && <span className="thread-item-date">Due {item.due}</span>}
                            </button>
                          </div>
                        ))}
                        {/* User conversation threads */}
                        {isEmpty ? (
                          <div className="thread-section-empty">None yet</div>
                        ) : modeChatList
                            .filter(c => !(modeKey === 'test' && c.plan_id))
                            .map((c, ci, list) => renderThreadRow(c, ci, list))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}
          </div>
        )}

        {/* Main chat content column */}
        <div className="chat-main">

      {/* Right-click context menu for chat deletion */}
      {chatCtxMenu && mode === 'instructor' && (
        <>
          <div className="chat-ctx-backdrop" onClick={() => setChatCtxMenu(null)} />
          <div className="chat-ctx-menu" style={{ top: chatCtxMenu.y, left: chatCtxMenu.x }}>
            <button className="chat-ctx-item chat-ctx-item--danger" onClick={() => handleDeleteChat(chatCtxMenu.chat_index)}>
              Delete conversation
            </button>
          </div>
        </>
      )}


      {/* Test mode setup screen */}
      {/* Setup only when nothing is loaded: with a past attempt open, the
          transcript is what the student asked to see. */}
      {effectiveMode === 'test' && !testQuestions && !showStudentHome && messages.length === 0 && (
        <div className="test-setup test-setup--test">
          {loadingActiveTest && (
            <p className="test-setup-loading">Checking for an instructor test…</p>
          )}

          {!loadingActiveTest && (
            <>
              {activeInstructorTest && (() => {
                const qs = activeInstructorTest.plan?.questions || [];
                const total = qs.length;
                const counts = qs.reduce((acc, q) => {
                  const t = (q.type || '').toLowerCase().replace(/\s+/g, '-');
                  acc[t] = (acc[t] || 0) + 1;
                  return acc;
                }, {});
                const estMin = Math.max(5, total * 2);
                const due = activeInstructorTest.due_date;
                return (
                  <div className="setup-cover setup-cover--test">
                    <div className="setup-cover-hero">
                      <span className="setup-cover-kicker">Instructor Test</span>
                      <h2 className="setup-cover-title">{paper?.name || 'Untitled paper'}</h2>
                    </div>
                    <div className="setup-cover-stats">
                      <div className="setup-stat">
                        <span className="setup-stat-num">{total}</span>
                        <span className="setup-stat-label">Question{total !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="setup-stat">
                        <span className="setup-stat-num">~{estMin}</span>
                        <span className="setup-stat-label">Minutes</span>
                      </div>
                      {due && (
                        <div className="setup-stat">
                          <span className="setup-stat-num">{new Date(due + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                          <span className="setup-stat-label">Due</span>
                        </div>
                      )}
                    </div>
                    {total > 0 && (counts['mcq'] || counts['short-answer'] || counts['counterfactual']) && (
                      <div className="setup-cover-section">
                        <div className="setup-cover-section-title">What's on it</div>
                        <ul className="setup-breakdown">
                          {counts['mcq'] > 0 && <li><span className="setup-dot setup-dot--mcq" />{counts['mcq']} Multiple choice</li>}
                          {counts['short-answer'] > 0 && <li><span className="setup-dot setup-dot--sa" />{counts['short-answer']} Short answer</li>}
                          {counts['counterfactual'] > 0 && <li><span className="setup-dot setup-dot--cf" />{counts['counterfactual']} Counterfactual</li>}
                        </ul>
                      </div>
                    )}
                    <div className="setup-cover-note">
                      💡 You'll answer one question at a time. Stuck? Ask for a hint or to be pointed to the relevant text.
                    </div>
                  </div>
                );
              })()}

              {!activeInstructorTest && (
                <div className="test-source-panel test-source-panel--empty">
                  <p className="test-setup-empty">Your instructor hasn't published a test for this paper yet.</p>
                </div>
              )}

              <button
                type="button"
                className="test-begin-btn"
                onClick={handleBeginTest}
                disabled={loading || !activeInstructorTest}
              >
                {loading ? 'Starting…' : 'Begin test →'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Cycle mode setup screen */}
      {effectiveMode === 'cycle' && !cycleQuestions && !showStudentHome && (
        <div className="test-setup test-setup--cycle">
          {loadingActiveCycle && (
            <p className="test-setup-loading">Checking for an instructor cycle plan…</p>
          )}

          {!loadingActiveCycle && (
            <>
              {activeCyclePlan && (() => {
                const cycles = activeCyclePlan.plan?.cycles || [];
                const total = cycles.length;
                const due = activeCyclePlan.due_date;
                return (
                  <div className="setup-cover setup-cover--cycle">
                    <div className="setup-cover-hero">
                      <span className="setup-cover-kicker">Instructor Cycle Plan</span>
                      <h2 className="setup-cover-title">{paper?.name || 'Untitled paper'}</h2>
                    </div>
                    <div className="setup-cover-stats">
                      <div className="setup-stat">
                        <span className="setup-stat-num">{total}</span>
                        <span className="setup-stat-label">Cycle{total !== 1 ? 's' : ''}</span>
                      </div>
                      {due && (
                        <div className="setup-stat">
                          <span className="setup-stat-num">{new Date(due + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                          <span className="setup-stat-label">Due</span>
                        </div>
                      )}
                    </div>
                    {total > 0 && (
                      <div className="setup-cover-section setup-cover-section--grow">
                        <div className="setup-cover-section-title">Your reading journey</div>
                        <ol className={`setup-journey setup-journey--${total <= 5 ? 'roomy' : total <= 9 ? 'cozy' : 'dense'}`}>
                          {cycles.map((c, i) => (
                            <li key={i} className="setup-journey-step">
                              <span className="setup-journey-num">{c.number ?? i + 1}</span>
                              <span className="setup-journey-body">
                                <span className="setup-journey-title">{c.title || `Cycle ${i + 1}`}</span>
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    <div className="setup-cover-note setup-cover-note--cycle">
                      📖 Work through one cycle at a time. The AI guides you with prompts and checks your understanding before you move on.
                    </div>
                  </div>
                );
              })()}

              {!activeCyclePlan && (
                <div className="test-source-panel test-source-panel--empty">
                  <p className="test-setup-empty">Your instructor hasn't published a cycle plan for this paper yet.</p>
                </div>
              )}

              <button
                type="button"
                className="test-begin-btn"
                onClick={handleBeginCycle}
                disabled={loading || !activeCyclePlan}
              >
                {loading ? 'Starting…' : 'Begin cycle →'}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Test plan: Edit screen ─────────────────────────────────────────── */}
      {mode === 'instructor' && currentPlan && currentPlan.plan_type === 'testing' && !planApproved && !showAnalytics && planPreviewScreen === 'edit' && (
        <div className="plan-edit-screen">
          <div className="guided-goals-topbar">
            <button
              type="button"
              className="guided-structure-back-btn"
              onClick={() => {
                setCurrentPlan(null);
                setShowModeHome(guidedTargetMode || 'test');
                handleInstructorModeToggle(null);
              }}
            >
              ← Back
            </button>
            <button
              type="button"
              className="guided-structure-continue-btn"
              onClick={() => setPlanPreviewScreen('final')}
            >
              Review & Export →
            </button>
          </div>
          <LessonPlanPreview
            plan={currentPlan}
            paperName={paper?.name}
            hideApproval
            hideExport
            onEditSection={handleEditSection}
            onRegenerateSection={handleRegenerateSection}
            onRegenerateQuestion={handleRegenerateQuestion}
            onReorderSection={handleReorderSection}
            onRemoveSection={handleRemoveSection}
            regenIndex={regenIndex}
            regenQuestionKey={regenQuestionKey}
            loading={loading}
            selectedText={selectedPdfText}
            selectedPage={selectedPdfPage}
          />
          {planAiReply && (
            <div className="plan-ai-reply">
              <div className="plan-ai-reply-label">AI Response</div>
              <div className="plan-ai-reply-text">{planAiReply}</div>
            </div>
          )}
          <div className="plan-edit-ai-bar">
            <textarea
              className="plan-edit-ai-input"
              placeholder="Ask the AI to make changes… e.g. 'Make question 3 harder'"
              value={planApprovalNotes}
              onChange={e => setPlanApprovalNotes(e.target.value)}
              rows={3}
              disabled={loading}
            />
            <button
              type="button"
              className="plan-edit-ai-btn"
              onClick={handleRequestPlanChanges}
              disabled={loading || !planApprovalNotes.trim()}
            >
              {loading ? 'Working…' : 'Request Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Test plan: Final screen ────────────────────────────────────────── */}
      {mode === 'instructor' && currentPlan && currentPlan.plan_type === 'testing' && !planApproved && !showAnalytics && planPreviewScreen === 'final' && (
        <div className="plan-final-screen">
          <div className="guided-goals-topbar">
            <button
              type="button"
              className="guided-structure-back-btn"
              onClick={() => setPlanPreviewScreen('edit')}
            >
              ← Back to Edit
            </button>
            <button
              type="button"
              className="guided-structure-continue-btn"
              onClick={handleApprovePlan}
              disabled={loading || planSaved || !!(planDueDate && planDueDate < new Date().toISOString().slice(0, 10))}
            >
              {loading ? 'Saving…' : planSaved ? '✓ Saved' : '✓ Approve & Save'}
            </button>
          </div>
          <div className="plan-final-meta">
            <div className="plan-final-field">
              <label className="plan-final-label">Plan name</label>
              <input
                type="text"
                className="plan-name-input"
                placeholder="Give this plan a name (optional)…"
                value={planName}
                onChange={e => setPlanName(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="plan-final-field">
              <label className="plan-final-label">Due date <span className="plan-final-optional">(optional)</span></label>
              <input
                type="date"
                className="plan-due-date-input"
                value={planDueDate || ''}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => setPlanDueDate(e.target.value)}
                disabled={loading}
              />
              {planDueDate && planDueDate < new Date().toISOString().slice(0, 10) && (
                <span className="plan-due-date-error">Due date cannot be in the past</span>
              )}
            </div>
            <div className="plan-final-field">
              <label className="plan-final-label">Export</label>
              <div className="plan-final-export-btns">
                <button className="plan-export-btn" onClick={() => exportPdf(currentPlan, paper?.name)}>📄 PDF</button>
                <button className="plan-export-btn" onClick={() => exportWord(currentPlan, paper?.name)}>📝 Word</button>
                <button className="plan-export-btn" onClick={() => exportJson(currentPlan, paper?.name)}>&#123;&#125; JSON</button>
              </div>
            </div>
            {(() => {
              const qs = currentPlan.questions || [];
              const cfg = currentPlan.config || {};
              const mcqN = qs.filter(q => q.type === 'mcq').length;
              const saN  = qs.filter(q => q.type === 'short-answer').length;
              const cfN  = qs.filter(q => q.type === 'counterfactual').length;
              const hintLabel = { exam: 'Exam', helpful: 'Helpful', guided: 'Guided', relaxed: 'Relaxed' };
              const diffLabel = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
              const stats = [
                { val: qs.length, key: 'Questions' },
                mcqN  > 0 && { val: mcqN,  key: 'MCQ' },
                saN   > 0 && { val: saN,   key: 'Short Answer' },
                cfN   > 0 && { val: cfN,   key: 'Counterfactual' },
                cfg.difficulty && { val: diffLabel[cfg.difficulty] || cfg.difficulty, key: 'Difficulty' },
                currentPlan.hint_style && { val: hintLabel[currentPlan.hint_style] || currentPlan.hint_style, key: 'AI Format' },
                { val: currentPlan.show_confidence !== false ? 'On' : 'Off', key: 'Confidence' },
              ].filter(Boolean);
              return (
                <div className="plan-final-field">
                  <label className="plan-final-label">Test Settings</label>
                  <div className="plan-final-summary">
                    {stats.map(({ val, key }) => (
                      <div key={key} className="plan-summary-stat">
                        <span className="plan-summary-val">{val}</span>
                        <span className="plan-summary-key">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="plan-final-field">
              <div className="retake-policy-header">
                <InfoTooltip text="Controls whether students can retake this test, and whether they receive the same questions or a new set from the question bank." />
                <span className="retake-policy-title">Retakes</span>
              </div>
              <div className="retake-policy-options">
                {(currentPlan.is_bank ? [
                  { val: 'none', label: 'No retakes' },
                  { val: 'same', label: 'Same questions' },
                  { val: 'new', label: 'All new questions' },
                  { val: 'random', label: 'Random questions' },
                ] : [
                  { val: 'none', label: 'No retakes' },
                  { val: 'same', label: 'Retakes allowed' },
                ]).map(({ val, label }) => {
                  const bankTakes = currentPlan.is_bank && currentPlan.bank_size && currentPlan.sample_size
                    ? Math.floor(currentPlan.bank_size / currentPlan.sample_size)
                    : null;
                  const isDisabled = val === 'new' && bankTakes !== null && bankTakes < 2;
                  return (
                    <label key={val} className={`retake-policy-option${isDisabled ? ' retake-policy-option--disabled' : ''}`}>
                      <input
                        type="radio"
                        name="retake-policy-final"
                        value={val}
                        checked={(currentPlan.retake_policy || 'none') === val}
                        onChange={() => { if (!isDisabled) setCurrentPlan(prev => ({ ...prev, retake_policy: val })); }}
                        disabled={isDisabled}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              {currentPlan.is_bank && currentPlan.retake_policy === 'new' &&
               currentPlan.bank_size && currentPlan.sample_size &&
               Math.floor(currentPlan.bank_size / currentPlan.sample_size) >= 2 && (
                <span className="retake-new-hint">
                  Students can take this test up to {Math.floor(currentPlan.bank_size / currentPlan.sample_size)}× total
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Cycle plan finalize screen ──────────────────────────────────── */}
      {mode === 'instructor' && currentPlan && currentPlan.plan_type !== 'testing' && !planApproved && !showAnalytics && (
        <div className="plan-final-screen plan-final-screen--cycles">
          <div className="guided-goals-topbar">
            <button
              type="button"
              className="guided-structure-back-btn"
              onClick={() => {
                setCurrentPlan(null);
                setAuthorPhase('detailing');
                setAuthorFinalized(prev => prev.slice(0, -1));
              }}
            >
              ← Back to Edit
            </button>
            <button
              type="button"
              className="guided-structure-continue-btn"
              onClick={handleApprovePlan}
              disabled={loading || planSaved || !!(planDueDate && planDueDate < new Date().toISOString().slice(0, 10))}
            >
              {loading ? 'Saving…' : planSaved ? '✓ Saved' : '✓ Approve & Save'}
            </button>
          </div>
          <div className="plan-final-meta">
            <div className="plan-final-field">
              <label className="plan-final-label">Plan name</label>
              <input
                type="text"
                className="plan-name-input"
                placeholder="Give this plan a name (optional)…"
                value={planName}
                onChange={e => setPlanName(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="plan-final-field">
              <label className="plan-final-label">Due date <span className="plan-final-optional">(optional)</span></label>
              <input
                type="date"
                className="plan-due-date-input"
                value={planDueDate || ''}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => setPlanDueDate(e.target.value)}
                disabled={loading}
              />
              {planDueDate && planDueDate < new Date().toISOString().slice(0, 10) && (
                <span className="plan-due-date-error">Due date cannot be in the past</span>
              )}
            </div>
            <div className="plan-final-field">
              <label className="plan-final-label">Export</label>
              <div className="plan-final-export-btns">
                <button className="plan-export-btn" onClick={() => exportPdf(currentPlan, paper?.name)}>📄 PDF</button>
                <button className="plan-export-btn" onClick={() => exportWord(currentPlan, paper?.name)}>📝 Word</button>
                <button className="plan-export-btn" onClick={() => exportJson(currentPlan, paper?.name)}>&#123;&#125; JSON</button>
              </div>
            </div>
            {(() => {
              const cycles = currentPlan.cycles || [];
              const totalQ = cycles.reduce((acc, c) => acc + (c.questions?.length || 0), 0);
              const cfg = currentPlan.config || {};
              const hintLabel = { minimal: 'Minimal', moderate: 'Moderate', 'heavily-involved': 'Involved' };
              const stats = [
                { val: cycles.length, key: 'Cycles' },
                { val: totalQ, key: 'Questions' },
                cfg.hints   && { val: hintLabel[cfg.hints]   || cfg.hints,                          key: 'AI Help' },
                cfg.accuracy && { val: cfg.accuracy === 'in-depth' ? 'Detailed' : 'Brief',           key: 'Answers' },
              ].filter(Boolean);
              return (
                <div className="plan-final-field">
                  <label className="plan-final-label">Cycle Settings</label>
                  <div className="plan-final-summary">
                    {stats.map(({ val, key }) => (
                      <div key={key} className="plan-summary-stat">
                        <span className="plan-summary-val">{val}</span>
                        <span className="plan-summary-key">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="plan-final-cycles">
            {(currentPlan.cycles || []).map((cycle, i) => (
              <CycleCard
                key={i}
                cycle={cycle}
                index={i}
                defaultOpen={false}
                readOnly={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Plan approved confirmation */}
      {mode === 'instructor' && planApproved && !showAnalytics && (
        <div className="plan-approved-banner">
          <span>✓ Plan saved successfully.</span>
        </div>
      )}

      {/* Instructor home screen */}
      {mode === 'instructor' && showInstructorHome && !currentPlan && !showAnalytics && (
        <div className="instructor-home">
          {cycleEnabled || testEnabled ? (
            <p className="instructor-home-subtitle">
              Tools active for <strong>{paper?.name || 'this paper'}</strong>
              {cycleEnabled && testEnabled ? ' — Cycle Mode · Test Mode' : cycleEnabled ? ' — Cycle Mode' : ' — Test Mode'}.
            </p>
          ) : (
            <p className="instructor-home-subtitle">Choose a tool to get started for <strong>{paper?.name || 'this paper'}</strong>.</p>
          )}
          <div className="instructor-home-cards">
            <div className={`instructor-home-card instructor-home-card--featured${cycleEnabled ? ' instructor-home-card--active' : ''}`}>
              <div className="instructor-home-card-icon"><Icon name="book" size={22} /></div>
              <h3>Cycle Mode {cycleEnabled && <span className="instructor-home-active-badge">active</span>}</h3>
              <p>Guide students through the paper section by section — core ideas, discussion prompts, and an optional concept diagram.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--featured"
                onClick={() => { setShowModeHome('cycle'); setShowInstructorHome(false); }}
                disabled={loading}
              >
                {cycleEnabled ? 'Manage cycle' : 'Go to Cycle Mode'}
              </button>
            </div>
            <div className={`instructor-home-card instructor-home-card--test${testEnabled ? ' instructor-home-card--active' : ''}`}>
              <div className="instructor-home-card-icon"><Icon name="clipboard" size={22} /></div>
              <h3>Test Mode {testEnabled && <span className="instructor-home-active-badge">active</span>}</h3>
              <p>Generate multiple choice, short answer, and counterfactual questions to review and approve.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--test"
                onClick={() => { setShowModeHome('test'); setShowInstructorHome(false); }}
                disabled={loading}
              >
                {testEnabled ? 'Manage test' : 'Go to Test Mode'}
              </button>
            </div>
            <div className="instructor-home-card instructor-home-card--guidelines">
              <div className="instructor-home-card-icon"><Icon name="sparkle" size={22} /></div>
              <h3>AI Guidelines</h3>
              <p>Customize the AI tutor's tone, focus areas, and behavioral constraints for this paper.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--guidelines"
                onClick={() => { setInstructorTab('prompt'); setShowInstructorHome(false); }}
                disabled={loading}
              >
                Open AI Guidelines
              </button>
            </div>
            <div className="instructor-home-card instructor-home-card--assistant">
              <div className="instructor-home-card-icon">✦</div>
              <h3>Analytics Assistant</h3>
              <p>Ask plain-English questions about student performance, test scores, confusion topics, and engagement — answered with real class data.</p>
              <button
                className="instructor-home-card-btn instructor-home-card-btn--assistant"
                onClick={() => { setShowAnalyticsAssistant(true); setShowInstructorHome(false); }}
                disabled={loading}
              >
                Open Analytics Assistant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mode home screen (cycle / test) */}
      {mode === 'instructor' && showModeHome && !currentPlan && !showAnalytics && (
        <ModeHomeScreen
          mode={showModeHome}
          paper={paper}
          user={user}
          onBack={() => { setShowModeHome(null); setShowInstructorHome(true); }}
          onCreateNew={() => startGuidedForMode(showModeHome)}
          onPlanActivated={(planType) => {
            if (planType === 'cycles') {
              setCycleEnabled(true);
              if (paper) saveModeSettings(paper.id, { cycle_enabled: true }).catch(() => {});
            }
            if (planType === 'testing') {
              setTestEnabled(true);
              if (paper) saveModeSettings(paper.id, { test_enabled: true }).catch(() => {});
            }
          }}
        />
      )}

      {/* Cycle authoring — overview progress rail + the cycle currently being detailed */}
      {mode === 'instructor' && authorPhase === 'detailing' && authorOverview && !showAnalytics && (
        <div className="cycle-author-panel">
          <div className="cycle-author-topbar">
            {authorReviewingIndex !== null ? (
              <>
                <div className="cycle-author-topbar-left">
                  {authorReviewingIndex > 0 && (
                    <button
                      type="button"
                      className="cycle-author-back-btn"
                      onClick={() => handleRevisitCycle(authorReviewingIndex - 1)}
                      disabled={loading}
                    >
                      ← Cycle {authorReviewingIndex}
                    </button>
                  )}
                </div>
                <span className="cycle-author-topbar-label cycle-author-topbar-label--revisit">
                  Editing Cycle {authorReviewingIndex + 1} (completed)
                </span>
                <div className="cycle-author-topbar-right">
                  <button
                    type="button"
                    className="cycle-author-finalize-btn"
                    onClick={handleReturnToCurrentCycle}
                    disabled={loading}
                  >
                    Back to Cycle {authorCycleIndex + 1} →
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="cycle-author-topbar-left">
                  {authorCycleIndex > 0 && (
                    <button
                      type="button"
                      className="cycle-author-back-btn"
                      onClick={() => handleRevisitCycle(authorCycleIndex - 1)}
                      disabled={loading}
                    >
                      ← Cycle {authorCycleIndex}
                    </button>
                  )}
                </div>
                <span className="cycle-author-topbar-label">
                  Cycle {authorCycleIndex + 1} of {authorOverview.cycles.length}
                </span>
                <div className="cycle-author-topbar-right">
                  <button
                    type="button"
                    className="cycle-author-finalize-btn"
                    onClick={handleFinalizeCycle}
                    disabled={loading || !authorDraftCycle}
                  >
                    {authorCycleIndex + 1 >= authorOverview.cycles.length
                      ? '✓ Finalize & Review Plan'
                      : 'Finalize cycle →'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className={`cycle-author-rail${railOpen ? ' cycle-author-rail--open' : ''}`}>
            <button
              type="button"
              className="cycle-author-rail-toggle"
              onClick={() => setRailOpen(o => !o)}
            >
              <span className="cycle-author-rail-toggle-label">
                {railOpen ? 'All Cycles' : 'Cycles'}
              </span>
              <span className="cycle-author-rail-chevron">{railOpen ? '▲' : '▼'}</span>
            </button>
            {railOpen ? (
              <ol className="cycle-author-steps">
                {authorOverview.cycles.map((c, i) => {
                  const isDone = i < authorCycleIndex;
                  const isCurrent = i === authorCycleIndex;
                  const isRevisiting = authorReviewingIndex === i;
                  const state = isRevisiting ? 'revisiting' : isDone ? 'done' : isCurrent ? 'current' : 'upcoming';
                  const isReady = !isDone && !isCurrent && !!authorPregeneratedRef.current[i];
                  return (
                    <li
                      key={i}
                      className={`cycle-author-step cycle-author-step--${state}${isReady ? ' cycle-author-step--ready' : ''}`}
                      onClick={isDone && !loading ? () => handleRevisitCycle(i) : undefined}
                      style={isDone && !loading ? { cursor: 'pointer' } : undefined}
                      title={isDone && !loading ? `Click to review and edit Cycle ${i + 1}` : undefined}
                    >
                      <span className="cycle-author-step-marker">{isDone || isRevisiting ? '✓' : c.number ?? i + 1}</span>
                      <span className="cycle-author-step-body">
                        <span className="cycle-author-step-title">{c.title || `Cycle ${i + 1}`}</span>
                        {c.read_sections && <span className="cycle-author-step-reads">{c.read_sections}</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : (() => {
              const activeIdx = authorReviewingIndex !== null ? authorReviewingIndex : authorCycleIndex;
              const c = authorOverview.cycles[activeIdx];
              if (!c) return null;
              const isDone = activeIdx < authorCycleIndex;
              const isCurrent = activeIdx === authorCycleIndex;
              const isRevisiting = authorReviewingIndex === activeIdx;
              const state = isRevisiting ? 'revisiting' : isDone ? 'done' : isCurrent ? 'current' : 'upcoming';
              return (
                <ol className="cycle-author-steps">
                  <li className={`cycle-author-step cycle-author-step--${state}`}>
                    <span className="cycle-author-step-marker">{isDone || isRevisiting ? '✓' : c.number ?? activeIdx + 1}</span>
                    <span className="cycle-author-step-body">
                      <span className="cycle-author-step-title">{c.title || `Cycle ${activeIdx + 1}`}</span>
                      {c.read_sections && <span className="cycle-author-step-reads">{fixPageRanges(c.read_sections)}</span>}
                    </span>
                  </li>
                </ol>
              );
            })()}
          </div>

          <div className="cycle-author-draft">
            {authorReviewingIndex !== null ? (
              <CycleCard
                cycle={authorFinalized[authorReviewingIndex]}
                index={0}
                alwaysOpen={true}
                onEdit={handleAuthorEdit}
                onRegenerate={handleAuthorRegenerate}
                onRegenerateSection={handleAuthorRegenerateSection}
                regenLoading={authorRegenLoading}
                readOnly={false}
                selectedText={selectedPdfText}
                selectedPage={selectedPdfPage}
                onRegenerateQuestion={handleAuthorRegenerateQuestion}
                onAddQuestion={handleAuthorAddQuestion}
                regenQuestionIndex={authorRegenQuestionIndex}
              />
            ) : authorDraftCycle ? (
              <CycleCard
                cycle={authorDraftCycle}
                index={0}
                alwaysOpen={true}
                onEdit={handleAuthorEdit}
                onRegenerate={handleAuthorRegenerate}
                onRegenerateSection={handleAuthorRegenerateSection}
                regenLoading={authorRegenLoading}
                readOnly={false}
                selectedText={selectedPdfText}
                selectedPage={selectedPdfPage}
                onRegenerateQuestion={handleAuthorRegenerateQuestion}
                onAddQuestion={handleAuthorAddQuestion}
                regenQuestionIndex={authorRegenQuestionIndex}
              />
            ) : (
              <div className="cycle-author-empty">Generating this cycle…</div>
            )}

            {/* Upcoming cycles: the next few are pre-generated in the background, so
                show them here (read-only preview) instead of one-cycle-at-a-time.
                They become the editable draft as the instructor finalizes forward. */}
            {authorReviewingIndex === null && authorDraftCycle && (() => {
              const total = authorOverview?.cycles?.length || 0;
              const from = authorCycleIndex + 1;
              const to = Math.min(total, from + PREGEN_LOOKAHEAD);
              if (to <= from) return null;
              const indices = [];
              for (let i = from; i < to; i++) indices.push(i);
              return (
                <div className="cycle-author-upcoming" data-v={pregenVersion}>
                  <div className="cycle-author-upcoming-label">
                    Upcoming cycles · preview ({indices.length})
                  </div>
                  {indices.map(i => {
                    const pg = authorPregeneratedRef.current[i];
                    if (pg?.cycle) {
                      return (
                        <CycleCard
                          key={i}
                          cycle={pg.cycle}
                          index={i}
                          defaultOpen={false}
                          readOnly={true}
                          selectedText={selectedPdfText}
                          selectedPage={selectedPdfPage}
                        />
                      );
                    }
                    const skel = authorOverview?.cycles?.[i];
                    return (
                      <div key={i} className="cycle-author-upcoming-pending">
                        <span className="cycle-number">{i + 1}</span>
                        <span className="cycle-title">{skel?.title || `Cycle ${i + 1}`}</span>
                        <span className="cycle-author-pending-note">⟳ Generating…</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

        </div>
      )}

      {/* Messages */}
      {/* Paper structure review step (cycle guided setup) */}
      {mode === 'instructor' && !showInstructorHome && !showModeHome && instructorSubMode === 'guided' && guidedStep === 'structure' && (
        <div className="guided-structure-page">
          {/* Top nav */}
          <div className="guided-goals-topbar">
            <button
              type="button"
              className="guided-structure-back-btn"
              onClick={() => setGuidedStep(null)}
            >
              ← Back
            </button>
            <button
              type="button"
              className="guided-structure-continue-btn"
              onClick={handleContinueToSetup}
              disabled={guidedStructureSections.length === 0 || regeneratingSections || structureAiLoading || guidedPresetsLoading}
            >
              {guidedPresetsLoading ? 'Generating…' : 'Generate →'}
            </button>
          </div>
          {regeneratingSections ? (
            <p className="sections-popup-regen-msg">A new structure is being generated — it will be ready in a few moments.</p>
          ) : (
            <>
              <div className="guided-structure-scroll">
              <div className="guided-structure-intro">
                <p>Each section below will become one cycle. Adjust the breakdown until it matches how you want students to progress through the paper.</p>
              </div>
              {/* Answers + AI Help */}
              <div className="guided-structure-dropdowns">
                <div className="guided-structure-dropdown-with-tooltip">
                  <InfoTooltip text="How thorough student responses need to be. Brief = a short correct answer is fine. Detailed = students must explain their reasoning in depth." />
                  <label className="guided-structure-dropdown-item">
                    <span className="guided-structure-dropdown-label">Answers</span>
                    <select
                      className="guided-structure-dropdown-select"
                      value={instructorSetupAccuracy || 'in-depth'}
                      onChange={e => setInstructorSetupAccuracy(e.target.value)}
                    >
                      <option value="minimal">Brief</option>
                      <option value="in-depth">Detailed</option>
                    </select>
                  </label>
                </div>
                <div className="guided-structure-dropdown-with-tooltip">
                  <InfoTooltip text="How actively the AI tutor assists students. Minimal = brief nudges only. Moderate = guided hints and clarifying explanations. Heavily Involved = detailed walkthroughs and worked examples." />
                  <label className="guided-structure-dropdown-item">
                    <span className="guided-structure-dropdown-label">AI Help</span>
                    <select
                      className="guided-structure-dropdown-select"
                      value={instructorSetupHints || 'moderate'}
                      onChange={e => setInstructorSetupHints(e.target.value)}
                    >
                      <option value="minimal">Minimal</option>
                      <option value="moderate">Moderate</option>
                      <option value="heavily-involved">Heavily Involved</option>
                    </select>
                  </label>
                </div>
              </div>

              {/* Section list */}
              <ol className="guided-structure-list">
                {guidedStructureSections.map((s, idx) => (
                  <li key={idx} className="guided-structure-item">
                    <div className="guided-structure-item-header">
                      <div className="guided-structure-move-btns">
                        <button
                          type="button"
                          className="guided-structure-move-btn"
                          onClick={() => moveGuidedStructureSection(idx, -1)}
                          disabled={idx === 0}
                          title="Move up"
                        >↑</button>
                        <button
                          type="button"
                          className="guided-structure-move-btn"
                          onClick={() => moveGuidedStructureSection(idx, 1)}
                          disabled={idx === guidedStructureSections.length - 1}
                          title="Move down"
                        >↓</button>
                      </div>
                      <span className="guided-structure-item-num">{idx + 1}</span>
                      <input
                        className="guided-structure-item-title"
                        value={s.title || ''}
                        onChange={e => updateGuidedStructureSection(idx, 'title', e.target.value)}
                      />
                      {(s.start_page || s.end_page) && (
                        <span className="guided-structure-item-pages">
                          {fmtPages(s.start_page, s.end_page)}
                        </span>
                      )}
                      <button
                        type="button"
                        className="guided-structure-item-remove"
                        onClick={() => removeGuidedStructureSection(idx)}
                        title="Remove this section"
                        disabled={guidedStructureSections.length <= 1}
                      >✕</button>
                    </div>
                    {s.description != null && (
                      <textarea
                        className="guided-structure-item-desc"
                        rows={2}
                        value={s.description || ''}
                        onChange={e => updateGuidedStructureSection(idx, 'description', e.target.value)}
                      />
                    )}
                    <div className="guided-structure-item-settings">
                      <button
                        type="button"
                        className={`guided-structure-settings-toggle${sectionDropdownOpen[idx] ? ' open' : ''}`}
                        onClick={() => setSectionDropdownOpen(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      >
                        ⚙ Settings {sectionDropdownOpen[idx] ? '▲' : '▼'}
                      </button>
                      {sectionDropdownOpen[idx] && (
                        <div className="guided-structure-settings-panel">
                          <div className="sections-popup-control">
                            <span className="sections-popup-control-label">Difficulty</span>
                            <div className="sections-popup-slider-row">
                              <span className="sections-popup-slider-min">Easy</span>
                              <input
                                type="range" min={0} max={10}
                                value={s.difficulty ?? 5}
                                onChange={e => updateGuidedStructureSection(idx, 'difficulty', Number(e.target.value))}
                              />
                              <span className="sections-popup-slider-max">Hard</span>
                              <span className="sections-popup-slider-val">{s.difficulty ?? 5}/10</span>
                            </div>
                          </div>
                          <div className="sections-popup-control">
                            <span className="sections-popup-control-label">Section Importance</span>
                            <div className="sections-popup-slider-row">
                              <span className="sections-popup-slider-min">Low</span>
                              <input
                                type="range" min={0} max={10}
                                value={s.importance ?? 5}
                                onChange={e => updateGuidedStructureSection(idx, 'importance', Number(e.target.value))}
                              />
                              <span className="sections-popup-slider-max">High</span>
                              <span className="sections-popup-slider-val">{s.importance ?? 5}/10</span>
                            </div>
                          </div>
                          <div className="sections-popup-control">
                            <span className="sections-popup-control-label">Comprehension Questions</span>
                            <div className="sections-popup-slider-row">
                              <span className="sections-popup-slider-min">0</span>
                              <input
                                type="range" min={0} max={5}
                                value={s.q_per_cycle ?? 1}
                                onChange={e => updateGuidedStructureSection(idx, 'q_per_cycle', Number(e.target.value))}
                              />
                              <span className="sections-popup-slider-max">5</span>
                              <span className="sections-popup-slider-val">{s.q_per_cycle ?? 1}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>

              {/* Add section + Regenerate */}
              <div className="guided-structure-bottom-row">
                <button
                  type="button"
                  className="guided-structure-add-btn"
                  onClick={handleAddGuidedStructureSection}
                >
                  + Add Section
                </button>
                <button
                  type="button"
                  className={`sections-popup-regen-btn${regeneratingSections ? ' loading' : ''}`}
                  onClick={async () => {
                    const fresh = await handleRegenerateSections();
                    setGuidedStructureSections(withSectionDefaults(fresh));
                  }}
                  disabled={regeneratingSections || structureAiLoading}
                >
                  {regeneratingSections ? 'Regenerating…' : '↺ Regenerate'}
                </button>
              </div>
              </div>{/* end guided-structure-scroll */}

              {/* AI chat for structural modifications */}
              <div className="guided-structure-ai-row">
                <input
                  type="text"
                  className="guided-structure-ai-input"
                  value={structureAiInput}
                  onChange={e => setStructureAiInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleStructureAiRequest(); } }}
                  placeholder='Ask the AI — e.g. "split section 3 in two" or "merge sections 1 and 2"'
                  disabled={structureAiLoading}
                />
                <button
                  type="button"
                  className="guided-structure-ai-send"
                  onClick={handleStructureAiRequest}
                  disabled={!structureAiInput.trim() || structureAiLoading}
                >
                  {structureAiLoading ? '…' : 'Ask'}
                </button>
              </div>
              {structureAiMessage && (
                <p className="guided-structure-ai-status">{structureAiMessage}</p>
              )}
            </>
          )}

        </div>
      )}

      {viewingPastAttempt && (
        <div className="past-attempt-bar">
          <span className="past-attempt-bar__label">Past attempt — read only</span>
          <button
            type="button"
            className="past-attempt-bar__back"
            onClick={() => setMessages([])}
          >
            Back to test
          </button>
        </div>
      )}

      {/* messages.length: a past attempt has no live questions, but its transcript
          is exactly what the student opened it to read. */}
      {(effectiveMode !== 'test' || testQuestions || messages.length > 0) && (effectiveMode !== 'cycle' || cycleQuestions || messages.length > 0) && !(mode === 'student' && showStudentHome) && !(mode === 'instructor' && instructorTab === 'prompt') && !(mode === 'instructor' && currentPlan && !planApproved) && !(mode === 'instructor' && showInstructorHome) && !(mode === 'instructor' && showModeHome) && !(mode === 'instructor' && guidedStep === 'structure') && !(mode === 'instructor' && authorPhase === 'detailing') && (
        <div className="messages-container">
          {messages.length === 0 && !loading ? (
            mode === 'instructor' && instructorSubMode === 'guided' ? (
              <div className="guided-goals-page">
                <div className="guided-goals-topbar">
                  <button
                    type="button"
                    className="guided-goals-back-btn"
                    onClick={() => {
                      setShowModeHome(guidedTargetMode);
                      handleInstructorModeToggle(null);
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="guided-goals-skip-btn"
                    onClick={() => {
                      setGuidedGoals('');
                      if (guidedTargetMode === 'test') {
                        handleInstructorModeToggle('test');
                        setInstructorTab('chat');
                        setShowInstructorHome(false);
                      } else {
                        setGuidedStructureSections(withSectionDefaults(aiGeneratedSections));
                        setGuidedStep('structure');
                      }
                    }}
                    disabled={loadingPaperSections}
                  >
                    Skip to Paper Structure →
                  </button>
                </div>
                <div className="guided-empty-state">
                <div className="guided-empty-icon">🎯</div>
                <p className="guided-empty-heading">
                  What are your goals for {guidedTargetMode === 'test' ? 'Test' : 'Cycle'} Mode?
                </p>
                <p className="guided-empty-hint">
                  Describe your learning objectives, your students' level, and anything you want to focus on or avoid.
                  The AI will customise the {guidedTargetMode === 'test' ? 'test' : 'cycle'} settings and section weights to match your vision.
                </p>
                <textarea
                  className="guided-initial-textarea"
                  value={guidedInput}
                  onChange={e => setGuidedInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (!guidedInput.trim() || guidedPresetsLoading) return;
                      if (guidedTargetMode === 'test') {
                        handleTestGoalsSubmit(guidedInput.trim());
                      } else {
                        handleCycleGoalsSubmit(guidedInput.trim());
                      }
                    }
                  }}
                  placeholder={"e.g. I want students to deeply understand the core algorithm. They're graduate level with some ML background. Don't worry too much about related work — focus on the method and evaluation."}
                  rows={6}
                  autoFocus
                />
                <button
                  type="button"
                  className="guided-initial-send-btn"
                  onClick={() => {
                    if (!guidedInput.trim() || guidedPresetsLoading) return;
                    if (guidedTargetMode === 'test') {
                      handleTestGoalsSubmit(guidedInput.trim());
                    } else {
                      handleCycleGoalsSubmit(guidedInput.trim());
                    }
                  }}
                  disabled={!guidedInput.trim() || loadingPaperSections || guidedPresetsLoading}
                >
                  {guidedPresetsLoading ? 'Preparing Presets…' : loadingPaperSections ? 'Loading sections…' : 'Submit Prompt'}
                </button>
                </div>
              </div>
            ) : mode === 'instructor' && !instructorSetupSubmitted && instructorSubMode !== 'cycle' ? (
              <div className="guided-presets-page">
                {/* Top nav */}
                <div className="guided-goals-topbar">
                  <button
                    type="button"
                    className="guided-structure-back-btn"
                    onClick={() => {
                      if (instructorSubMode === 'cycle') {
                        setGuidedStep('structure');
                        setInstructorSubMode('guided');
                      } else {
                        setInstructorSubMode('guided');
                      }
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="guided-structure-continue-btn"
                    disabled={
                      (instructorSubMode === 'test' && instructorSetupQTypes.length === 0) ||
                      !instructorSetupAccuracy || !instructorSetupHints || loading
                    }
                    onClick={handleInstructorSetupSubmit}
                  >
                    {loading ? 'Working…' : 'Generate →'}
                  </button>
                </div>

                {/* Intro */}
                <div className="guided-presets-intro">
                  <p>
                    {instructorSubMode === 'cycle'
                      ? 'Customise how the AI runs each reading cycle.'
                      : 'Customise how the AI runs the assessment.'}
                  </p>
                  <span className="setup-cycles-badge">
                    {instructorSubMode === 'cycle'
                      ? `${instructorSetupCycles} Cycle${instructorSetupCycles !== 1 ? 's' : ''}`
                      : `${Math.max(3, instructorSetupNumQuestions)} Question${Math.max(3, instructorSetupNumQuestions) !== 1 ? 's' : ''}`
                    }
                  </span>
                </div>

                <div className="setup-rows-grid">
                <div className="setup-row">
                  {instructorSubMode === 'cycle'
                    ? <InfoTooltip text="How actively the AI tutor assists students. Minimal = brief nudges only. Moderate = guided hints and clarifying explanations. Heavily Involved = detailed walkthroughs and worked examples." />
                    : <InfoTooltip text="Exam: no AI help, grade at end. Helpful: AI confirms right/wrong and points to relevant text. Guided: AI gives hints and guides toward the answer. Relaxed: AI awaits a genuine attempt then reveals the answer; grading is effort-based." />}
                  <span className="setup-row-label">{instructorSubMode === 'cycle' ? 'AI Help' : 'AI Format'}</span>
                  <div className="setup-toggle-group">
                    {(instructorSubMode === 'cycle'
                      ? [['minimal','Minimal'],['moderate','Moderate'],['heavily-involved','Heavily Involved']]
                      : [['exam','Exam'],['helpful','Helpful'],['guided','Guided'],['relaxed','Relaxed']]
                    ).map(([val,label]) => (
                      <button key={val} type="button"
                        className={`setup-toggle-btn${instructorSetupHints === val ? ' active' : ''}`}
                        onClick={() => setInstructorSetupHints(val)}>{label}</button>
                    ))}
                  </div>
                </div>

                {instructorSubMode === 'test' && (
                  <div className="setup-row">
                    <InfoTooltip text="Which question formats to include. MCQ = multiple choice with 4 options. Short Answer = open-ended written response. Counterfactual = 'what if' questions that test deeper reasoning." />
                    <span className="setup-row-label">Types</span>
                    <div className="setup-toggle-group">
                      {[['mcq','MCQ'],['short-answer','Short Answer'],['counterfactual','Counterfactual']].map(([val,label]) => (
                        <button key={val} type="button"
                          className={`setup-toggle-btn${instructorSetupQTypes.includes(val) ? ' active' : ''}`}
                          onClick={() => handleToggleQType(val)}>{label}</button>
                      ))}
                      {instructorSetupQTypes.length >= 2 && (
                        <button type="button" className="q-balance-toggle"
                          onClick={() => setQBalanceOpen(o => !o)}
                          title={qBalanceOpen ? 'Collapse question balance' : 'Expand question balance'}
                        ><span className="q-balance-toggle-icon">⚙</span>{qBalanceOpen ? '▴' : '▾'}</button>
                      )}
                    </div>
                  </div>
                )}

                {instructorSubMode === 'test' && instructorSetupQTypes.length >= 2 && qBalanceOpen && (
                  <div className="q-balance-panel">
                    {instructorSetupQTypes.map(type => {
                      const typeLabel = { mcq: 'MCQ', 'short-answer': 'Short Answer', counterfactual: 'Counterfactual' }[type];
                      return (
                        <div key={type} className="q-balance-row">
                          <span className="q-balance-label">{typeLabel}</span>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={qTypeWeights[type] || 0}
                            className="q-balance-slider"
                            onChange={e => handleQTypeWeightChange(type, Number(e.target.value))}
                          />
                          <span className="q-balance-pct">{qTypeWeights[type] || 0}%</span>
                        </div>
                      );
                    })}
                    <div className="q-balance-footer">
                      <button type="button" className="topic-distribution-action-btn topic-distribution-reset-btn"
                        onClick={handleResetQTypeWeights}>Reset</button>
                    </div>
                  </div>
                )}

                {!(instructorSubMode === 'cycle' && suggestedPaperSections && suggestedPaperSections.length > 0) && (
                  <div className="setup-row">
                    <InfoTooltip text={instructorSubMode === 'test'
                      ? (instructorSetupBankEnabled ? 'How many questions each student is randomly given from the bank.' : 'Total number of questions in the test.')
                      : 'How many reading sections to divide the paper into.'} />
                    <span className="setup-row-label">{instructorSubMode === 'test' ? (instructorSetupBankEnabled ? 'Per student' : 'Questions') : 'Cycles'}</span>
                    <div className="setup-row-slider">
                      {instructorSubMode === 'cycle' ? (
                        <>
                          <input type="range" min={1} max={20} value={instructorSetupCycles}
                                 onChange={e => setInstructorSetupCycles(Number(e.target.value))} />
                          <span className="setup-slider-count">{instructorSetupCycles}</span>
                        </>
                      ) : (
                        <>
                          <input type="range" min={3} max={25} value={Math.max(3, instructorSetupNumQuestions)}
                                 onChange={e => setInstructorSetupNumQuestions(Number(e.target.value))} />
                          <span className="setup-slider-count">{Math.max(3, instructorSetupNumQuestions)}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {instructorSubMode === 'test' && (
                  <div className="setup-row">
                    <InfoTooltip text="Generate a large pool of questions, then give each student a different random subset (drawn from the pool). Reduces copying and lets students retry with fresh questions." />
                    <span className="setup-row-label">Question Bank</span>
                    <div className="setup-toggle-group">
                      {[['on', 'On'], ['off', 'Off']].map(([val, label]) => (
                        <button key={val} type="button"
                          className={`setup-toggle-btn${(val === 'on') === instructorSetupBankEnabled ? ' active' : ''}`}
                          onClick={() => setInstructorSetupBankEnabled(val === 'on')}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}

                {instructorSubMode === 'test' && instructorSetupBankEnabled && (
                  <div className="setup-row">
                    <InfoTooltip text="Total questions generated for the pool. Students each receive a random sample of the size set above." />
                    <span className="setup-row-label">Bank size</span>
                    <div className="setup-row-slider">
                      <input type="range" min={10} max={100} value={Math.max(instructorSetupNumQuestions, instructorSetupBankSize)}
                             onChange={e => setInstructorSetupBankSize(Number(e.target.value))} />
                      <span className="setup-slider-count">{Math.max(instructorSetupNumQuestions, instructorSetupBankSize)}</span>
                    </div>
                  </div>
                )}

                {instructorSubMode === 'cycle' && (
                  <div className="setup-row">
                    <InfoTooltip text="How challenging the questions are. Easy = recall-based. Medium = requires some inference from the text. Hard = demands analysis, synthesis, or application." />
                    <span className="setup-row-label">Difficulty</span>
                    <div className="setup-toggle-group">
                      {[['easy','Easy'],['medium','Medium'],['hard','Hard']].map(([val,label]) => (
                        <button key={val} type="button"
                          className={`setup-toggle-btn${instructorSetupDifficulty === val ? ' active' : ''}`}
                          onClick={() => setInstructorSetupDifficulty(val)}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="setup-row">
                  <InfoTooltip text="How thorough student responses need to be. Brief = a short correct answer is fine. Detailed = students must explain their reasoning in depth." />
                  <span className="setup-row-label">Answers</span>
                  <div className="setup-toggle-group">
                    {[['minimal','Brief'],['in-depth','Detailed']].map(([val,label]) => (
                      <button key={val} type="button"
                        className={`setup-toggle-btn${instructorSetupAccuracy === val ? ' active' : ''}`}
                        onClick={() => setInstructorSetupAccuracy(val)}>{label}</button>
                    ))}
                  </div>
                </div>

                {instructorSubMode === 'test' && (
                  <div className="setup-row">
                    <InfoTooltip text="When shown, students rate their confidence (1–5) before submitting each answer. Helps identify guessing and builds self-awareness." />
                    <span className="setup-row-label">Confidence</span>
                    <div className="setup-toggle-group">
                      {[['on', 'Show'], ['off', 'Hide']].map(([val, label]) => (
                        <button key={val} type="button"
                          className={`setup-toggle-btn${(val === 'on') === instructorSetupConfidence ? ' active' : ''}`}
                          onClick={() => setInstructorSetupConfidence(val === 'on')}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}
                </div>

                {/* Question distribution — test mode only */}
                {instructorSubMode === 'test' && (
                  <div className="topic-distribution-section">
                    <div className="topic-distribution-header">
                      <InfoTooltip text="Set what percentage of questions should cover each topic. Sliders always sum to 100% — adjusting one redistributes the others automatically." />
                      <span className="topic-distribution-title">Question Distribution</span>
                      {testTopics.length > 0 && (
                        <>
                          <button
                            type="button"
                            className="topic-distribution-action-btn"
                            onClick={handleSuggestTopicWeights}
                            disabled={topicWeightSuggestLoading}
                            title="Have AI suggest weights based on topic importance"
                          >{topicWeightSuggestLoading ? '…' : 'AI Suggest'}</button>
                          <button
                            type="button"
                            className="topic-distribution-action-btn topic-distribution-reset-btn"
                            onClick={handleResetTopicWeights}
                            title="Reset all topics to equal weight"
                          >Reset</button>
                        </>
                      )}
                      <span className="topic-distribution-total">100%</span>
                    </div>
                    {testTopicsLoading ? (
                      <div className="topic-distribution-loading">Detecting topics…</div>
                    ) : (
                      <>
                        {testTopics.length === 0 && (
                          <div className="topic-distribution-loading">No topics detected. Add one below.</div>
                        )}
                        {testTopics.length > 0 && (
                          <div className="topic-distribution-list">
                            {testTopics.map((topic, idx) => (
                              <div key={idx} className="topic-distribution-row">
                                <button
                                  type="button"
                                  className="topic-distribution-delete"
                                  onClick={() => handleDeleteTopic(idx)}
                                  title="Remove topic"
                                  disabled={testTopics.length <= 1}
                                >×</button>
                                <span className="topic-distribution-label">{topic.title}</span>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  value={topic.weight}
                                  className="topic-distribution-slider"
                                  onChange={e => handleTopicWeightChange(idx, Number(e.target.value))}
                                />
                                <span className="topic-distribution-pct">{topic.weight}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="topic-distribution-add">
                          <input
                            type="text"
                            className="topic-distribution-add-input"
                            placeholder="Add a topic…"
                            value={newTopicInput}
                            onChange={e => setNewTopicInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTopic(); } }}
                          />
                          <button
                            type="button"
                            className="topic-distribution-add-btn"
                            onClick={handleAddTopic}
                            disabled={!newTopicInput.trim()}
                          >Add</button>
                        </div>
                      </>
                    )}
                  </div>
                )}


              </div>
            ) : mode !== 'instructor' && chatMode === 'annotation' ? (
            <div className="empty-state">
              {activeAnnotation?.anchor_text && (
                <blockquote className="empty-state-anchor">
                  “{activeAnnotation.anchor_text}”
                </blockquote>
              )}
              <p className="empty-state-hint">
                {activeAnnotation?.body
                  || 'Your instructor marked this passage. Ask anything about it.'}
              </p>
            </div>
            ) : mode !== 'instructor' && chatMode === 'qa' ? (
            <div className="empty-state">
              <p className="empty-state-hint">Ask a question about the paper, or try:</p>
              <div className="suggested-questions">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    className="suggested-chip"
                    onClick={() => handleChipClick(q)}
                    disabled={loading}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
            ) : null
          ) : (
            messages.map((msg) => {
              return (
              <div
                key={msg.id}
                className={`message ${msg.is_instructor ? 'instructor-message' : msg.is_ai ? 'ai-message' : 'user-message'}`}
                title={new Date(msg.timestamp).toLocaleString()}
              >
                {msg.is_instructor && (
                  <div className="message-header">Instructor</div>
                )}
                <div className="message-content">
                  {msg.is_instructor
                    ? <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>{msg.message}</ReactMarkdown>
                    : msg.is_ai
                    ? <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                        {effectiveMode === 'test' ? stripMcqOptionLines(msg.message) : msg.message}
                      </ReactMarkdown>
                    : msg.message}
                </div>
                {msg.is_ai && msg.instructor_corrections?.map((c, i) => (
                  <div key={`corr-${i}`} className="instructor-correction-card">
                    <div className="instructor-correction-header">⚑ Instructor correction</div>
                    <p className="instructor-correction-body">
                      {c.correction
                        ? c.correction
                        : 'Your instructor flagged this response as potentially inaccurate.'}
                    </p>
                  </div>
                ))}
                {msg.pullContexts?.map((ctx, i) => {
                  let safeHref = null;
                  try {
                    const u = new URL(ctx.source);
                    if (u.protocol === 'https:' || u.protocol === 'http:') safeHref = ctx.source;
                  } catch {}
                  return (
                    <div key={i} className="pull-context-card">
                      <div className="pull-context-header">Background context</div>
                      <p className="pull-context-body">{ctx.context}</p>
                      <p className="pull-context-reasoning">{ctx.reasoning}</p>
                      {safeHref && (
                        <a
                          href={safeHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pull-context-source"
                        >
                          Learn more →
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
              );
            })
          )}

          {/* Standalone question panel — never added to messages, so it never leaks into history.
              Hidden while reviewing a past question (the panel shows the CURRENT question). */}
          {effectiveMode === 'test' && !testFinished && !loading && !!currentQuestionText && viewingQuestion == null && (
            <div className="message ai-message">
              <div className="message-header">🤖 PaperBuddy</div>
              <div className={`mcq-inline-options${mcqData?.isMultiAnswer ? ' mcq-inline-options--multi' : ''}`}>
                <div className="mcq-question-text">{currentQuestionText}</div>
                {mcqData && (<>
                  {mcqData.isMultiAnswer && <p className="mcq-multi-hint">Select all that apply</p>}
                  <div className="mcq-inline-choices">
                    {mcqData.options.map(opt => {
                      if (mcqData.isMultiAnswer) {
                        const checked = selectedMcqOptions.has(opt.letter);
                        return (
                          <button key={opt.letter} type="button"
                            className={`mcq-inline-btn mcq-inline-btn--check${checked ? ' mcq-inline-btn--selected' : ''}`}
                            onClick={() => setSelectedMcqOptions(prev => {
                              const next = new Set(prev);
                              next.has(opt.letter) ? next.delete(opt.letter) : next.add(opt.letter);
                              return next;
                            })}
                          >
                            <span className={`mcq-checkbox${checked ? ' mcq-checkbox--checked' : ''}`}>{checked ? '✓' : ''}</span>
                            <span className="mcq-option-letter">{opt.letter}</span>
                            <span className="mcq-option-text">{opt.text}</span>
                          </button>
                        );
                      }
                      return (
                        <button key={opt.letter} type="button"
                          className={`mcq-inline-btn${selectedMcqOption === opt.letter ? ' mcq-inline-btn--selected' : ''}`}
                          onClick={() => setSelectedMcqOption(opt.letter)}
                        >
                          <span className="mcq-option-letter">{opt.letter}</span>
                          <span className="mcq-option-text">{opt.text}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" className="mcq-submit-btn"
                    onClick={() => {
                      if (mcqData.isMultiAnswer) {
                        if (selectedMcqOptions.size === 0) return;
                        // Submit "A. Option text, C. Other text" so the LLM can
                        // grade by option content, not just by letter (letter
                        // positions can shift after shuffling).
                        const fullTexts = [...selectedMcqOptions].sort().map(letter => {
                          const opt = mcqData.options.find(o => o.letter === letter);
                          return opt ? `${letter}. ${opt.text}` : letter;
                        }).join(', ');
                        sendUserMessage(fullTexts);
                        setSelectedMcqOptions(new Set());
                      } else {
                        if (!selectedMcqOption) return;
                        const opt = mcqData.options.find(o => o.letter === selectedMcqOption);
                        sendUserMessage(opt ? `${selectedMcqOption}. ${opt.text}` : selectedMcqOption);
                        setSelectedMcqOption(null);
                      }
                    }}
                    disabled={mcqData.isMultiAnswer ? selectedMcqOptions.size === 0 : !selectedMcqOption}
                  >
                    Submit answer →
                  </button>
                </>)}
              </div>
            </div>
          )}

          {llmDown && !loading && (
            <div className="chat-llm-down" role="alert">
              <span className="chat-llm-down-icon" aria-hidden="true">!</span>
              <div>
                <strong>PaperBuddy is unavailable right now.</strong>
                <div className="chat-llm-down-detail">{llmDown}</div>
                <div className="chat-llm-down-detail">
                  {'If this keeps happening, the server’s Anthropic API key is probably '
                   + 'missing, invalid, or expired. Your message was not sent — try again once it’s fixed.'}
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="message ai-message loading-message">
              <div className="message-header">🤖 PaperBuddy</div>
              <div className="message-content thinking-bubble">
                <span className="thinking-text">{aiStalled ? 'Still trying' : 'Thinking'}</span>
                <span className="thinking-dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </div>
              {aiStalled && (
                <div className="thinking-stalled" role="status">
                  <span className="thinking-stalled-icon" aria-hidden="true">!</span>
                  <span>
                    {'PaperBuddy isn’t responding. The AI service looks like it’s down, '
                     + 'or its API key is invalid or expired. Still waiting in case it’s just '
                     + 'slow — if nothing arrives, try sending your message again.'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Inline instructor options — toggle buttons as part of the chat */}
          {effectiveMode === 'instructor' && instructorTab !== 'prompt' && optionsPanel && !loading && (
            <div className="message ai-message chat-options-message">
              <div className="message-header">🤖 PaperBuddy</div>
              <div className="message-content">
                {optionsPanel.questions.map(q => (
                  <div key={q.id} className="chat-options-question">
                    <div className="chat-options-label">{q.label}</div>
                    {q.choices ? (
                      <div className="chat-options-choices">
                        {q.choices.map(c => (
                          <button
                            key={c}
                            type="button"
                            className={`chat-options-btn${optionsPanel.selections[q.id] === c ? ' chat-options-btn--selected' : ''}`}
                            onClick={() => handleOptionSelect(q.id, c)}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    ) : q.id === 'num_questions' ? (
                      <div className="num-q-slider-wrap">
                        <input
                          type="range"
                          className="num-q-slider"
                          min={0}
                          max={NUM_Q_SLIDER_VALUES.length - 1}
                          step={1}
                          value={numQValueToIndex(optionsPanel.selections[q.id] || '5')}
                          onChange={e => setOptionsPanel(prev => ({
                            ...prev,
                            selections: { ...prev.selections, [q.id]: String(numQIndexToValue(Number(e.target.value))) },
                          }))}
                        />
                        <span className="num-q-value">{optionsPanel.selections[q.id] || '5'}</span>
                        <span className="num-q-hint">1 – 15, 20, 25, 30, 50</span>
                      </div>
                    ) : (
                      <input
                        type="number"
                        className="options-number-input"
                        value={optionsPanel.selections[q.id]}
                        onChange={e => setOptionsPanel(prev => ({
                          ...prev,
                          selections: { ...prev.selections, [q.id]: e.target.value },
                        }))}
                        min="1"
                        max="50"
                        placeholder="e.g. 10"
                      />
                    )}
                  </div>
                ))}
                {optionsPanel.questions.length > 1 && (
                  <button
                    type="button"
                    className="chat-options-submit"
                    onClick={handleOptionsSubmit}
                    disabled={loading}
                  >
                    Generate →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Inline LGTM */}
          {effectiveMode === 'instructor' && instructorTab !== 'prompt' && awaitingReview && !loading && (
            <div className="message ai-message chat-lgtm-message">
              <div className="message-header">🤖 PaperBuddy</div>
              <div className="message-content chat-lgtm-content">
                <button
                  type="button"
                  className="lgtm-btn"
                  onClick={() => sendUserMessage('Looks good')}
                >
                  ✓ Looks good
                </button>
                <span className="lgtm-hint">or type feedback below</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Selected text context chip — student/test only. In instructor mode a PDF
          selection is used to pin highlights (the 🖍﹢ buttons), not to define /
          walk through / note, so the chip is hidden there. The selection itself is
          preserved so highlight-pinning still works. */}
      {selectedPdfText && mode !== 'instructor' && (
        <div className="selection-context-chip">
          <span className="selection-context-label">Context:</span>
          <span className="selection-context-text">
            {selectedPdfText.length > 80
              ? `${selectedPdfText.slice(0, 80)}…`
              : selectedPdfText}
          </span>
          <button
            type="button"
            className="selection-define-btn"
            onClick={handleDefine}
            disabled={loadingDefinition}
            aria-label="Define this term"
            title="What does this mean?"
          >
            📖
          </button>
          <button
            type="button"
            className="selection-walkthrough-btn"
            onClick={handleWalkthrough}
            disabled={loadingWalkthrough}
            aria-label="Walk me through this step by step"
            title="Walk me through this"
          >
            🔢
          </button>
          <button
            type="button"
            className="selection-note-btn"
            onClick={() => setShowNoteInput(true)}
            aria-label="Add a note about this paragraph"
            title="Add a note"
          >
            📝
          </button>
          <button
            type="button"
            className="selection-context-clear"
            onClick={onClearSelection}
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
      )}

      {/* Definition popover */}
      {selectedPdfText && definition && (
        <div className="definition-panel">
          <div className="definition-header">
            <span className="definition-label">📖 Definition</span>
            <button
              type="button"
              className="definition-dismiss"
              onClick={handleDismissDefinition}
              aria-label="Dismiss definition"
            >
              ×
            </button>
          </div>
          <div className="definition-term">
            "{definition.term.length > 80 ? definition.term.slice(0, 80) + '…' : definition.term}"
          </div>
          <div className="definition-body">
            {definition.text === null
              ? <span className="definition-loading">Looking it up…</span>
              : definition.text}
          </div>
        </div>
      )}

      {/* Walkthrough panel */}
      {selectedPdfText && walkthrough && (
        <div className="walkthrough-panel">
          <div className="walkthrough-header">
            <span className="walkthrough-label">🔢 Step-by-step walkthrough</span>
            <button
              type="button"
              className="walkthrough-dismiss"
              onClick={handleDismissWalkthrough}
              aria-label="Dismiss walkthrough"
            >
              ×
            </button>
          </div>
          <div className="walkthrough-selection">
            "{walkthrough.selection.length > 80 ? walkthrough.selection.slice(0, 80) + '…' : walkthrough.selection}"
          </div>
          <div className="walkthrough-body">
            {walkthrough.text === null
              ? <span className="walkthrough-loading">Generating walkthrough…</span>
              : <ReactMarkdown remarkPlugins={[remarkGfm]}>{walkthrough.text}</ReactMarkdown>}
          </div>
        </div>
      )}

      {/* Note input */}
      {selectedPdfText && showNoteInput && (
        <div className="note-input-wrapper">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Your note about this paragraph..."
            className="note-input"
            rows={3}
            autoFocus
          />
          <div className="note-input-actions">
            <button type="button" className="note-cancel-btn" onClick={handleCancelNote}>
              Cancel
            </button>
            <button
              type="button"
              className="note-save-btn"
              onClick={handleSaveNote}
              disabled={!noteText.trim() || savingNote}
            >
              {savingNote ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}

      {/* Confidence slider — test mode only */}
      {effectiveMode === 'test' && !viewingPastAttempt && !(mode === 'student' && showStudentHome) && showConfidenceMeter && messages.length > 0 && messages[messages.length - 1].is_ai && !loading && !testFinished && (
        <div className="confidence-slider-wrap">
          <div className="confidence-slider-header">
            <label htmlFor="confidence-slider" className="confidence-slider-label">
              How confident are you?
            </label>
            <span className="confidence-slider-value">
              {confidence}/5
            </span>
          </div>
          <input
            id="confidence-slider"
            type="range"
            min="1"
            max="5"
            step="1"
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            className="confidence-slider"
          />
          <div className="confidence-slider-labels">
            <span>Not sure</span>
            <span>Very sure</span>
          </div>
        </div>
      )}

      {/* Test assist button — hint. (Point-me-to-text was removed: questions already
          carry instructor-pinned highlights, and hints now highlight the exact spot.) */}
      {showHintButton && effectiveMode === 'test' && !(mode === 'student' && showStudentHome) && messages.length > 0 && messages[messages.length - 1].is_ai && !loading && !testFinished && (
        <div className="test-assist-btns">
          <button
            type="button"
            className="point-to-text-btn"
            onClick={handleGiveHint}
            disabled={loading}
          >
            💡 Give me a hint
          </button>
        </div>
      )}



      {/* Cycle navigation bar */}
      {effectiveMode === 'cycle' && !(mode === 'student' && showStudentHome) && cycleQuestions && messages.length > 0 && messages[messages.length - 1]?.is_ai && !loading && (
        <div className="cycle-next-bar">
          <button
            type="button"
            className="cycle-visual-btn"
            onClick={() => sendUserMessage('Show me a visual for this section.')}
          >
            Show me a visual
          </button>
          {showCycleHintButton && cyclePhase === 'questioning' && (
            <button
              type="button"
              className="cycle-visual-btn"
              onClick={handleGiveHint}
            >
              💡 Give me a hint
            </button>
          )}
          {(() => {
            // Is there actually a cycle after the one the student is on? Once they're
            // on the last cycle there's nowhere to advance — so we never show an
            // enabled "Next Cycle", which is what let the counter run to 133% and
            // re-loop the final cycle.
            const hasNextCycle = cycleProgress.total > 0 && cycleActiveC < cycleProgress.total - 1;
            if (cyclePhase === 'reading') {
              return (
                <button
                  type="button"
                  className="cycle-next-btn"
                  onClick={() => { setCyclePhase('questioning'); sendUserMessage("I'm ready for a question."); }}
                >
                  I'm ready for a question →
                </button>
              );
            }
            // Last cycle finished → the plan is complete. Offer a non-advancing
            // "done" affordance (reopens the summary) instead of "Next Cycle".
            if (!hasNextCycle && cyclePhase === 'done') {
              return (
                <button
                  type="button"
                  className="cycle-next-btn"
                  onClick={() => setCycleComplete(prev => ({ ...prev, open: true }))}
                  title="You've completed every cycle"
                >
                  🎉 All cycles complete
                </button>
              );
            }
            // Last cycle, still answering its questions → no cycle to advance to.
            if (!hasNextCycle) return null;
            return (
              <button
                type="button"
                className="cycle-next-btn"
                disabled={cyclePhase !== 'done'}
                title={cyclePhase !== 'done' ? 'Answer the current question to unlock' : ''}
                onClick={() => {
                  if (cycleIndexRef.current >= cycleProgress.total - 1) return; // never past the last cycle
                  // Advance to next cycle's independent thread
                  cycleIndexRef.current += 1;
                  setCycleActiveC(cycleIndexRef.current);
                  // If reviewing a past cycle, restore live messages before advancing
                  if (viewingCycle !== null) {
                    if (continuousMessagesRef.current != null) {
                      setMessages(continuousMessagesRef.current);
                      continuousMessagesRef.current = null;
                    }
                    setViewingCycle(null);
                  }
                  setCyclePhase('reading');
                  sendUserMessage('Move to the next cycle.');
                }}
              >
                Next Cycle →
              </button>
            );
          })()}
        </div>
      )}

      {/* Guided recommendation CTA */}
      {mode === 'instructor' && instructorSubMode === 'guided' && guidedRecommendation && guidedStep !== 'structure' && (
        <div className="guided-recommendation-bar">
          <span className="guided-recommendation-label">
            Your {guidedTargetMode === 'test' ? 'Test' : 'Cycle'} Mode settings are ready.
          </span>
          <button
            type="button"
            className="guided-take-me-btn"
            onClick={() => {
              if (guidedTargetMode === 'cycle') {
                // Cycle mode must pass through structure review (step 3)
                setGuidedStructureSections(withSectionDefaults(aiGeneratedSections));
                setGuidedStep('structure');
              } else {
                // Test mode goes directly to presets (no structure step)
                handleInstructorModeToggle(guidedRecommendation.mode);
                setInstructorTab('chat');
                setShowInstructorHome(false);
              }
            }}
          >
            {guidedTargetMode === 'cycle' ? 'Continue to Structure Setup →' : 'Go to Test Mode Setup →'}
          </button>
        </div>
      )}

      {/* Context-health nudge (Q&A): long thread → answers may lose detail; suggest a fresh chat */}
      {effectiveMode === 'student' && chatMode !== 'annotation' && contextDegrading && (
        <div className="context-nudge">
          <span className="context-nudge-text">
            💡 This conversation is getting long — answers may start losing detail. Start a fresh chat for sharper replies (I'll still remember what we covered).
          </span>
          <div className="context-nudge-actions">
            <button type="button" className="context-nudge-btn" onClick={handleSidebarNewChat}>
              Start new chat
            </button>
            <button
              type="button"
              className="context-nudge-dismiss"
              onClick={() => { contextNudgeDismissedRef.current = true; setContextDegrading(false); }}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      {(mode !== 'instructor' || (instructorSubMode === 'guided' && messages.length > 0 && guidedStep !== 'structure') || authorPhase === 'detailing') && !(mode === 'student' && showStudentHome) && !(effectiveMode === 'test' && !testQuestions) && !(effectiveMode === 'cycle' && !cycleQuestions) && (
        <form onSubmit={handleSendMessage} className="message-input-form">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={
              authorPhase === 'detailing' ? 'Ask for changes to this cycle…'
              : viewingQuestion != null ? 'Return to current question to continue…'
              : viewingCycle != null ? 'Return to current cycle to continue…'
              : 'Type your question...'
            }
            disabled={loading || viewingQuestion != null || viewingCycle != null}
            className="message-input"
          />
          <button
            type="submit"
            disabled={loading || viewingQuestion != null || viewingCycle != null || !inputMessage.trim()}
            className="send-btn"
          >
            {loading ? '...' : 'Send'}
          </button>
        </form>
      )}
        </div>{/* closes chat-main */}
      </div>
      )}{/* closes chat-body */}
      </>
      )}

      <TestCompleteModal
        isOpen={testComplete.open}
        score={testComplete.score}
        questionResults={categoricalGrades}
        totalQuestions={progress.total}
        retakePolicy={retakePlanRef.current?.plan?.retake_policy}
        onClose={() => setTestComplete(prev => ({ ...prev, open: false }))}
        onRestart={undefined}
        onRetake={retakePlanRef.current?.plan?.retake_policy && retakePlanRef.current.plan.retake_policy !== 'none'
          ? () => handleRetakeTest()
          : undefined}
      />

      <TestCompleteModal
        isOpen={cycleComplete.open}
        title="All cycles complete!"
        subtitle="Great work finishing every cycle."
        onClose={() => setCycleComplete(prev => ({ ...prev, open: false }))}
        onRestart={() => {
          setCycleComplete({ open: false });
          handleCycleRestart();
        }}
      />

      {showPlanHistory && (
        <PlanHistoryPanel
          paper={paper}
          user={user}
          onClose={() => setShowPlanHistory(false)}
          onPlanActivated={(planType) => {
            if (planType === 'cycles') {
              setCycleEnabled(true);
              if (paper) saveModeSettings(paper.id, { cycle_enabled: true }).catch(() => {});
            }
            if (planType === 'testing') {
              setTestEnabled(true);
              if (paper) saveModeSettings(paper.id, { test_enabled: true }).catch(() => {});
            }
          }}
        />
      )}

      {/* Paper Structure popup */}
      {showSectionsPopup && (
        <div className="sections-popup-overlay" onClick={() => setShowSectionsPopup(false)}>
          <div className="sections-popup" onClick={e => e.stopPropagation()}>
            <div className="sections-popup-header">
              <span className="sections-popup-title">Paper Structure</span>
              {suggestedPaperSections && suggestedPaperSections.length > 0 && (
                <span className="sections-popup-count">
                  {suggestedPaperSections.length} section{suggestedPaperSections.length !== 1 ? 's' : ''}
                </span>
              )}
              {suggestedPaperSections && suggestedPaperSections.length > 0 && (
                <button
                  type="button"
                  className={`sections-popup-regen-btn${regeneratingSections ? ' loading' : ''}`}
                  onClick={handleRegenerateSections}
                  disabled={regeneratingSections}
                  title="Ask the AI to re-divide the paper into sections from scratch"
                >
                  {regeneratingSections ? 'Regenerating…' : '↺ Regenerate'}
                </button>
              )}
              <button
                type="button"
                className="sections-popup-close"
                onClick={() => setShowSectionsPopup(false)}
                aria-label="Close"
              >✕</button>
            </div>

            {regeneratingSections ? (
              <p className="sections-popup-regen-msg">
                A new structure is being generated — it will be ready in a few moments.
              </p>
            ) : suggestedPaperSections && suggestedPaperSections.length > 0 ? (
              <ol className="sections-popup-list">
                {suggestedPaperSections.map(s => (
                  <li key={s.number} className="sections-popup-item">
                    <div className="sections-popup-item-top">
                      <span className="sections-popup-item-title">{s.title}</span>
                      <span className="sections-popup-item-pages">{fmtPages(s.start_page, s.end_page)}</span>
                    </div>
                    {s.description && (
                      <p className="sections-popup-item-desc">{s.description}</p>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="sections-popup-empty">
                No structured sections could be detected for this paper.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatSection;
